import React, { useRef, useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
// import { useDropzone } from 'react-dropzone';
import Html5VideoPlayer from './Html5VideoPlayer';
import PlayerTrackingCanvas from './PlayerTrackingCanvas';
import OccupancyHeatmapCanvas from './OccupancyHeatmapCanvas';
import GridOccupancyCanvas from './GridOccupancyCanvas';
import { loadCSV } from './utils/csvUtils';
import { analyzeRally } from '../../redux/network/bffApi';
import { POST_LIST_POST, GET_FETCH_GAME_POST_FILE, BORQS_URL } from '../../config/endpoints';
import { getUserProfile } from '../../redux/network/userApi';
import styles from './Playground.module.scss';
import { thumbnailFailureTracker } from '../../utils/thumbnailFailureTracker';

// Extracted utilities and components
import { renderStructuredMessage, formatResponseMessage } from './utils/messageUtils';
import { PREDEFINED_QUESTIONS, VIDEO_OPTIONS, DUMMY_ANALYTICS, getEnvironment, POST_IDS } from './utils/constants';
import { parsePhaseWinPercentages, parseOverallWinPercentages, fmt, formatPct1, normalizePercentsInText, computeWinPercentages, computeWinPercentagePhases } from './utils/analyticsUtils';
import ShuttlecockAnimation from './components/ShuttlecockAnimation';
import AnalysisCanvasLoader from './components/AnalysisCanvasLoader';
import BarChart from './components/BarChart';
import CoachFeedback from '../CoachFlow/CoachFeedback';
import FeedbackList from '../Video.js/componnets/FeedbackList';
import ClearButton from '../ClearButton/ClearButton';
import cfStyles from '../Video.js/create-feedback/CreateFeedback.module.scss';
import ImmersiveEmptyState from './components/ImmersiveEmptyState';
import { FaMicrophone, FaTrash as FaTrashIcon, FaChevronLeft, FaChevronRight, FaPlus, FaHistory, FaMagic, FaInfoCircle } from 'react-icons/fa';
import { useCoachFeedback } from './hooks/useCoachFeedback';
import { setStandaloneFeedbacks, addStandaloneFeedback as addStandaloneFeedbackAction, removeStandaloneFeedbackAtTime } from '../../redux/reducers/standaloneSlice';
import { setIsLoginDialog } from '../../redux/reducers/loginSlice';
import { createUpdateFeedback, getCoachFeedback } from '../../redux/network/coachAPI';
import AccessRestricted from './components/AccessRestricted';
import DirectLoadAnimation from './components/DirectLoadAnimation';
import ReactGA from 'react-ga4';
// useStitchedVideoFetcher replaced with inline useEffect

// Resolve thumbnail path for a given option. Convention: /playground/<key>/thumbnail.jpg
// Falls back to a generic placeholder image from public/assets if missing.
const getThumbnailUrlFor = (opt) => {
  if (!opt) return null;
  // Use the key (if present) to find sample thumbnails, fallback to value
  const val = opt.key || opt.value || '';
  if (val.startsWith('video')) {
    const num = val.replace('video', '');
    return `https://sportintelligence.s3.ap-south-1.amazonaws.com/playground/video${num}/video${num}_thumbnail.jpg`;
  }
  return null;
};

export default function Playground() {
  // Define refs at the top level to comply with React Hooks rules
  const playerRef = useRef(null);
  const videoRef = useRef(null);
  const env = process.env.REACT_APP_ENV;
  const dispatch = useDispatch();
  const [pendingAnnotation, setPendingAnnotation] = useState(null); // normalized {x1,y1,x2,y2}
  const location = useLocation();
  const navigate = useNavigate();
  const [activeFeedbackId, setActiveFeedbackId] = useState(null);
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editedCommentText, setEditedCommentText] = useState('');
  const [feedbackLoading, setFeedbackLoading] = useState(false);

  // GA: screen view for Playground
  useEffect(() => {
    try { ReactGA.event({ category: 'Playground', action: 'view' }); } catch { }
  }, []);

  // Disable console.log for Playground to keep console clean
  useEffect(() => {
    const originalLog = console.log;
    console.log = () => { };
    return () => { console.log = originalLog; };
  }, []);

  const [playingAudioId, setPlayingAudioId] = useState(null);
  // Cleanup and sequencing refs for rally segment playback
  const endGuardCleanupRef = useRef(null);
  const chainingRef = useRef(false);
  const rightScrollRef = useRef(null);
  const metricCarouselRef = useRef(null);
  const tabletMetricCarouselRef = useRef(null);
  const zoomDataMetricsRef = useRef(null);
  // Redux: standalone feedback annotations
  const standaloneAnnotations = useSelector((state) => state?.standAlone?.feedbacks?.feedbackData || []);
  const [checkboxes, setCheckboxes] = useState({ occupancy: true, tracking: false, cumulative: true, heatmap: true, coach: false });
  // Snapshot of checkboxes captured on last Fetch; visuals render based on this
  // Coach feedback using custom hook
  const coachFeedbackHook = useCoachFeedback(videoRef, playerRef);
  const { coachFeedbacks } = coachFeedbackHook;

  // Auth state snapshot (used to gate trial-only behavior)
  const isAuthed = (() => { try { return !!localStorage.getItem('BQS_TOKEN'); } catch { return false; } })();

  // Trial mode: enabled when user comes from Explore in PreSign
  const [trialMode, setTrialMode] = useState(() => {
    try { return sessionStorage.getItem('PG_TRIAL_MODE') === '1'; } catch { return false; }
  });
  const [trialRandomizing, setTrialRandomizing] = useState(false);
  const [trialExpired, setTrialExpired] = useState(false);
  const [zoomedViz, setZoomedViz] = useState(null); // { type, renderProps }
  const [zoomRallyIndex, setZoomRallyIndex] = useState(0);
  const [showSharePopup, setShowSharePopup] = useState(false);
  const [shareSelections, setShareSelections] = useState({
    video: true,
    summary: false,
    detailed: false,
  });
  const [shareInProgress, setShareInProgress] = useState(false);
  const trialTimerRef = useRef(null);

  // State for securely fetched stitched video blob URL
  const [stitchedVideoBlobUrl, setStitchedVideoBlobUrl] = useState(null);

  const trialSpinRef = useRef(null);
  // Trial chat limits
  const [trialAskCount, setTrialAskCount] = useState(() => {
    try { return Number(sessionStorage.getItem('PG_TRIAL_ASK_COUNT') || 0) || 0; } catch { return 0; }
  });
  const TRIAL_ASK_LIMIT = 5;
  const [trialChatLocked, setTrialChatLocked] = useState(false);

  // Direct load mode: enabled when coach navigates from CreateFeedback
  const [directLoadMode, setDirectLoadMode] = useState(false);
  const [directVideoData, setDirectVideoData] = useState(null);
  const [loadingVideo, setLoadingVideo] = useState(false);
  const [videoThumbnail, setVideoThumbnail] = useState(null); // Auto-generated thumbnail from video frame
  const [showMatchInfoPopup, setShowMatchInfoPopup] = useState(false);




  // Get user role early for direct load detection
  const userRole = (() => { try { return localStorage.getItem('BQS_ROLE'); } catch { return null; } })();


  // Highlight feedback when video pauses at/near its timestamp
  useEffect(() => {
    // Ensure login modal is closed and any showLogin state is cleared on this public route
    try { dispatch(setIsLoginDialog(false)); } catch { }
    try {
      if (location?.state) {
        navigate('/SmartInsights', { replace: true, state: {} });
      }
    } catch { }

    // Initialize trial mode timers
    try {
      const authed = (() => { try { return !!localStorage.getItem('BQS_TOKEN'); } catch { return false; } })();
      const isTrial = !authed && sessionStorage.getItem('PG_TRIAL_MODE') === '1';
      setTrialMode(isTrial);
      if (!authed && isTrial) {
        // If number changed, reset any prior locks and counters
        try {
          const keyNow = sessionStorage.getItem('PG_TRIAL_PHONE_KEY') || '';
          const keyPrev = sessionStorage.getItem('PG_TRIAL_PHONE_KEY_LAST') || '';
          if (keyNow && keyNow !== keyPrev) {
            sessionStorage.setItem('PG_TRIAL_PHONE_KEY_LAST', keyNow);
            sessionStorage.setItem('PG_TRIAL_ASK_COUNT', '0');
            setTrialChatLocked(false);
            // Clear any miscellaneous locks that chat UI may rely on
            try { sessionStorage.removeItem('TRIAL_CHAT_LOCKED'); } catch { }
            try { localStorage.removeItem('TRIAL_CHAT_LOCKED'); } catch { }
            try { localStorage.removeItem('PG_TRIAL_ASK_COUNT'); } catch { }
          }
        } catch { }
        // Lock chat if limit already reached
        try {
          const raw = sessionStorage.getItem('PG_TRIAL_ASK_COUNT');
          const cnt = Number(raw == null ? '0' : raw) || 0;
          if (raw == null) sessionStorage.setItem('PG_TRIAL_ASK_COUNT', '0');
          if (cnt >= TRIAL_ASK_LIMIT) setTrialChatLocked(true);
        } catch { }
        // Ensure a valid trial start timestamp exists; initialize if missing/invalid
        let startedAt = Number(sessionStorage.getItem('PG_TRIAL_START'));
        if (!Number.isFinite(startedAt) || startedAt <= 0) {
          startedAt = Date.now();
          try { sessionStorage.setItem('PG_TRIAL_START', String(startedAt)); } catch { }
        }
        // We no longer expire trial based on time; limit is questions asked (5)
        setTrialExpired(false);
        // Helper: pick a random video option for trial users when no selection exists
        const pickRandomTrialVideo = () => {
          try {
            const opts = Array.isArray(allVideoOptions) ? allVideoOptions.filter(o => o && typeof o.value === 'string') : [];
            if (!opts.length) return;
            const idx = Math.floor(Math.random() * opts.length);
            const chosen = opts[idx];
            if (chosen && chosen.value) {
              setSelectedVideo(chosen.value);
              try { localStorage.setItem(SELECTED_VIDEO_KEY, chosen.value); } catch { }
            }
          } catch { }
        };
        // If no video selected yet, try restore from localStorage; otherwise randomize for Explore users
        if ((!selectedVideo || selectedVideo === 'none')) {
          let saved = null;
          try {
            const val = localStorage.getItem('PLAYGROUND_SELECTED_VIDEO');
            if (val && typeof val === 'string' && val !== 'none') {
              saved = val;
              setSelectedVideo(saved);
            }
          } catch { }
          // If Explore Now requested a forced random, override any saved selection once
          try {
            const force = sessionStorage.getItem('PG_TRIAL_FORCE_RANDOM') === '1';
            if (force) {
              // Start the randomizing spinner animation for better UX
              try { ReactGA.event({ category: 'Playground', action: 'trial_random_pick' }); } catch { }
              startTrialRandomPick();
              sessionStorage.removeItem('PG_TRIAL_FORCE_RANDOM');
            } else if (!saved) {
              // No saved selection? Start randomizing to avoid landing page for trial users
              startTrialRandomPick();
            }
          } catch {
            if (!saved) startTrialRandomPick();
          }
        }
      }
    } catch { }

    const el = videoRef?.current;
    if (!el) return;
    const onPause = () => {
      try {
        const t = Number(el.currentTime) || 0;
        if (!Array.isArray(coachFeedbacks) || coachFeedbacks.length === 0) {
          setActiveFeedbackId(null);
          return;
        }
        const TOL = 0.75; // seconds tolerance
        let best = null;
        let bestDelta = Infinity;
        for (const item of coachFeedbacks) {
          const dt = Math.abs((Number(item.timeSec) || 0) - t);
          if (dt <= TOL && dt < bestDelta) { best = item; bestDelta = dt; }
        }
        setActiveFeedbackId(best ? best.id : null);
      } catch {
        setActiveFeedbackId(null);
      }
    };

    const pickRandomTrialVideo = () => {
      try {
        const opts = Array.isArray(allVideoOptions) ? allVideoOptions.filter(o => o && typeof o.value === 'string') : [];
        if (!opts.length) return;
        const idx = Math.floor(Math.random() * opts.length);
        const chosen = opts[idx];
        if (chosen && chosen.value) {
          setSelectedVideoSafe(chosen.value);
          try { localStorage.setItem(SELECTED_VIDEO_KEY, chosen.value); } catch { }
        }
      } catch { }
    };

    const onPlay = () => {
      setActiveFeedbackId(null);
    };
    el.addEventListener('pause', onPause);
    el.addEventListener('play', onPlay);
    return () => {
      el.removeEventListener('pause', onPause);
      el.removeEventListener('play', onPlay);
    };
  }, [videoRef, coachFeedbacks]);

  useEffect(() => {
    return () => {
      try { if (trialTimerRef.current) clearTimeout(trialTimerRef.current); } catch { }
      try { if (trialSpinRef.current) clearInterval(trialSpinRef.current); } catch { }
    };
  }, []);

  // Detect direct load mode from CreateFeedback navigation
  useEffect(() => {
    try {
      const isDirect = sessionStorage.getItem('PG_DIRECT_LOAD') === '1';
      const videoDataRaw = sessionStorage.getItem('PG_DIRECT_VIDEO_DATA');



      if (isDirect && videoDataRaw && (userRole === 'coach' || userRole === 'player')) {
        const videoData = JSON.parse(videoDataRaw);
        setDirectLoadMode(true);
        setDirectVideoData(videoData);
        setLoadingVideo(true);

        // Extract rally data if present
        if (videoData.rallyData) {
          console.log('[Playground] Rally data detected:', videoData.rallyData);

          const rallyData = videoData.rallyData;

          // Check if CSV paths are available
          if (rallyData.playerTrackingCsvPath || rallyData.heatmapCsvPath || rallyData.occupancySourceCsvPath) {
            console.log('[Playground] CSV paths found, will auto-enable visualizations:', {
              tracking: rallyData.playerTrackingCsvPath,
              heatmap: rallyData.heatmapCsvPath,
              occupancy: rallyData.occupancySourceCsvPath
            });

            // Set a pseudo video key for direct load mode
            // This allows the Playground to load CSVs even without a hardcoded video selection
            setSelectedVideo('direct_load_video');

            // Enable checkboxes for available visualizations after loading completes
            setTimeout(() => {
              // Check both root level and data field for CSV paths
              const dataField = rallyData.data || rallyData;
              const hasPlayerCoords = !!(rallyData.playerTrackingCsvPath || rallyData.player_coords_csv || dataField.player_coords_csv);
              const hasOccupancy = !!(rallyData.occupancySourceCsvPath || rallyData.occupancy_csv || dataField.occupancy_csv);
              const hasHeatmap = hasPlayerCoords; // Heatmap uses same CSV

              const newCheckboxes = {
                occupancy: false,
                tracking: false, // Don't auto-check Player Tracking
                cumulative: true, // Enable Range mode for match videos
                heatmap: false,
                coach: false // Don't auto-check Coach Feedback
              };
              setCheckboxes(newCheckboxes);
              setFetchedCheckboxes(newCheckboxes);
              console.log('[Playground] Auto-enabled visualizations:', newCheckboxes, {
                hasPlayerCoords,
                hasOccupancy,
                hasHeatmap,
                rallyData: rallyData
              });
            }, 2000); // Enable after loading animation completes
          }
        } else {
          console.log('[Playground] No rally data available for this video');
        }

        // Keep sessionStorage data to persist across page refreshes
        // sessionStorage.removeItem('PG_DIRECT_LOAD');
        // sessionStorage.removeItem('PG_DIRECT_VIDEO_DATA');

        // Show loading animation for 1.8 seconds
        setTimeout(() => {
          setLoadingVideo(false);
        }, 1800);
      }
    } catch (err) {
      console.error('Direct load failed:', err);
      setDirectLoadMode(false);
      setLoadingVideo(false);
    }
  }, [userRole]);

  // Generate thumbnail from video for direct load mode
  useEffect(() => {
    if (!directLoadMode || !directVideoData?.videoUrl) return;

    const generateThumbnail = async () => {
      const videoUrl = directVideoData.videoUrl;

      // Check if this thumbnail generation has previously failed
      if (thumbnailFailureTracker.hasFailed('playground', videoUrl)) {
        console.log('[Playground] Skipping previously failed thumbnail generation:', videoUrl);
        return;
      }

      try {
        const video = document.createElement('video');
        video.crossOrigin = 'anonymous';
        video.src = videoUrl;
        video.currentTime = 1; // Seek to 1 second to avoid black frame

        video.addEventListener('loadeddata', () => {
          try {
            const canvas = document.createElement('canvas');
            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

            const thumbnailDataUrl = canvas.toDataURL('image/jpeg', 0.8);
            setVideoThumbnail(thumbnailDataUrl);
            console.log('[Playground] Generated thumbnail from video frame');

            // Clean up
            video.src = '';
          } catch (canvasError) {
            console.error('[Playground] Canvas error during thumbnail generation:', canvasError);
            // Mark as failed to prevent retries
            thumbnailFailureTracker.markAsFailed('playground', videoUrl);
          }
        });

        video.addEventListener('error', (e) => {
          console.error('[Playground] Failed to generate thumbnail:', e);
          // Mark as failed to prevent retries
          thumbnailFailureTracker.markAsFailed('playground', videoUrl);
        });
      } catch (err) {
        console.error('[Playground] Thumbnail generation error:', err);
        // Mark as failed to prevent retries
        thumbnailFailureTracker.markAsFailed('playground', videoUrl);
      }
    };

    generateThumbnail();
  }, [directLoadMode, directVideoData?.videoUrl]);

  // Securely fetch stitched video using backend proxy when directVideoData contains S3 path
  useEffect(() => {
    if (!directLoadMode || !directVideoData) {
      return;
    }

    const dataField = directVideoData.rallyData || directVideoData;
    const stitchedPath = dataField.summary_video_file || dataField.Summary_video_file || dataField.stitched_video_path;

    if (!stitchedPath) {
      return;
    }

    // Try to find postId from various locations
    const fetchStitchedVideo = async () => {
      try {
        // DIRECT S3 URL - Bypass proxy entirely
        let s3Url = stitchedPath;
        if (s3Url.startsWith('s3://')) {
          const withoutPrefix = s3Url.replace('s3://', '');
          const [bucket, ...pathParts] = withoutPrefix.split('/');
          s3Url = `https://${bucket}.s3.ap-south-1.amazonaws.com/${pathParts.join('/')}`;
        }

        // Use direct URL - let the video player handle streaming (supports 206 partial content)
        setStitchedVideoBlobUrl(s3Url);

      } catch (err) {
        console.error('🎬 [StitchedVideoFetch] ❌ Error:', err);
      }
    };

    fetchStitchedVideo();

    // Cleanup
    return () => {
      if (stitchedVideoBlobUrl && stitchedVideoBlobUrl.startsWith('blob:')) {
        URL.revokeObjectURL(stitchedVideoBlobUrl);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [directLoadMode, directVideoData]);


  const startTrialRandomPick = () => {
    try {
      try { ReactGA.event({ category: 'Playground', action: 'trial_random_pick' }); } catch { }
      if (!Array.isArray(allVideoOptions) || allVideoOptions.length === 0) return;
      setTrialRandomizing(true);
      let idx = 0;
      trialSpinRef.current = setInterval(() => {
        idx = Math.floor(Math.random() * allVideoOptions.length);
        const opt = allVideoOptions[idx];
        if (opt && opt.value) setSelectedVideoSafe(opt.value);
      }, 120);
      setTimeout(() => {
        try { trialSpinRef.current && clearInterval(trialSpinRef.current); } catch { }
        trialSpinRef.current = null;
        setTrialRandomizing(false);
      }, 1500);
    } catch { }
  };

  // Delete handler compatible with FeedbackList signature (receives full annotation)
  const handleDeleteClick = (annotation) => {
    const id = annotation?._id || annotation?.id;
    if (!id) return;
    handleDeleteCoachFeedback(id);
  };

  const currentPlaygroundTitle = (() => {
    if (directLoadMode && directVideoData?.videoTitle) return directVideoData.videoTitle;
    if (directLoadMode && directVideoData?.matchMeta?.matchMeta?.video_name) return directVideoData.matchMeta.matchMeta.video_name;
    if (directLoadMode && directVideoData?.rallyData?.video_name) return directVideoData.rallyData.video_name;
    if (directLoadMode) return selectedVideo?.replace('direct_load_video', 'Set Video')?.replace(/_/g, ' ');
    return 'PLAYGROUND';
  })();

  const scrollMetricRail = (direction, railRef = metricCarouselRef) => {
    const el = railRef.current;
    if (!el) return;
    const track = el.firstElementChild;
    const trackStyles = track ? window.getComputedStyle(track) : null;
    const gap = trackStyles ? parseFloat(trackStyles.columnGap || trackStyles.gap || '0') || 0 : 0;
    const firstCard = track?.firstElementChild;
    const cardWidth = firstCard?.getBoundingClientRect().width || 0;
    const viewportWidth = el.clientWidth;
    const isDesktopMetricGrid = window.innerWidth >= 1280;
    const perCardStep = cardWidth > 0 ? cardWidth + gap : Math.max(220, Math.floor(viewportWidth * 0.4));
    const pageStep = isDesktopMetricGrid
      ? viewportWidth + gap
      : perCardStep * Math.max(1, Math.floor((viewportWidth + gap) / Math.max(perCardStep, 1)));
    const target = Math.max(0, Math.round((el.scrollLeft + (direction * pageStep)) / perCardStep) * perCardStep);
    el.scrollTo({ left: target, behavior: 'smooth' });
  };

  const scrollZoomDataMetrics = (direction) => {
    const el = zoomDataMetricsRef.current;
    if (!el) return;
    const firstCard = el.firstElementChild;
    const styles = window.getComputedStyle(el);
    const gap = parseFloat(styles.columnGap || styles.gap || '0') || 0;
    const cardWidth = firstCard?.getBoundingClientRect().width || 180;
    const step = cardWidth + gap;
    el.scrollBy({ left: direction * step, behavior: 'smooth' });
  };

  const getChatVizCanvasSize = (variant = 'single') => {
    const base = variant === 'compact'
      ? { width: 120, height: 240 }
      : { width: 160, height: 320 };

    if (typeof window === 'undefined' || window.innerWidth > 768) {
      return base;
    }

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    return {
      width: Math.round(base.width * dpr),
      height: Math.round(base.height * dpr),
    };
  };

  const getSidePanelCanvasSize = () => {
    if (typeof window === 'undefined') {
      return { width: 210, height: 400 };
    }

    if (window.innerWidth <= 640) {
      return { width: 176, height: 316 };
    }

    if (window.innerWidth <= 1024) {
      return { width: 148, height: 452 };
    }

    if (window.innerWidth <= 1100) {
      return { width: 188, height: 332 };
    }

    return { width: 210, height: 400 };
  };

  const getSidePanelCanvasStyle = () => {
    if (typeof window !== 'undefined' && window.innerWidth >= 768 && window.innerWidth <= 1024) {
      return { width: '100%', height: '100%', borderRadius: '12px' };
    }

    return { width: '100%', borderRadius: '12px' };
  };

  const SHARE_OPTIONS = [
    { id: 'video', label: 'Processed Set Video' },
    { id: 'summary', label: 'Summary Report' },
    { id: 'detailed', label: 'Detailed Report' },
  ];

  const buildSafeFileBaseName = () => {
    const rawName = (directVideoData?.videoTitle || selectedVideo || 'smartinsights').toString();
    return rawName
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '') || 'smartinsights';
  };

  const downloadFile = (file) => {
    const objectUrl = URL.createObjectURL(file);
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = file.name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
  };

  const getExtensionFromType = (contentType = '') => {
    const normalizedType = contentType.toLowerCase();
    if (normalizedType.includes('pdf')) return 'pdf';
    if (normalizedType.includes('json')) return 'json';
    if (normalizedType.includes('text/plain')) return 'txt';
    if (normalizedType.includes('msword')) return 'doc';
    if (normalizedType.includes('wordprocessingml')) return 'docx';
    if (normalizedType.includes('zip')) return 'zip';
    return 'bin';
  };

  const buildReportShareFile = async (reportType, baseFileName) => {
    const jwtToken = (() => { try { return localStorage.getItem('BQS_TOKEN'); } catch { return null; } })();
    const postId = getPostIdForSelected();

    if (!jwtToken) {
      throw new Error('Login is required to share reports.');
    }

    if (!postId) {
      throw new Error('Post information is unavailable for this report.');
    }

    const response = await fetch(`${BORQS_URL}/assetmgmt/post/${postId}/reports`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${jwtToken}`,
        'Content-Type': 'application/json',
        'Accept': '*/*',
      },
      body: JSON.stringify({
        type: reportType,
      }),
    });

    if (!response.ok) {
      throw new Error(`Unable to fetch ${reportType}.`);
    }

    const contentType = (response.headers.get('content-type') || '').toLowerCase();

    if (contentType.includes('application/json')) {
      const json = await response.json();
      const remoteUrl = json?.url || json?.signedUrl || json?.data?.url || json?.data?.signedUrl;
      if (!remoteUrl) {
        throw new Error(`Unable to prepare ${reportType}.`);
      }

      const remoteResponse = await fetch(remoteUrl);
      if (!remoteResponse.ok) {
        throw new Error(`Unable to download ${reportType}.`);
      }

      const remoteBlob = await remoteResponse.blob();
      const remoteType = remoteBlob.type || 'application/pdf';
      const remoteExtension = getExtensionFromType(remoteType);
      const safeType = reportType.replace(/\s+/g, '-').toLowerCase();
      return new File([remoteBlob], `${baseFileName}-${safeType}.${remoteExtension}`, { type: remoteType });
    }

    const blob = await response.blob();
    const fileType = blob.type || contentType || 'application/pdf';
    const extension = getExtensionFromType(fileType);
    const safeType = reportType.replace(/\s+/g, '-').toLowerCase();
    return new File([blob], `${baseFileName}-${safeType}.${extension}`, { type: fileType });
  };

  const buildVideoShareFile = async (baseFileName) => {
    if (!stitchedVideoBlobUrl) {
      throw new Error('Stitched video is not available yet.');
    }

    const response = await fetch(stitchedVideoBlobUrl);
    const blob = await response.blob();
    const fileType = blob.type || 'video/mp4';
    const extension = fileType.includes('webm') ? 'webm' : 'mp4';

    return new File([blob], `${baseFileName}-stitched-video.${extension}`, { type: fileType });
  };

  const buildShareFiles = async () => {
    const baseFileName = buildSafeFileBaseName();
    const files = [];

    if (shareSelections.video) {
      files.push(await buildVideoShareFile(baseFileName));
    }

    if (shareSelections.summary) {
      files.push(await buildReportShareFile('summary', baseFileName));
    }

    if (shareSelections.detailed) {
      files.push(await buildReportShareFile('detailed report', baseFileName));
    }

    if (files.length === 0) {
      throw new Error('Select at least one asset to share.');
    }

    const selectedLabels = SHARE_OPTIONS
      .filter((option) => shareSelections[option.id])
      .map((option) => option.label);

    return { files, selectedLabels };
  };

  const handleShareSelection = async () => {
    if (typeof window === 'undefined') return;

    try {
      setShareInProgress(true);
      const { files, selectedLabels } = await buildShareFiles();
      const supportsFileShare =
        typeof navigator !== 'undefined' &&
        !!navigator.share &&
        typeof navigator.canShare === 'function' &&
        window.isSecureContext &&
        navigator.canShare({ files });

      if (supportsFileShare) {
        try {
          await navigator.share({
            title: 'SmartInsights Share',
            text: `Shared from SmartInsights: ${selectedLabels.join(', ')}`,
            files,
          });
        } catch (error) {
          if (error?.name === 'AbortError') {
            return;
          }

          files.forEach(downloadFile);
          alert('Sharing apps are not available here, so the selected files were downloaded instead.');
        }
      } else {
        files.forEach(downloadFile);
        alert('Direct app sharing is not supported on this browser. The selected files have been downloaded instead.');
      }

      setShowSharePopup(false);
    } catch (error) {
      if (error?.name !== 'AbortError') {
        alert(error?.message || 'Unable to share the selected files right now.');
      }
    } finally {
      setShareInProgress(false);
    }
  };

  const renderHeaderActions = () => (
    <>
      <button
        className={`${styles.headerControlBtn} ${showMatchInfoPopup ? styles.headerActive : ''}`}
        onClick={() => setShowMatchInfoPopup(true)}
        title="View Set Information"
      >
        <FaInfoCircle width="14" height="14" />
        <span>Set Info</span>
      </button>

      {!trialMode && userRole === 'coach' && (
        <button
          className={`${styles.headerControlBtn} ${checkboxes.coach ? styles.headerActive : ''}`}
          onClick={() => setCheckboxes(prev => ({ ...prev, coach: !prev.coach }))}
          title="Toggle Coach Feedback"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg>
          <span>Coach Feedback</span>
        </button>
      )}

      <button
        className={styles.headerControlBtn}
        onClick={() => setShowSharePopup(true)}
        title="Share Match"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" /><line x1="8.59" y1="13.51" x2="15.42" y2="17.49" /><line x1="15.41" y1="6.51" x2="8.59" y2="10.49" /></svg>
        <span>Share</span>
      </button>
    </>
  );

  const sidePanelCanvasSize = getSidePanelCanvasSize();
  const feedbackModeActive = !trialMode && checkboxes.coach;
  const sidePanelCanvasStyle = getSidePanelCanvasStyle();

  const renderControlsBar = (extraClassName = '') => (
    <div
      className={`flex flex-wrap items-center gap-3 px-3 py-1.5 rounded-2xl my-2.5 ${styles.controlsRow} ${extraClassName}`}
      style={{
        background: 'rgba(255,255,255,0.04)',
        border: '1px solid rgba(0,177,64,0.20)',
        backdropFilter: 'blur(12px)'
      }}
    >
      <span className={`${styles.controlsLabel} ${styles.mobileControlsDesktopLabel} text-[10px] font-black tracking-[0.14em] uppercase`} style={{ color: 'rgba(163,240,192,0.5)' }}>Select Rallies</span>

      <div className={styles.mobileControlsPanel}>
        <div className={styles.mobileControlsTopRow}>
          <div className={styles.mobileControlsLabel}>Select Rallies</div>
          <div className={styles.mobileControlsSelectors}>
            {[
              { val: firstRally, key: 'first', disableVal: secondRally },
              { val: secondRally, key: 'second', disableVal: firstRally },
            ].map(({ val, key, disableVal }) => (
              <select
                key={key}
                value={val || 'none'}
                onChange={(e) => handleRallyChange(key, e.target.value)}
                disabled={!rallies.length && !trackingData?.length}
                className={styles.mobileRallySelect}
              >
                <option value="none" style={{ background: '#0a1a10' }}>None</option>
                {(() => {
                  const getId = (r) => r?.rally_id ?? r?.RallyID ?? r?.id ?? r?.number ?? r?.rallyNo;
                  const jsonIds = Array.isArray(rallies) ? rallies.map(r => Number(getId(r))).filter(Number.isFinite) : [];
                  const csvIds = Array.isArray(trackingData) ? trackingData.map(d => Number(d?.RallyID)).filter(Number.isFinite) : [];
                  const ids = Array.from(new Set([...jsonIds, ...csvIds])).sort((a, b) => a - b);
                  return ids.map(id => (
                    <option key={`${key}-${id}`} value={id} disabled={String(disableVal) === String(id)} style={{ background: '#0a1a10' }}>
                      Rally {id}
                    </option>
                  ));
                })()}
              </select>
            ))}
          </div>
        </div>

        <div className={styles.mobileControlsToggles}>
          <label className={styles.mobileControlChip}>
            <div className={styles.mobileCheckbox}>
              <input type="checkbox" checked={checkboxes.cumulative} onChange={() => handleCheckbox('cumulative')} className="sr-only peer" />
              <div className={styles.mobileCheckboxBox}>
                {checkboxes.cumulative && <svg width="10" height="10" viewBox="0 0 12 10" fill="none"><path d="M1 5L4.5 8.5L11 1" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>}
              </div>
            </div>
            <span className={`${styles.controlCheckboxLabel} ${checkboxes.cumulative ? styles.selected : ''}`}>Range</span>
          </label>

          {!trialMode && (
            <label className={styles.mobileControlChip}>
              <div className={styles.mobileCheckbox}>
                <input type="checkbox" checked={checkboxes.tracking} onChange={() => handleCheckbox('tracking')} className="sr-only peer" />
                <div className={styles.mobileCheckboxBox}>
                  {checkboxes.tracking && <svg width="10" height="10" viewBox="0 0 12 10" fill="none"><path d="M1 5L4.5 8.5L11 1" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>}
                </div>
              </div>
              <span className={`${styles.controlCheckboxLabel} ${checkboxes.tracking ? styles.selected : ''}`}>Player Tracking</span>
            </label>
          )}

          <label className={styles.mobileControlChip}>
            <div className={styles.mobileCheckbox}>
              <input type="checkbox" checked={checkboxes.heatmap} onChange={() => handleCheckbox('heatmap')} className="sr-only peer" />
              <div className={styles.mobileCheckboxBox}>
                {checkboxes.heatmap && <svg width="10" height="10" viewBox="0 0 12 10" fill="none"><path d="M1 5L4.5 8.5L11 1" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>}
              </div>
            </div>
            <span className={`${styles.controlCheckboxLabel} ${checkboxes.heatmap ? styles.selected : ''}`}>Heatmap</span>
          </label>

          <label className={styles.mobileControlChip}>
            <div className={styles.mobileCheckbox}>
              <input type="checkbox" checked={checkboxes.occupancy} onChange={() => handleCheckbox('occupancy')} className="sr-only peer" />
              <div className={styles.mobileCheckboxBox}>
                {checkboxes.occupancy && <svg width="10" height="10" viewBox="0 0 12 10" fill="none"><path d="M1 5L4.5 8.5L11 1" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>}
              </div>
            </div>
            <span className={`${styles.controlCheckboxLabel} ${checkboxes.occupancy ? styles.selected : ''}`}>Occupancy</span>
          </label>
        </div>

        <button
          type="button"
          onClick={handleCompare}
          disabled={([firstRally, secondRally].filter(v => !(v === null || v === undefined || v === 'none' || v === 'undefined' || String(v).trim() === '')).length === 0)}
          aria-disabled={([firstRally, secondRally].filter(v => !(v === null || v === undefined || v === 'none' || v === 'undefined' || String(v).trim() === '')).length === 0)}
          title={([firstRally, secondRally].filter(v => !(v === null || v === undefined || v === 'none' || v === 'undefined' || String(v).trim() === '')).length === 0) ? 'Select at least one rally to fetch' : undefined}
          className={styles.mobileFetchBtn}
        >
          <span className={styles.mobileFetchBtnGlow} aria-hidden="true" />
          <span className={styles.mobileFetchBtnText}>
            {checkboxes.cumulative ? 'Fetch' : ([firstRally, secondRally].filter(v => v && v !== 'none').length === 2 ? 'Compare' : 'Fetch')}
          </span>
        </button>
      </div>
      {showCumDialog && createPortal(
        (
          <div
            className="fixed inset-0 w-screen h-screen bg-[rgba(30,40,60,0.26)] flex items-center justify-center z-[2147483647] pointer-events-auto animate-fadeIn"
            role="dialog"
            aria-modal="true"
            aria-labelledby="cumDialogTitle"
            onClick={() => setShowCumDialog(false)}
          >
            <div className="bg-white rounded-[22px] shadow-[0_8px_44px_rgba(30,40,60,0.19)] py-10 px-9 min-w-[340px] max-w-[90vw] text-center animate-dialogPop border-t-[7px] border-[#1db954] relative flex flex-col items-center z-[2147483647]" onClick={(e) => e.stopPropagation()}>
              <button type="button" aria-label="Close" className="absolute top-2 right-2 bg-transparent border-none text-[#6b7280] text-xl leading-none cursor-pointer p-1 rounded-md hover:bg-black/5 hover:text-[#374151] active:scale-95 transition-all" onClick={() => setShowCumDialog(false)}>×</button>
              <div className="-mt-14 mb-2.5 bg-white rounded-full shadow-[0_2px_10px_rgba(30,40,60,0.10)] p-2 flex items-center justify-center" aria-hidden>
                <img src="/playground/shuttle.png" alt="Shuttlecock" className="animate-spin-slow" width={38} height={38} />
              </div>
              <h3 id="cumDialogTitle" className="text-xl font-extrabold mb-2.5 text-[#222] tracking-tight">Select Two Rallies</h3>
              <p className="text-base text-[#555] mb-6 mt-0.5">Select two rallies to view their cumulative stats.</p>
              <div className="flex gap-5 justify-center mt-2">
                <button type="button" className="bg-gradient-to-r from-[#1db954] to-[#159c43] text-white border-none rounded-lg px-7 py-3 text-base font-bold cursor-pointer shadow-sm hover:from-[#159c43] hover:to-[#1db954] hover:shadow-md transition-all" onClick={() => setShowCumDialog(false)}>OK</button>
              </div>
            </div>
          </div>
        ),
        document.body
      )}
    </div>
  );

  const renderMetricOverview = (extraClassName = '', railRef = metricCarouselRef) => {
    const totalDistA = memoTotalDistA;
    const totalDistB = memoTotalDistB;
    const totalShotsA = memoTotalShotsA;
    const totalShotsB = memoTotalShotsB;
    const avgDurBV = memoAvgDuration;
    const countRallies = memoTotalRalliesCount;

    const items = [];
    items.push({ type: 'single', label: 'WINNER', value: memoMatchCards.winnerName, sub: 'set winner' });
    items.push({ type: 'single', label: 'FINAL SCORE', value: memoMatchCards.finalScoreStr, sub: 'final score' });
    items.push({ type: 'single', label: 'PLAYER NAME', value: memoMatchCards.pName, sub: memoMatchCards.pPos || 'info' });

    const getPct = (a, b) => {
      const total = a + b;
      if (total === 0) return { a: 50, b: 50 };
      return { a: Math.round((a / total) * 100), b: Math.round((b / total) * 100) };
    };


    const distPct = getPct(totalDistA, totalDistB);
    items.push({
      type: 'comparison',
      label: 'DISTANCE COVERED',
      valA: `${totalDistA.toFixed(1)} m`,
      valB: `${totalDistB.toFixed(1)} m`,
      pctA: distPct.a,
      pctB: distPct.b
    });

    const shotsPct = getPct(totalShotsA, totalShotsB);
    items.push({
      type: 'comparison',
      label: 'TOTAL HITS',
      valA: totalShotsA,
      valB: totalShotsB,
      pctA: shotsPct.a,
      pctB: shotsPct.b
    });

    items.push({ type: 'single', label: 'TOTAL RALLIES', value: countRallies, sub: 'set total' });
    items.push({ type: 'single', label: 'AVG RALLY DUR', value: `${avgDurBV.toFixed(1)}s`, sub: 'duration' });

    const showRangeTable = fetchedCheckboxes.cumulative && compareClicked;
    if (fetchedCheckboxes.cumulative && showRangeTable && memoRangeStats && !memoRangeStats.empty) {
      const winsA = memoRangeStats.wins?.A ?? 0;
      const winsB = memoRangeStats.wins?.B ?? 0;
      const winsPct = getPct(winsA, winsB);

      items.push({ type: 'single', label: 'RANGE', value: `${memoRangeStats.start || '-'}-${memoRangeStats.end || '-'}`, sub: 'rallies' });
      items.push({ type: 'single', label: 'RALLIES', value: memoRangeStats.totalRallies, sub: 'rallies in range' });
      items.push({ type: 'single', label: 'RANGE DUR', value: `${Number(memoRangeStats.duration || 0).toFixed(1)}s`, sub: 'total duration' });

      items.push({
        type: 'comparison',
        label: 'RANGE WINS',
        valA: winsA,
        valB: winsB,
        pctA: winsPct.a,
        pctB: winsPct.b
      });

      const rdA = Number(memoRangeStats.distance?.A || 0);
      const rdB = Number(memoRangeStats.distance?.B || 0);
      const rdPct = getPct(rdA, rdB);
      items.push({
        type: 'comparison',
        label: 'RANGE DIST',
        valA: `${rdA.toFixed(1)} m`,
        valB: `${rdB.toFixed(1)} m`,
        pctA: rdPct.a,
        pctB: rdPct.b
      });

      const rhA = Number(memoRangeStats.hits?.A || 0);
      const rhB = Number(memoRangeStats.hits?.B || 0);
      const rhPct = getPct(rhA, rhB);
      items.push({
        type: 'comparison',
        label: 'RANGE HITS',
        valA: rhA,
        valB: rhB,
        pctA: rhPct.a,
        pctB: rhPct.b
      });
    } else {
      const fallbackRangeStart = fetchedRallyIds?.[0];
      const fallbackRangeEnd = fetchedRallyIds?.[1];
      const hasFallbackRange = fallbackRangeStart && fallbackRangeStart !== 'none' && fallbackRangeEnd && fallbackRangeEnd !== 'none';
      const fallbackRangeLabel = hasFallbackRange ? `${fallbackRangeStart}-${fallbackRangeEnd}` : '1-14';

      items.push({ type: 'single', label: 'RALLIES', value: memoTotalRalliesCount, sub: 'rallies in range' });
      items.push({ type: 'single', label: 'RANGE', value: fallbackRangeLabel, sub: 'rallies' });
      items.push({ type: 'single', label: 'RANGE DUR', value: `${avgDurBV.toFixed(1)}s`, sub: 'total duration' });
    }

    if (firstRally && firstRally !== 'none') {
      const rid = firstRally;
      const getId = (r) => r?.rally_id ?? r?.RallyID ?? r?.id ?? r?.number ?? r?.rallyNo;
      const rallyMeta = Array.isArray(rallies) ? rallies.find(r => String(getId(r)) === String(rid)) : null;

      if (rallyMeta) {
        items.push({ type: 'single', label: 'RALLY #', value: rid, sub: '#' });
        items.push({ type: 'single', label: 'WINNER', value: typeof rallyMeta.winner === 'string' ? rallyMeta.winner : (rallyMeta.winner?.a || rallyMeta.winner?.A || rallyMeta.winner?.b || rallyMeta.winner?.B || 'N/A'), sub: 'player' });

        const rallyDuration = durOfBV(rallyMeta);
        items.push({ type: 'single', label: 'DURATION', value: rallyDuration !== null ? `${rallyDuration.toFixed(1)}s` : 'N/A', sub: 'rally time' });

        let scoreA = scoreAOfBV(rallyMeta);
        let scoreB = scoreBOfBV(rallyMeta);
        if (scoreA === null || scoreB === null) {
          const cumulative = getCumulativeScoreUntilBV(rid);
          if (cumulative) {
            scoreA = cumulative.sA;
            scoreB = cumulative.sB;
          }
        }

        if (scoreA !== null && scoreB !== null) {
          items.push({ type: 'single', label: 'SCORE', value: `${scoreA}-${scoreB}`, sub: 'at rally' });
        }

        const rallyDistA = distAOfBV(rallyMeta);
        const rallyDistB = distBOfBV(rallyMeta);
        const rallyDistPct = getPct(rallyDistA, rallyDistB);
        items.push({
          type: 'comparison',
          label: `RALLY ${rid} DIST`,
          valA: `${rallyDistA.toFixed(1)} m`,
          valB: `${rallyDistB.toFixed(1)} m`,
          pctA: rallyDistPct.a,
          pctB: rallyDistPct.b
        });

        const rallyHitsA = hitsAOfBV(rallyMeta);
        const rallyHitsB = hitsBOfBV(rallyMeta);
        const rallyHitsPct = getPct(rallyHitsA, rallyHitsB);
        items.push({
          type: 'comparison',
          label: `RALLY ${rid} HITS`,
          valA: rallyHitsA,
          valB: rallyHitsB,
          pctA: rallyHitsPct.a,
          pctB: rallyHitsPct.b
        });

        items.push({ type: 'single', label: 'TOTAL DIST', value: `${(rallyDistA + rallyDistB).toFixed(1)}m`, sub: 'total' });
      }
    }

    return (
      <div className={`${styles.metricCarouselContainer} ${extraClassName}`}>
        <div className={styles.metricRailHeader}>
          <div className={styles.metricRailTitle}>
            Video Overview
            <div className={styles.railLegend}>
              <div className={styles.legendItem}>
                <div className={`${styles.legendChip} ${styles.chipA}`}>A</div>
                <span>Far Side Player</span>
              </div>
              <div className={styles.legendItem}>
                <div className={`${styles.legendChip} ${styles.chipB}`}>B</div>
                <span>Near Side Player</span>
              </div>
            </div>
          </div>
          <div className={styles.metricRailNavGroup}>
            <button
              type="button"
              className={styles.metricRailNavBtn}
              onClick={() => scrollMetricRail(-1, railRef)}
              aria-label="Scroll metric cards left"
            >
              <FaChevronLeft />
            </button>
            <button
              type="button"
              className={styles.metricRailNavBtn}
              onClick={() => scrollMetricRail(1, railRef)}
              aria-label="Scroll metric cards right"
            >
              <FaChevronRight />
            </button>
          </div>
        </div>

        <div className={styles.carouselViewport} ref={railRef}>
          <div className={styles.carouselTrack}>
            {items.map((item, idx) => (
              <div key={`${item.label}-${idx}`} className={styles.carouselItem}>
                <div className={styles.dataMetricCard}>
                  <div className={styles.dmLabel}>{item.label}</div>
                  {item.type === 'single' ? (
                    <div className={styles.dmSingleContent}>
                      <div className={`${styles.dmValue} ${(item.label === 'WINNER' || item.label === 'PLAYER' || item.label === 'PLAYER NAME') ? styles.dmValueSmall : ''}`}>{item.value}</div>
                      <div className={styles.dmSubText}>{item.sub}</div>
                    </div>
                  ) : (
                    <div className={styles.dmComparisonContent}>
                      <div className={styles.dmCompRow}>
                        <div className={`${styles.playerLabel} ${styles.playerA}`}>A</div>
                        <div className={styles.dmCompValue}>{item.valA}</div>
                      </div>
                      <div className={styles.dmCompRow}>
                        <div className={`${styles.playerLabel} ${styles.playerB}`}>B</div>
                        <div className={styles.dmCompValue}>{item.valB}</div>
                      </div>
                      <div className={styles.dmProgressBar}>
                        <div className={styles.barA} style={{ width: `${item.pctA}%` }} />
                        <div className={styles.barB} style={{ width: `${item.pctB}%` }} />
                      </div>
                      <div className={styles.dmPercentRow}>
                        <div className={styles.pctA}>{item.pctA}%</div>
                        <div className={styles.pctB}>{item.pctB}%</div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const renderSideUtilitiesGroup = (extraClassName = '', includeControls = false) => (
    <div className={`${styles.sideUtilitiesGroup} ${extraClassName}`}>
      <div className={styles.matchStats}>
        {selectedVideo && selectedVideo !== 'none' && (
          <div className={styles.sidePanelVisuals}>
            <div className={styles.sideTabs}>
              <button
                className={`${styles.sideTab} ${activeSideTab === 'tracking' ? styles.activeSideTab : ''}`}
                onClick={() => setActiveSideTab('tracking')}
                title="Player Tracking"
                aria-label="Player Tracking"
              >
                <span className={`${styles.sideTabIcon} ${styles.sideTabIconTracking}`} aria-hidden="true" />
                <span className={styles.sideTabLabel}>Track</span>
              </button>
              <button
                className={`${styles.sideTab} ${activeSideTab === 'heatmap' ? styles.activeSideTab : ''}`}
                onClick={() => setActiveSideTab('heatmap')}
                title="Heatmap"
                aria-label="Heatmap"
              >
                <span className={`${styles.sideTabIcon} ${styles.sideTabIconHeatmap}`} aria-hidden="true" />
                <span className={styles.sideTabLabel}>Heat</span>
              </button>
              <button
                className={`${styles.sideTab} ${activeSideTab === 'occupancy' ? styles.activeSideTab : ''}`}
                onClick={() => setActiveSideTab('occupancy')}
                title="Occupancy"
                aria-label="Occupancy"
              >
                <span className={`${styles.sideTabIcon} ${styles.sideTabIconOccupancy}`} aria-hidden="true" />
                <span className={styles.sideTabLabel}>Zones</span>
              </button>
            </div>

            {activeSideTab === 'tracking' && (
              <div
                className={`${styles.sideVisualItem} ${styles.zoomableCanvas}`}
                key={`tracking-${sidePanelCanvasRenderKey}`}
                onClick={() => setZoomedViz({ type: 'tracking', isSidePanel: true })}
                title="Click to Zoom"
              >
                <PlayerTrackingCanvas
                  data={getCleanedTrackingPoints(sideRallyIds)}
                  hits={getCleanedTrackingPoints(sideRallyIds)}
                  width={sidePanelCanvasSize.width}
                  height={sidePanelCanvasSize.height}
                  showLegend={false}
                  pathColors={{ A: '#ef4444', B: '#60A5FA' }}
                  pathStrokeWidth={2}
                  style={sidePanelCanvasStyle}
                />
              </div>
            )}

            {activeSideTab === 'heatmap' && (
              <div
                className={`${styles.sideVisualItem} ${styles.zoomableCanvas}`}
                key={`heatmap-${sidePanelCanvasRenderKey}`}
                onClick={() => setZoomedViz({ type: 'heatmap', isSidePanel: true })}
                title="Click to Zoom"
              >
                <OccupancyHeatmapCanvas
                  data={getCleanedTrackingPoints(sideRallyIds)}
                  width={sidePanelCanvasSize.width}
                  height={sidePanelCanvasSize.height}
                  showLegend={false}
                  style={sidePanelCanvasStyle}
                />
              </div>
            )}

            {activeSideTab === 'occupancy' && (
              <div
                className={`${styles.sideVisualItem} ${styles.zoomableCanvas}`}
                key={`occupancy-${sidePanelCanvasRenderKey}`}
                onClick={() => setZoomedViz({ type: 'occupancy', isSidePanel: true })}
                title="Click to Zoom"
              >
                <GridOccupancyCanvas
                  csvUrl={(() => {
                    const vOpt = allVideoOptions.find(opt => opt.value === selectedVideo);
                    return vOpt ? (vOpt.occupancyPath || (vOpt ? `${vOpt.basePath}occupancy/${vOpt.value}_occupancy.csv` : null)) : null;
                  })()}
                  width={sidePanelCanvasSize.width}
                  height={sidePanelCanvasSize.height}
                  gridCols={2}
                  gridRows={6}
                  rallyId={!Array.isArray(sideRallyIds) ? sideRallyIds : undefined}
                  rallyIds={Array.isArray(sideRallyIds) ? sideRallyIds : undefined}
                  showLegend={false}
                  trackingPoints={getCleanedTrackingPoints(sideRallyIds)}
                  style={sidePanelCanvasStyle}
                />
              </div>
            )}
          </div>
        )}
      </div>

      {includeControls && selectedVideo && selectedVideo !== 'none' && (
        renderControlsBar(styles.inlineControlsMobile)
      )}
      {!feedbackModeActive && selectedVideo && selectedVideo !== 'none' && (
        renderMetricOverview(styles.tabletMetricsPanel, tabletMetricCarouselRef)
      )}
    </div>
  );

  // Update comment text in the first comment and persist
  const handleUpdateComment = async (annotation) => {
    try {
      try { ReactGA.event({ category: 'Playground', action: 'feedback_update_comment' }); } catch { }
      if (!annotation?._id) return;
      const updatedList = (Array.isArray(standaloneAnnotations) ? standaloneAnnotations : []).map(a => {
        if (a._id !== annotation._id) return a;
        const comments = Array.isArray(a.comments) && a.comments.length ? [...a.comments] : [{ body: '', metaData: {} }];
        comments[0] = { ...(comments[0] || {}), body: editedCommentText };
        return { ...a, comments };
      });
      dispatch(setStandaloneFeedbacks(updatedList));

      // Persist
      const jwtToken = (() => { try { return localStorage.getItem('BQS_TOKEN'); } catch { return null; } })();
      const postId = getPostIdForSelected();
      // Map to API schema
      const fps = 30;
      const feedbackData = updatedList.map((a) => {
        const t = Number(a?.timeRange?.startTime) || 0;
        const tRounded = Number(t.toFixed(3));
        const coordsSafe = {
          x1: a?.markerDetails?.coordinates?.x1 || 0,
          y1: a?.markerDetails?.coordinates?.y1 || 0,
          x2: a?.markerDetails?.coordinates?.x2 || 0,
          y2: a?.markerDetails?.coordinates?.y2 || 0,
        };
        const base = {
          timeRange: { startTime: tRounded, endTime: tRounded },
          markerDetails: { coordinates: coordsSafe },
          comments: (Array.isArray(a.comments) ? a.comments : []).map(c => ({ metaData: c.metaData, body: c.body, type: c.type })),
        };
        // Only include _id in standalone mode (parity with CreateFeedback.jsx)
        return env === 'standalone' ? { _id: a?._id, ...base } : base;
      });
      if (jwtToken && postId) {
        await createUpdateFeedback(jwtToken, feedbackData, postId);
        // Refetch to mirror server-normalized state
        try {
          const resp = await getCoachFeedback(jwtToken, postId);
          const apiFeedbacks = resp?.data?.feedbacks || resp?.data || [];
          const arr = Array.isArray(apiFeedbacks) ? apiFeedbacks : [];
          const mapped = arr.map((a, idx) => ({
            _id: a?._id || `${Date.now()}-${idx}`,
            timeRange: a?.timeRange || { startTime: Number(a?.timeSec) || 0, endTime: Number(a?.timeSec) || 0 },
            markerDetails: { coordinates: a?.markerDetails?.coordinates || { x1: 0, y1: 0, x2: 0, y2: 0 } },
            comments: Array.isArray(a?.comments) ? a.comments.map(c => ({ body: c?.body, metaData: c?.metaData })) : [],
          }));
          dispatch(setStandaloneFeedbacks(mapped));
        } catch { }
      }
      setEditingCommentId(null);
      setEditedCommentText('');
    } catch { }
  };

  const clearAllFeedbacks = async () => {
    try {
      try { ReactGA.event({ category: 'Playground', action: 'feedback_clear_all' }); } catch { }
      try { console.debug('[PG] clearAllFeedbacks called. selectedVideo:', selectedVideo); } catch { }
      dispatch(setStandaloneFeedbacks([]));
      const jwtToken = (() => { try { return localStorage.getItem('BQS_TOKEN'); } catch { return null; } })();
      const postId = getPostIdForSelected();
      try { console.debug('[PG] clearAllFeedbacks -> token?', !!jwtToken, 'postId:', postId); } catch { }
      if (jwtToken && postId) {
        await createUpdateFeedback(jwtToken, [], postId);
        try { console.debug('[PG] POST createUpdateFeedback (clear) OK for postId:', postId); } catch { }
        // Refetch to ensure server state is reflected
        try {
          const resp = await getCoachFeedback(jwtToken, postId);
          try { console.debug('[PG] GET after clear OK for postId:', postId); } catch { }
          const apiFeedbacks = resp?.data?.feedbacks || resp?.data || [];
          const arr = Array.isArray(apiFeedbacks) ? apiFeedbacks : [];
          const mapped = arr.map((a, idx) => ({
            _id: a?._id || `${Date.now()}-${idx}`,
            timeRange: a?.timeRange || { startTime: Number(a?.timeSec) || 0, endTime: Number(a?.timeSec) || 0 },
            markerDetails: { coordinates: a?.markerDetails?.coordinates || { x1: 0, y1: 0, x2: 0, y2: 0 } },
            comments: Array.isArray(a?.comments) ? a.comments.map(c => ({ body: c?.body, metaData: c?.metaData })) : [],
          }));
          dispatch(setStandaloneFeedbacks(mapped));
        } catch { }
      }
    } catch { }
  };

  const handleAudioPlay = (audioElement/*, annotation*/) => {
    try {
      // Pause any other audio
      document.querySelectorAll('audio').forEach(a => { if (a !== audioElement) { try { a.pause(); } catch { } } });
      // Pause video if playing
      const el = videoRef?.current;
      if (el && !el.paused) { try { el.pause(); } catch { } }
      setPlayingAudioId(Date.now());
      audioElement.addEventListener('ended', () => setPlayingAudioId(null), { once: true });
    } catch { }
  };

  // When a coach feedback is deleted, also remove the corresponding marker at that time
  const handleDeleteCoachFeedback = async (id) => {
    // find timeSec of the feedback being deleted
    const item = Array.isArray(coachFeedbacks) ? coachFeedbacks.find(it => it.id === id) : null;
    try { ReactGA.event({ category: 'Playground', action: 'feedback_delete', label: String(id) }); } catch { }
    const t = item ? Number(item.timeSec) || 0 : null;
    if (t != null) {
      dispatch(removeStandaloneFeedbackAtTime({ timeSec: t, tol: 0.75 }));
    }
    // invoke the hook's delete to update the local list
    try { coachFeedbackHook.handleDeleteFeedback(id); } catch { }

    // Persist updated list to backend and refresh (CreateFeedback parity)
    try {
      const jwtToken = (() => { try { return localStorage.getItem('BQS_TOKEN'); } catch { return null; } })();
      const postId = getPostIdForSelected();
      if (!jwtToken || !postId) return;
      try { console.debug('[PG] handleDeleteCoachFeedback -> id:', id, 'token?', !!jwtToken, 'postId:', postId); } catch { }
      const afterDelete = Array.isArray(standaloneAnnotations) ? standaloneAnnotations.filter(a => a.id !== id && a._id !== id) : [];
      const feedbackData = afterDelete.map((a, idx) => {
        const t = Number(a?.timeRange?.startTime) || 0;
        const tRounded = Number(t.toFixed(3));
        const coordsSafe = {
          x1: a?.markerDetails?.coordinates?.x1 || 0,
          y1: a?.markerDetails?.coordinates?.y1 || 0,
          x2: a?.markerDetails?.coordinates?.x2 || 0,
          y2: a?.markerDetails?.coordinates?.y2 || 0,
        };
        const base = {
          timeRange: { startTime: tRounded, endTime: tRounded },
          markerDetails: { coordinates: coordsSafe },
          comments: (Array.isArray(a.comments) ? a.comments : []).map(c => ({ metaData: c.metaData, body: c.body, type: c.type })),
        };
        return env === 'standalone' ? { _id: a?._id, ...base } : base;
      });
      await createUpdateFeedback(jwtToken, feedbackData, postId);
      try { console.debug('[PG] POST createUpdateFeedback (delete) OK. newCount:', afterDelete.length); } catch { }
      try {
        const resp = await getCoachFeedback(jwtToken, postId);
        try { console.debug('[PG] GET after delete OK. postId:', postId); } catch { }
        const apiFeedbacks = resp?.data?.feedbacks || resp?.data || [];
        const arr = Array.isArray(apiFeedbacks) ? apiFeedbacks : [];
        const mapped = arr.map((a, i) => ({
          _id: a?._id || `${Date.now()}-${i}`,
          timeRange: a?.timeRange || { startTime: Number(a?.timeSec) || 0, endTime: Number(a?.timeSec) || 0 },
          markerDetails: { coordinates: a?.markerDetails?.coordinates || { x1: 0, y1: 0, x2: 0, y2: 0 } },
          comments: Array.isArray(a?.comments) ? a.comments.map(c => ({ body: c?.body, metaData: c?.metaData })) : [],
        }));
        dispatch(setStandaloneFeedbacks(mapped));
      } catch { }
    } catch { }
  };

  // Commit pending bounding box and save feedback (text only) similar to CreateFeedback.jsx
  const handleSaveCoachFeedback = async () => {
    try { ReactGA.event({ category: 'Playground', action: 'feedback_save' }); } catch { }
    // Gather current inputs BEFORE the hook clears them
    const text = (coachFeedbackHook.coachFeedbackText || '').trim();
    // Require at least a text comment
    if (!text) { try { console.warn('Please provide a comment or use voice typing.'); } catch { } return; }

    // Derive current playback time
    let now = 0;
    try {
      const el = videoRef?.current;
      if (el && typeof el.currentTime === 'number') now = Number(el.currentTime) || 0;
    } catch { }

    // Build comments with metadata
    const commentsToAdd = [];
    const userId = (() => { try { return localStorage.getItem('BQS__ID') || localStorage.getItem('userId') || 'local-user'; } catch { return 'local-user'; } })();
    const firstName = (() => { try { return localStorage.getItem('firstname') || ''; } catch { return ''; } })();
    const lastName = (() => { try { return localStorage.getItem('lastname') || ''; } catch { return ''; } })();
    const userName = [firstName, lastName].filter(Boolean).join(' ') || 'coach';
    const metaBase = { dateTime: new Date().toISOString(), userId, userName };
    if (text) { commentsToAdd.push({ _id: `${crypto?.randomUUID ? crypto.randomUUID() : Date.now() + '-t'}`, body: text, metaData: { ...metaBase }, type: 'text' }); }

    // Build annotation using pending normalized coordinates when available
    const coords = pendingAnnotation && pendingAnnotation.x1 != null
      ? { ...pendingAnnotation }
      : { x1: 0, y1: 0, x2: 0, y2: 0 };

    // Create separate annotations for each comment, mirroring CreateFeedback.jsx
    const newAnnotations = commentsToAdd.map((c) => ({
      _id: `${Math.floor(now * 1000)}-${Math.random().toString(36).slice(2, 7)}`,
      timeRange: { startTime: now, endTime: now },
      markerDetails: { coordinates: coords },
      comments: [c],
    }));

    // Update local redux list immediately for UI responsiveness
    newAnnotations.forEach((ann) => dispatch(addStandaloneFeedbackAction(ann)));
    setPendingAnnotation(null);

    // Persist annotations to backend when possible (mirror CreateFeedback saveFeedback mapping)
    try {
      const jwtToken = (() => { try { return localStorage.getItem('BQS_TOKEN'); } catch { return null; } })();
      const postId = (selectedVideo && selectedVideo !== 'none') ? getPostIdForSelected() : null;
      const updatedAnnotations = [...(Array.isArray(standaloneAnnotations) ? standaloneAnnotations : []), ...newAnnotations];

      // Map UI annotations to API schema
      const accountId = (() => { try { return localStorage.getItem('BQS__ID') || localStorage.getItem('userId') || null; } catch { return null; } })();
      const feedbackData = updatedAnnotations.map((a) => {
        const t = Number(a?.timeRange?.startTime) || 0;
        const tRounded = Number(t.toFixed(3));
        const coordsSafe = {
          x1: a?.markerDetails?.coordinates?.x1 || 0,
          y1: a?.markerDetails?.coordinates?.y1 || 0,
          x2: a?.markerDetails?.coordinates?.x2 || 0,
          y2: a?.markerDetails?.coordinates?.y2 || 0,
        };
        const base = {
          timeRange: { startTime: tRounded, endTime: tRounded },
          markerDetails: { coordinates: coordsSafe },
          comments: (Array.isArray(a.comments) ? a.comments : []).map((c) => ({ metaData: c.metaData, body: c.body, type: c.type })),
        };
        return env === 'standalone' ? { _id: a?._id, ...base } : base;
      });

      if (jwtToken && postId) {
        try { console.debug('[PG] handleSaveCoachFeedback -> token?', !!jwtToken, 'postId:', postId, 'payloadCount:', feedbackData.length); } catch { }
        try {
          await createUpdateFeedback(jwtToken, feedbackData, postId);
          try { console.debug('[PG] POST createUpdateFeedback (save) OK. count:', feedbackData.length); } catch { }
          // Always refresh from backend to ensure parity with server state
          try {
            const resp = await getCoachFeedback(jwtToken, postId);
            try { console.debug('[PG] GET after save OK. postId:', postId); } catch { }
            const apiFeedbacks = resp?.data?.feedbacks || resp?.data || [];
            const arr = Array.isArray(apiFeedbacks) ? apiFeedbacks : [];
            const mapped = arr.map((a, idx) => ({
              _id: a?._id || `${Date.now()}-${idx}`,
              timeRange: a?.timeRange || { startTime: Number(a?.timeSec) || 0, endTime: Number(a?.timeSec) || 0 },
              markerDetails: { coordinates: a?.markerDetails?.coordinates || { x1: 0, y1: 0, x2: 0, y2: 0 } },
              comments: Array.isArray(a?.comments) ? a.comments.map(c => ({ body: c?.body, metaData: c?.metaData })) : [],
            }));
            dispatch(setStandaloneFeedbacks(mapped));
          } catch { }
        } catch (e) {
          // Non-fatal; keep local state and allow retry later
          try { console.warn('Feedback POST failed:', e?.message || e); } catch { }
        }
      }
    } catch { }

    // Maintain hook's local list for the panel UX
    try { coachFeedbackHook.handleSaveCoachFeedback(); } catch { }
  };
  const [fetchedCheckboxes, setFetchedCheckboxes] = useState({ occupancy: true, tracking: false, cumulative: true, heatmap: true, coach: false });
  const [chat, setChat] = useState([]);
  const questionsScrollRef = useRef(null);
  const [showQuestionsArrow, setShowQuestionsArrow] = useState(false);

  // Helper to check if predefined questions need a scroll arrow
  const updateQuestionsScroll = () => {
    if (questionsScrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = questionsScrollRef.current;
      // Show arrow if there's more content to the right (with a small buffer)
      setShowQuestionsArrow(scrollLeft + clientWidth < scrollWidth - 10);
    }
  };

  // Attach scroll listener to predefined questions
  useEffect(() => {
    const el = questionsScrollRef.current;
    if (!el) return;
    const handleScroll = () => updateQuestionsScroll();
    el.addEventListener('scroll', handleScroll);
    // Initial check and check on chat change
    updateQuestionsScroll();
    return () => el.removeEventListener('scroll', handleScroll);
  }, [chat]);
  // Signup gate dialog for trial/non-signed-in users
  const [showSignupGate, setShowSignupGate] = useState(false);
  // Landing-only: select video dialog visibility
  const [showSelectVideoDialog, setShowSelectVideoDialog] = useState(false);
  // Confirm dialog before changing video when one is already selected
  const [showConfirmChangeVideo, setShowConfirmChangeVideo] = useState(false);
  // Keep track of what action to perform after confirming a video change
  const [pendingVideoOption, setPendingVideoOption] = useState(null); // stores option to select after confirm
  const [pendingOpenSelect, setPendingOpenSelect] = useState(false); // whether to open selection dialog after confirm
  // When true, bypass confirmation for the very next selection (used after user confirms change)
  const [suppressNextConfirm, setSuppressNextConfirm] = useState(false);
  // Effect trigger to open Select dialog after confirm; avoids event bubbling/race
  const [openSelectAfterConfirm, setOpenSelectAfterConfirm] = useState(false);
  // Track when the select dialog opened to ignore immediate overlay click
  const [selectDialogOpenedAt, setSelectDialogOpenedAt] = useState(0);
  // Track explicit user interactions (Fetch/Compare/Search) after video selection
  const [hasUserAction, setHasUserAction] = useState(false);
  // Dialog: prompt user to select two rallies for cumulative mode
  const [askPanelOpen, setAskPanelOpen] = React.useState(true);
  const [analysisPanelOpen, setAnalysisPanelOpen] = React.useState(true);
  const [showCumDialog, setShowCumDialog] = useState(false);
  // Persist the last fetched rally IDs so UI doesn't change until the user clicks Fetch again
  const [fetchedRallyIds, setFetchedRallyIds] = useState([]);
  // Coach name from profile (Redux)
  const userProfile = useSelector((state) => state?.user?.profile);
  const profileData = userProfile?.value?.profileData || {};
  const coachName = (
    profileData?.userName ||
    profileData?.username ||
    [profileData?.name?.firstName, profileData?.name?.lastName].filter(Boolean).join(' ') ||
    'Coach'
  );

  // Gate access using the same logic as "Get AI feedback":
  // userProfile.value.exclusiveFeaturesList contains entries with exclusiveFeatureName
  // We check for 'Generate AI Feedback' to permit access for whitelisted users.
  const hasGenerateAIFeedback = !!(userProfile?.value?.exclusiveFeaturesList?.some(
    (feature) => feature?.exclusiveFeatureName === 'Generate AI Feedback'
  ));
  const profileLoading = !!userProfile?.isStarted;

  // Ensure profile is loaded for authenticated coaches so we can evaluate exclusiveFeaturesList
  useEffect(() => {
    try {
      const token = localStorage.getItem('BQS_TOKEN');
      if (isAuthed && userRole === 'coach' && token) {
        const hasProfile = userProfile && userProfile.value && Object.keys(userProfile.value).length > 0;
        if (!hasProfile && !profileLoading) {
          dispatch(getUserProfile(token));
        }
      }
    } catch { }
  }, [isAuthed, userRole, dispatch, userProfile, profileLoading]);
  // Player tracking data state
  const [trackingData, setTrackingData] = useState(null);
  const [hitsData, setHitsData] = useState(null);
  const [trackingLoading, setTrackingLoading] = useState(false);
  const [trackingError, setTrackingError] = useState(null);

  // Memoized tracking points for occupancy to prevent infinite loop
  const getCleanedTrackingPoints = useMemo(() => {
    if (!Array.isArray(trackingData)) return () => [];

    const clean = (arr) => arr
      .map(it => ({
        ...it,
        frame: Number(it.frame),
        mid_x: Number(it.mid_x),
        mid_y: Number(it.mid_y)
      }))
      .filter(it => Number.isFinite(it.frame) && Number.isFinite(it.mid_x) && Number.isFinite(it.mid_y));

    // Cache to prevent Re-filtering 10,000 points 20 times per render in the chat map
    const cache = new Map();

    return (rallyIdOrIds) => {
      if (!rallyIdOrIds) {
        if (!cache.has('all')) cache.set('all', clean(trackingData));
        return cache.get('all');
      }

      // Convert array of IDs to a sorted string key for caching
      let cacheKey = '';
      if (Array.isArray(rallyIdOrIds)) {
        cacheKey = rallyIdOrIds.slice().sort().join(',');
      } else {
        cacheKey = String(rallyIdOrIds);
      }

      if (cache.has(cacheKey)) return cache.get(cacheKey);

      // Support array of rally IDs for cumulative/range analysis
      let result;
      if (Array.isArray(rallyIdOrIds)) {
        const rallySet = new Set(rallyIdOrIds.map(String));
        result = clean(trackingData.filter(item => rallySet.has(String(item.RallyID))));
      } else {
        // Single rally ID
        result = clean(trackingData.filter(item => String(item.RallyID) === String(rallyIdOrIds)));
      }

      cache.set(cacheKey, result);
      return result;
    };
  }, [trackingData]);

  const [input, setInput] = useState('');
  // Analytics are query-driven
  const [analyticsView, setAnalyticsView] = useState(null); // e.g., 'win_percentage'
  const [winStats, setWinStats] = useState(null); // cached overall win stats
  // API-provided phased win percentages (if available)
  const [winPhaseStats, setWinPhaseStats] = useState(null);
  // Phase stats parsed directly from latest LLM chat text (preferred when present)
  const [llmPhaseStats, setLlmPhaseStats] = useState(null);
  // Overall win % parsed directly from latest LLM chat text (two percentages)
  const [llmOverallWin, setLlmOverallWin] = useState(null);
  // When true, force showing only the overall win-rate chart (no phase breakdown)
  const [forceOverallWinOnly, setForceOverallWinOnly] = useState(false);
  // Chat loading state
  const [chatLoading, setChatLoading] = useState(false);
  // Tracking visualization UI controls
  const [legendVisible, setLegendVisible] = useState(false);
  const [strokeWidth, setStrokeWidth] = useState(4);



  const [firstRally, setFirstRally] = useState('none');
  const [secondRally, setSecondRally] = useState('none');
  const [currentRallyId, setCurrentRallyId] = useState(null);
  const [activeSideTab, setActiveSideTab] = useState('tracking'); // 'tracking' | 'heatmap' | 'occupancy'
  const isTabletViewport = typeof window !== 'undefined' && window.innerWidth >= 768 && window.innerWidth <= 1024;
  const sidePanelCanvasRenderKey = `${activeSideTab}-${feedbackModeActive ? 'feedback' : 'default'}-${isTabletViewport ? 'tablet' : 'base'}`;
  const [rallyApiData, setRallyApiData] = useState(null);
  // Below-video panel: paginated rally index
  const [rallyBVPage, setRallyBVPage] = useState(0);
  const [rallyApiLoading, setRallyApiLoading] = useState(false);

  // Memoized rally IDs specifically for side panel visuals to ensure responsiveness
  const sideRallyIds = useMemo(() => {
    // 1. Prioritize real-time video playback rally
    if (currentRallyId && currentRallyId !== 'none') {
      return currentRallyId;
    }
    // 2. Fallback to primary dropdown selection (NEVER use fetchedRallyIds range)
    if (firstRally && firstRally !== 'none') {
      return firstRally;
    }
    return 'none';
  }, [currentRallyId, firstRally]);
  const [rallyApiError, setRallyApiError] = useState(null);
  // Analytics view control
  // (removed duplicate analyticsView/winStats state)
  const [chatEndRef, setChatEndRef] = useState(null);
  // Video select dropdown state
  const [selectedVideo, setSelectedVideo] = useState('none');
  const SELECTED_VIDEO_KEY = 'PLAYGROUND_SELECTED_VIDEO';

  // Cache summary per video to prevent redundant fetches across UI actions
  const summaryCacheRef = useRef({}); // key: video value => { rallies, matchStats }
  const summaryInFlightRef = useRef(null); // { video, promise }

  /**
   * Helper function to retrieve rally data from localStorage
   * @param {string} matchId - The match ID (video filename without extension)
   * @returns {Object|null} Rally data from API or null if not found
   */
  const getRallyDataFromStorage = (matchId) => {
    if (!matchId) return null;
    try {
      const storageKey = `RALLY_DATA_${matchId}`;
      const storedData = localStorage.getItem(storageKey);
      if (storedData) {
        const rallyData = JSON.parse(storedData);
        console.log('[Playground] Retrieved rally data from localStorage for:', matchId);
        return rallyData;
      }
    } catch (err) {
      console.error('[Playground] Error retrieving rally data from localStorage:', err);
    }
    return null;
  };

  // Idempotent setter for selectedVideo to avoid re-triggers with same value
  const setSelectedVideoSafe = (value) => {
    setSelectedVideo((prev) => (prev === value ? prev : value));
  };

  // Normalize bot text to avoid entire responses rendering as code blocks
  const normalizeLLMText = (s) => {
    try {
      if (s == null) return s;
      let t = String(s).replace(/^\uFEFF/, '').trim();
      // If the whole message is fenced with triple backticks, unwrap it
      const fenceRe = /^```[a-zA-Z0-9_-]*\n([\s\S]*?)\n```\s*$/m;
      const m = t.match(fenceRe);
      if (m) t = m[1].trim();
      // If all non-empty lines share a 4-space indent (markdown code), remove it
      const lines = t.split('\n');
      const meaningful = lines.filter(l => l.trim().length > 0);
      const allFourSpace = meaningful.length > 0 && meaningful.every(l => l.startsWith('    '));
      if (allFourSpace) t = lines.map(l => (l.startsWith('    ') ? l.slice(4) : l)).join('\n');
      return t;
    } catch {
      return s;
    }
  };

  // Dynamic video options fetched from backend (Match Videos)
  const [dynamicVideoOptions, setDynamicVideoOptions] = useState([]);
  const allVideoOptions = useMemo(() => {
    // Merge samples and dynamic options instead of replacing
    const dynamicArray = Array.isArray(dynamicVideoOptions) ? dynamicVideoOptions : [];
    return [...VIDEO_OPTIONS, ...dynamicArray];
  }, [dynamicVideoOptions]);

  const prevTrackingParamsRef = useRef({ video: null, tracking: false, heatmap: false, occupancy: false });

  // Clear any cached post ID mappings that might be from wrong environment
  useEffect(() => {
    const currentEnv = getEnvironment();
    if (currentEnv !== 'production') {
      try {
        const map = readPostIdMap();
        const hasOldMappings = Object.keys(map).length > 0;
        if (hasOldMappings) {
          writePostIdMap({});
        }
      } catch { }
    }
  }, []);

  // Controls whether to automatically continue to the next rally when one ends.
  // Set to false to require explicit user action (Play) to continue playback.
  const AUTO_CHAIN_RALLIES = true;

  // Helper to convert internal server paths to S3 URLs
  const convertServerPathToS3 = (path) => {
    if (!path || typeof path !== 'string') return null;
    if (path.startsWith('http://') || path.startsWith('https://')) return path; // Already a URL

    // Handle s3:// URIs directly
    if (path.startsWith('s3://')) {
      const withoutPrefix = path.slice(5); // remove s3://
      const parts = withoutPrefix.split('/');
      if (parts.length > 1) {
        const bucket = parts[0];
        const key = parts.slice(1).join('/');
        // Force lowercase bucket name for S3 DNS compliance and use ap-south-1
        return `https://${bucket.toLowerCase()}.s3.ap-south-1.amazonaws.com/${key}`;
      }
    }

    // Example: /home/ubuntu/workspace/Production/Visist-ai-badminton/Outputs/...
    // Target: https://visist-ai-badminton.s3.amazonaws.com/Outputs/...
    const productionMarker = '/workspace/Production/';
    const index = path.indexOf(productionMarker);
    if (index !== -1) {
      const relative = path.substring(index + productionMarker.length);
      // split bucket and rest
      // relative: Visist-ai-badminton/Outputs/...
      const parts = relative.split('/');
      if (parts.length > 1) {
        const bucket = parts[0];
        const key = parts.slice(1).join('/');
        // Force lowercase bucket name for S3 DNS compliance, but keep key case-sensitive
        return `https://${bucket.toLowerCase()}.s3.amazonaws.com/${key}`;
      }
    }
    return null;
  };

  // Resolve video URL strictly from public folder or direct load data
  const getVideoUrlFor = (opt) => {
    const key = opt?.value || selectedVideo;

    // 0. Check for stitched video in summary cache OR direct API option (Priority)

    if (opt?.summaryVideoPath && typeof opt.summaryVideoPath === 'string') {
      const stitchedUrl = convertServerPathToS3(opt.summaryVideoPath);
      if (stitchedUrl) {
        return stitchedUrl;
      }
    }

    // B. Check for stitched video in summary cache (if loaded later)
    // Only applies if we have valid cached stats for this video
    if (key && summaryCacheRef?.current?.[key]) {
      const stats = summaryCacheRef.current[key].matchStats;
      console.log('[Playground] Checking cache for stitched video. Key:', key, 'Stats keys:', Object.keys(stats || {}));

      const rawPath = stats?.summary_video_file || stats?.Summary_video_file || stats?.stitched_video_path;
      if (rawPath) {
        const stitchedUrl = convertServerPathToS3(rawPath);
        if (stitchedUrl) {
          console.log('[Playground] Using stitched video from summary cache:', stitchedUrl);
          return stitchedUrl;
        } else {
          console.log('[Playground] Found raw stitched path in cache but conversion failed:', rawPath);
        }
      } else {
        console.log('[Playground] No stitched video path found in cached stats.');
      }
    }
    // In direct load mode, use the video URL from CreateFeedback
    // In direct load mode, use the video URL from CreateFeedback
    // In direct load mode, use the video URL from CreateFeedback
    if (directLoadMode && directVideoData) {
      // 2024-02-FIX: Check if directVideoData has a stitched video path that we should prioritize over the raw videoUrl
      // The `buildPathsFromKey` logic extracts this, but if we are here, we might have skipped it or opt was minimal.
      // So we do a quick check here similar to buildPathsFromKey.
      const dataField = directVideoData.rallyData || directVideoData;

      const stitchedPath = dataField.summary_video_file || dataField.Summary_video_file || dataField.stitched_video_path;
      if (stitchedPath) {
        const stitchedUrl = convertServerPathToS3(stitchedPath);
        if (stitchedUrl) {
          return stitchedUrl;
        }
      }


      // 2. Fallback to original video (check both videoUrl and video_file keys)
      // The API response often uses 'video_file' but the frontend sometimes expects 'videoUrl'
      const originalVideo = directVideoData.videoUrl || dataField.video_file || dataField.videoUrl;

      if (originalVideo) {
        // If original video is an S3 path or server path, try to convert it too just in case, or use as is
        // Usually original video is a full URL or relative path handled by player. 
        // If it's a server path, try conversion.
        if (originalVideo.startsWith('/') && !originalVideo.startsWith('/playground')) {
          const converted = convertServerPathToS3(originalVideo);
          if (converted) return converted;
        }
        return originalVideo;
      }

      // If we reach here in direct load mode, no video path was found
      return null;
    }

    // const key = opt?.value || selectedVideo; // Moved up
    // Only use public folder paths
    if (opt?.videoPath && typeof opt.videoPath === 'string') {
      // Fallback to default
      return opt.videoPath;
    }
    if (key) {
      const built = buildPathsFromKey(key);
      if (built?.videoPath) return built.videoPath;
    }
    return null;
  };

  // Persist a mapping of selectedVideo -> postId so refresh retains backend linkage
  const POST_ID_MAP_STORAGE_KEY = 'PLAYGROUND_POST_ID_MAP';
  const readPostIdMap = () => {
    try {
      const raw = localStorage.getItem(POST_ID_MAP_STORAGE_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch { return {}; }
  };
  const writePostIdMap = (obj) => {
    try { localStorage.setItem(POST_ID_MAP_STORAGE_KEY, JSON.stringify(obj || {})); } catch { }
  };

  const handleSelectVideo = (opt) => {
    if (trialMode && selectedVideo && selectedVideo !== 'none') {
      // Lock selection in trial mode after first pick
      setShowSelectVideoDialog(false);
      return;
    }
    try {
      const wants = opt && typeof opt.value === 'string' ? opt : null;
      // If a video is already selected and the user is choosing a different one, ask for confirmation first
      if (!suppressNextConfirm && wants && selectedVideo && selectedVideo !== 'none' && wants.value !== selectedVideo) {
        setPendingVideoOption(wants);
        setPendingOpenSelect(false);
        setShowConfirmChangeVideo(true);
        return;
      }
      if (wants && typeof wants.value === 'string') {
        try { ReactGA.event({ category: 'Playground', action: 'select_video', label: wants.value }); } catch { }
        setSelectedVideoSafe(wants.value);
        try { console.debug('[PG] handleSelectVideo -> value:', wants.value, 'postId:', wants?.postId); } catch { }
        // If option carries a backend postId, remember it for refresh
        const pid = (wants && typeof wants.postId === 'string' && wants.postId.trim().length > 0) ? wants.postId.trim() : null;
        if (pid) {
          const map = readPostIdMap();
          map[wants.value] = pid;
          writePostIdMap(map);
          try { console.debug('[PG] Stored PLAYGROUND_POST_ID_MAP:', map); } catch { }
        }
      }
    } finally {
      // Clear suppression after a selection attempt
      if (suppressNextConfirm) setSuppressNextConfirm(false);
      setShowSelectVideoDialog(false);
    }
  };

  // Unified entry point to request opening the Select Video dialog
  const requestOpenSelectDialog = () => {
    try { ReactGA.event({ category: 'Playground', action: 'select_video_open' }); } catch { }
    // In trial mode, user cannot change the video; if none yet, trigger random pick
    if (trialMode) {
      if (!selectedVideo || selectedVideo === 'none') {
        // Try restore first to avoid unnecessary randomization when coming back from signup
        try {
          const saved = localStorage.getItem('PLAYGROUND_SELECTED_VIDEO');
          if (saved && typeof saved === 'string' && saved !== 'none') {
            setSelectedVideo(saved);
            return;
          }
        } catch { }
        startTrialRandomPick();
      }
      return;
    }
    if (selectedVideo && selectedVideo !== 'none') {
      // Ask confirmation before changing the video
      setPendingVideoOption(null);
      setPendingOpenSelect(true);
      try { ReactGA.event({ category: 'Playground', action: 'select_video_confirm_change' }); } catch { }
      setShowConfirmChangeVideo(true);
      return;
    }
    setShowSelectVideoDialog(true);
    setSelectDialogOpenedAt(Date.now());
  };

  // Robustly open the Select dialog after confirm using an effect
  useEffect(() => {
    if (openSelectAfterConfirm) {
      // Bypass one confirmation cycle for immediate selection
      setSuppressNextConfirm(true);
      const t = setTimeout(() => {
        setShowSelectVideoDialog(true);
        setSelectDialogOpenedAt(Date.now());
        setOpenSelectAfterConfirm(false);
      }, 220);
      return () => clearTimeout(t);
    }
  }, [openSelectAfterConfirm]);

  // Helper: derive standard playground paths from a known key like 'video1'
  const buildPathsFromKey = (key, inputData = null) => {
    // 1. Helper function to convert S3 URI to HTTPS URL
    const convertS3ToHttps = (s3Uri) => {
      if (!s3Uri) return null;
      if (s3Uri.startsWith('http://') || s3Uri.startsWith('https://')) return s3Uri;
      if (s3Uri.startsWith('s3://')) {
        const withoutPrefix = s3Uri.replace('s3://', '');
        const [bucket, ...pathParts] = withoutPrefix.split('/');
        const path = pathParts.join('/');
        return `https://${bucket}.s3.amazonaws.com/${path}`;
      }
      return s3Uri;
    };

    // 2. Resolve data from directVideoData (manual URL input) or the passed inputData (Match Videos fetcher)
    const effectiveData = (key === 'direct_load_video' ? directVideoData?.rallyData : inputData) || null;

    if (effectiveData) {
      // Check data field or rallyData field (common in API responses)
      const dataField = effectiveData.rallyData || effectiveData.data || effectiveData;

      // Extract coordinates with many fallback variants (based on user provided json)
      const playerCoordsCsv = dataField.playerTrackingCsvPath || dataField.player_coords_csv || dataField.heatmapCsvPath || dataField.gt_merged_csv || dataField.analysis_csv;
      const summaryJson = dataField.summaryPath || dataField.summary_json || dataField.gt_merged_csv;
      // Extract Stitched Video path from API if available
      const summaryVideoFile = dataField.summary_video_file
        || dataField.Summary_video_file
        || dataField.stitched_video_path;

      const playerCoordsUrl = convertS3ToHttps(playerCoordsCsv);
      // UNIFY: Use coordinates URL for occupancy too, as requested
      const occupancyUrl = playerCoordsUrl;
      const summaryUrl = convertS3ToHttps(summaryJson);

      const safe = String(key || '').toLowerCase();
      const isSampleKey = safe.startsWith('video');

      // Only allow fallback to local /playground/ paths IF this is a genuine sample video
      const allowSampleFallback = isSampleKey && (key === 'direct_load_video' ? false : !inputData);

      // Derive a friendly label
      const label = dataField.title || dataField.videoTitle || dataField.videoName || dataField.name || dataField.match_id || (isSampleKey ? key.replace(/^video(\d+)/i, 'Video $1') : 'Match Video');

      const result = {
        value: key,
        label: label,
        videoPath: (summaryVideoFile ? convertS3ToHttps(summaryVideoFile) : null) || dataField.videoUrl || dataField.video_url || (allowSampleFallback ? `/playground/${safe}/video/${safe}.mp4` : null),
        basePath: dataField.basePath || (allowSampleFallback ? `/playground/${safe}/` : ''),
        coordinatesPath: playerCoordsUrl || (allowSampleFallback ? `/playground/${safe}/coordinates/${safe}_rally.csv` : null),
        heatmapPath: playerCoordsUrl || (allowSampleFallback ? `/playground/${safe}/coordinates/${safe}_rally.csv` : null),
        occupancyPath: occupancyUrl || (allowSampleFallback ? `/playground/${safe}/occupancy/${safe}_occupancy.csv` : null),
        summaryPath: summaryUrl || (allowSampleFallback ? `/playground/${safe}/summary/${safe}.json` : null),
        hitsPath: allowSampleFallback ? `/playground/${safe}/coordinates/${safe}_hits.csv` : null,
        // Pass through the stitched video path from API
        summaryVideoPath: summaryVideoFile || null,
        // Carry through the raw data for summary usage
        rallyData: dataField
      };

      return result;
    }

    // 3. Default: Fallback to sample paths ONLY for hardcoded sample videos (no inputData)
    // CRITICAL: If inputData was provided (match video), return empty/null paths instead of falling back
    if (inputData) {
      console.warn(`[Playground] No valid paths found for match video ${key}, returning null paths`);
      return {
        value: key,
        label: 'Set Video',
        videoPath: null,
        basePath: '',
        summaryPath: null,
        coordinatesPath: null,
        hitsPath: null,
        occupancyPath: null,
        rallyData: null
      };
    }

    // Only use sample fallback for genuine sample videos (video1, video3, etc.)
    if (!key) return {};
    const safe = String(key).toLowerCase();
    return {
      value: key,
      label: key.replace(/^video(\d+)/i, 'Video $1'),
      videoPath: `/playground/${safe}/video/${safe}.mp4`,
      basePath: `/playground/${safe}/`,
      summaryPath: `/playground/${safe}/summary/${safe}.json`,
      coordinatesPath: `/playground/${safe}/coordinates/${safe}_rally.csv`,
      hitsPath: `/playground/${safe}/coordinates/${safe}_hits.csv`,
      occupancyPath: `/playground/${safe}/occupancy/${safe}_occupancy.csv`,
    };
  };

  // Fetch videos tagged as "Match Videos" and map to dynamic options
  useEffect(() => {
    let cancelled = false;
    const fetchMatchVideos = async () => {
      try {
        const jwtToken = (() => { try { return localStorage.getItem('BQS_TOKEN'); } catch { return null; } })();
        if (!jwtToken || !POST_LIST_POST) return;
        const bodyVariants = [
          { condition: { state: ["review_requested"], searchTerm: "", tags: ["Match Videos"] }, sort: { updatedAt: -1 }, skip: 0, limit: 50 },
          { condition: { state: ["review_requested"], searchTerm: "", tag: ["Match Videos"] }, sort: { updatedAt: -1 }, skip: 0, limit: 50 },
        ];

        let results = [];
        for (const payload of bodyVariants) {
          try {
            const res = await fetch(new URL(POST_LIST_POST), {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${jwtToken}`,
                'Content-Type': 'application/json',
              },
              body: JSON.stringify(payload),
            });
            if (!res.ok) continue;
            const json = await res.json();
            if (json?.code === 200 && Array.isArray(json?.data?.searchResults)) {
              results = json.data.searchResults;
              break;
            }
          } catch { }
        }

        if (!Array.isArray(results) || results.length === 0) return;

        // Use environment-specific post ID instead of backend post ID
        const currentEnv = getEnvironment();
        const envPostIds = POST_IDS[currentEnv];

        const mapped = results.map((post) => {
          // CRITICAL: Do NOT use detectKey for match videos - they should NEVER map to sample keys like "video3"
          // Always use unique IDs to prevent fallback to sample video paths
          const uniqueId = post?._id || `match-${Date.now()}-${Math.random()}`;

          // Build paths using the unique ID as the key, passing the post data for S3 path resolution
          const base = buildPathsFromKey(uniqueId, post);
          const envPostId = envPostIds && envPostIds[uniqueId] ? envPostIds[uniqueId] : uniqueId;

          console.log(`[Playground] Mapped match video: ${post?.title || post?.name} -> ${uniqueId}`);

          // Use postId as the unique value so React detects changes between videos
          return {
            ...base,
            value: envPostId || uniqueId,
            postId: envPostId,
            key: uniqueId  // Use unique ID, not a sample key
          };
        }).filter(Boolean);

        if (!cancelled && mapped.length) {
          setDynamicVideoOptions(mapped);
        }
      } catch { }
    };
    fetchMatchVideos();
    return () => { cancelled = true; };
  }, []);

  // Restore selected video from localStorage on mount and when options load
  useEffect(() => {
    try {
      const saved = localStorage.getItem(SELECTED_VIDEO_KEY);
      if (saved) {
        const exists = allVideoOptions.some(opt => opt.value === saved);
        if (exists) setSelectedVideo(saved);
        else localStorage.removeItem(SELECTED_VIDEO_KEY);
      }
    } catch { }
  }, [allVideoOptions.length]);

  // Persist selection to localStorage whenever it changes.
  // Do NOT clear saved selection when selectedVideo is 'none' to preserve across back-navigation.
  useEffect(() => {
    try {
      if (selectedVideo && selectedVideo !== 'none') {
        localStorage.setItem(SELECTED_VIDEO_KEY, selectedVideo);
      }
    } catch { }
  }, [selectedVideo]);

  // Reset rally index when zoom modal opens/changes
  useEffect(() => {
    setZoomRallyIndex(0);
  }, [zoomedViz?.msgIndex, zoomedViz?.type]);

  // Compare/analytics UI and video timing
  const [compareClicked, setCompareClicked] = useState(false);
  const [videoSeekTime, setVideoSeekTime] = useState(0);
  const [showAnalytics, setShowAnalytics] = useState(false);
  // Auto-init right panel (heatmap + occupancy cumulative) after selecting a video
  const [autoInitRightPanel, setAutoInitRightPanel] = useState(false);

  // Voice-to-text (English only) — separate states for Chat and Coach
  const [isChatListening, setIsChatListening] = useState(false);
  const [isCoachListening, setIsCoachListening] = useState(false);
  const recognitionRef = useRef(null);
  // Dynamic target for SR output: chat input by default; can be switched to coach feedback
  const speechTargetSetterRef = useRef(null);
  // Buffer of already-committed speech-to-text to avoid duplicates
  const srBufferRef = useRef('');

  const ensureSpeechRecognition = () => {
    if (recognitionRef.current) return recognitionRef.current;
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return null;
    const rec = new SR();
    rec.lang = 'en-US';
    rec.interimResults = true;
    rec.continuous = false; // capture one utterance per click

    rec.onstart = () => { try { srBufferRef.current = ''; } catch { } };
    rec.onend = () => { try { setIsChatListening(false); setIsCoachListening(false); srBufferRef.current = ''; } catch { } };
    rec.onerror = () => { try { setIsChatListening(false); setIsCoachListening(false); } catch { } };
    rec.onresult = (e) => {
      try {
        // Only commit FINAL results; ignore interim to avoid repeated growth
        let finalText = '';
        for (let i = e.resultIndex; i < e.results.length; i++) {
          if (e.results[i].isFinal) {
            finalText += e.results[i][0].transcript;
          }
        }
        finalText = (finalText || '').trim();
        if (!finalText) return;

        // Append only the NEW portion beyond what we already committed
        const prevCommitted = srBufferRef.current || '';
        let toAppend = finalText;
        if (prevCommitted && finalText.startsWith(prevCommitted)) {
          toAppend = finalText.slice(prevCommitted.length);
        }
        toAppend = (toAppend || '').trim();
        if (toAppend) {
          const setTarget = speechTargetSetterRef.current || setInput;
          setTarget((prev) => (prev ? (prev + ' ' + toAppend) : toAppend));
        }
        // Update buffer to the latest full finalText so subsequent onresult callbacks don't duplicate
        srBufferRef.current = finalText;
      } catch { }
    };
    recognitionRef.current = rec;
    return rec;
  };

  // Generic voice typing toggle; route SR output to provided setter
  // which: 'chat' | 'coach'
  const handleToggleMicFor = (targetSetter, which) => {
    try {
      const rec = ensureSpeechRecognition();
      if (!rec) {
        try { console.warn('Voice to text is not supported in this browser.'); } catch { }
        return;
      }
      const currentlyOn = isChatListening || isCoachListening;
      const sameTarget = (which === 'chat' && isChatListening) || (which === 'coach' && isCoachListening);
      if (currentlyOn && sameTarget) {
        // Turn off the same mic
        try { rec.stop(); } catch { }
        speechTargetSetterRef.current = null;
        setIsChatListening(false); setIsCoachListening(false);
        return;
      }
      // Switch target: stop if running, then start with new target
      if (currentlyOn && !sameTarget) {
        try { rec.stop(); } catch { }
        setIsChatListening(false); setIsCoachListening(false);
        // Small delay to ensure cleanup before restart
        setTimeout(() => {
          try {
            speechTargetSetterRef.current = targetSetter || null;
            setIsChatListening(which === 'chat');
            setIsCoachListening(which === 'coach');
            rec.start();
          } catch { }
        }, 180);
        return;
      }
      // Start fresh
      speechTargetSetterRef.current = targetSetter || null;
      setIsChatListening(which === 'chat');
      setIsCoachListening(which === 'coach');
      try { rec.start(); } catch { }
    } catch { }
  };

  // Back-compat for existing chat mic button
  const handleToggleMic = () => handleToggleMicFor(setInput, 'chat');

  // Cleanup SR on unmount
  useEffect(() => {
    return () => {
      try { recognitionRef.current && recognitionRef.current.stop && recognitionRef.current.stop(); } catch { }
    };
  }, []);

  // Toggle checkboxes handler
  const handleCheckbox = (name) => {
    setCheckboxes(prev => {
      const next = { ...prev, [name]: !prev[name] };
      try { ReactGA.event({ category: 'Playground', action: 'toggle_overlay', label: name, value: next[name] ? 1 : 0 }); } catch { }
      // If user turns ON Range (cumulative), prepare the range analysis immediately
      if (name === 'cumulative' && !prev.cumulative && next.cumulative) {
        try {
          // Derive first..last rally IDs from JSON and CSV to cover full match
          const getId = (r) => r?.rally_id ?? r?.RallyID ?? r?.id ?? r?.number ?? r?.rallyNo;
          const jsonIds = Array.isArray(rallies) ? rallies.map(r => Number(getId(r))).filter(Number.isFinite) : [];
          const csvIds = Array.isArray(trackingData) ? trackingData.map(d => Number(d?.RallyID)).filter(Number.isFinite) : [];
          const all = Array.from(new Set([...(jsonIds || []), ...(csvIds || [])])).sort((a, b) => a - b);
          if (all.length >= 1) {
            const first = all[0];
            const last = all[all.length - 1];
            setCompareClicked(true);
            setFetchedRallyIds(all.length === 1 ? [first] : [first, last]);
            setFetchedCheckboxes(prevFetched => ({ ...prevFetched, cumulative: true }));
            setCurrentRallyId(null); // ensure no single-rally card is shown
            try { rightScrollRef?.current?.scrollTo?.({ top: 0, behavior: 'smooth' }); } catch { }
          }
        } catch { }
      }
      // If user turns OFF Range, ensure fetched cumulative flag is also turned off so rally cards can show
      if (name === 'cumulative' && prev.cumulative && !next.cumulative) {
        setFetchedCheckboxes(prevFetched => ({ ...prevFetched, cumulative: false }));
      }
      return next;
    });
  };

  // Handle rally selection change for first/second dropdowns
  const handleRallyChange = (dropdown, value) => {
    try {
      if (value === 'none') {
        if (dropdown === 'first') setFirstRally('none'); else setSecondRally('none');
        setCurrentRallyId(null);
        return;
      }
      const numValue = Number(value);
      if (!Number.isFinite(numValue)) return;

      if (dropdown === 'first') setFirstRally(numValue); else setSecondRally(numValue);
      setCurrentRallyId(numValue);
    } catch { }
  };

  // Player name helpers
  const getPlayerNames = () => {
    const p1 = matchStats?.players?.player_1?.name || 'Player A';
    const p2 = matchStats?.players?.player_2?.name || 'Player B';
    return { p1, p2 };
  };

  // Use extracted utility functions from analyticsUtils
  const computeWinPercentagesLocal = () => computeWinPercentages(rallies, matchStats);
  const computeWinPercentagePhasesLocal = () => computeWinPercentagePhases(rallies, matchStats);

  // Helper: map selected video to backend postId; return null if none
  const getPostIdForSelected = () => {
    try {
      // Priority 0: Use direct load postId when in direct load mode
      if (directLoadMode && directVideoData?.postId) {
        return directVideoData.postId;
      }

      const opt = allVideoOptions.find(o => o.value === selectedVideo);
      const currentEnv = getEnvironment();
      const envPostIds = POST_IDS[currentEnv];

      // Priority 1: Use environment-specific post ID if available
      if (envPostIds && envPostIds[selectedVideo]) {
        const envPostId = envPostIds[selectedVideo];
        // Also update the stored mapping to prevent future issues
        try {
          const map = readPostIdMap();
          map[selectedVideo] = envPostId;
          writePostIdMap(map);
        } catch { }
        return envPostId;
      }

      // Priority 2: Use option post ID
      if (opt && typeof opt.postId === 'string' && opt.postId.trim().length > 0) {
        const pid = opt.postId.trim();
        return pid;
      }

      // Priority 3: Fallback to stored mapping (e.g., if dynamic options load after refresh)
      const map = readPostIdMap();
      const pid = map && selectedVideo ? map[selectedVideo] : null;
      const resolved = (typeof pid === 'string' && pid.trim().length > 0) ? pid.trim() : null;
      return resolved;
    } catch { return null; }
  };


  // State for rallies
  const [rallies, setRallies] = useState([]);

  // State for match statistics
  const [matchStats, setMatchStats] = useState(null);

  // --- START SHARED DATA HELPERS (for zoom and horizontal analysis) ---
  const numBV = (v) => { const x = Number(v); return Number.isFinite(x) ? x : 0; };
  const getIdBV = (r) => r?.rally_id ?? r?.RallyID ?? r?.id ?? r?.number ?? r?.rallyNo;
  const pickNumBV = (obj, keys) => { for (const k of keys) { const v = obj?.[k]; if (v != null && v !== '' && Number.isFinite(Number(v))) return Number(v); } return null; };

  const scoreAOfBV = (r) => {
    const n = r?.score?.A ?? r?.score?.a ?? r?.score?.player_1 ?? r?.score?.p1;
    if (n != null && n !== '' && Number.isFinite(Number(n))) return Number(n);
    const pk = pickNumBV(r, ['score_A', 'scoreA', 'score_player_1', 'score_p1', 'p1_score', 'points_A', 'points_a']);
    if (pk !== null) return pk;
    const f = r?.final_score?.A ?? r?.final_score?.a ?? r?.final_score?.player_1 ?? r?.final_score?.p1;
    if (f != null && f !== '' && Number.isFinite(Number(f))) return Number(f);
    return null;
  };
  const scoreBOfBV = (r) => {
    const n = r?.score?.B ?? r?.score?.b ?? r?.score?.player_2 ?? r?.score?.p2;
    if (n != null && n !== '' && Number.isFinite(Number(n))) return Number(n);
    const pk = pickNumBV(r, ['score_B', 'scoreB', 'score_player_2', 'score_p2', 'p2_score', 'points_B', 'points_b']);
    if (pk !== null) return pk;
    const f = r?.final_score?.B ?? r?.final_score?.b ?? r?.final_score?.player_2 ?? r?.final_score?.p2;
    if (f != null && f !== '' && Number.isFinite(Number(f))) return Number(f);
    return null;
  };
  const durOfBV = (r) => {
    const d = pickNumBV(r, ['duration_s', 'duration', 'time_s']);
    if (d) return d;
    const s = r?.stitched_time?.stitched_start_s;
    const e = r?.stitched_time?.stitched_end_s;
    return (String(e) && String(s)) ? (numBV(e) - numBV(s)) : null;
  };
  const distAOfBV = (r) => pickNumBV(r, ['dist_A_m', 'dist_a_m', 'distance_A_m', 'distance_a_m', 'distance_player_1_m', 'distance_p1_m']) ?? numBV(r?.player_distances?.dist_a_m);
  const distBOfBV = (r) => pickNumBV(r, ['dist_B_m', 'dist_b_m', 'distance_B_m', 'distance_b_m', 'distance_player_2_m', 'distance_p2_m']) ?? numBV(r?.player_distances?.dist_b_m);
  const hitsAOfBV = (r) => numBV(r?.hits_count?.A ?? r?.hits_count?.a ?? r?.shots?.A ?? r?.shots?.a ?? r?.shots_player_1 ?? r?.hits_player_1 ?? r?.strokes_p1);
  const hitsBOfBV = (r) => numBV(r?.hits_count?.B ?? r?.hits_count?.b ?? r?.shots?.B ?? r?.shots?.b ?? r?.shots_player_2 ?? r?.hits_player_2 ?? r?.strokes_p2);

  const getCumulativeScoreUntilBV = (rid) => {
    const target = Number(rid);
    if (isNaN(target)) return null;
    let sA = 0, sB = 0;
    const rallyData = directLoadMode && directVideoData?.rallyData ? directVideoData.rallyData : matchStats;
    const arr = rallyData?.rally_analysis || rallyData?.rallies || (Array.isArray(rallies) ? rallies : []);
    if (!Array.isArray(arr) || arr.length === 0) return null;
    const sorted = [...arr].sort((a, b) => Number(getIdBV(a)) - Number(getIdBV(b)));
    for (const r of sorted) {
      const id = Number(getIdBV(r));
      if (!isNaN(id) && id <= target) {
        const w = String(r?.winner || r?.Winner || r?.rally_winner || r?.victory || '').toUpperCase();
        if (w === 'A' || w === 'PLAYER_1' || w === 'P1') sA++;
        else if (w === 'B' || w === 'PLAYER_2' || w === 'P2') sB++;
      }
    }
    return { sA, sB };
  };

  const getRallyBreakdownDescription = (rid) => {
    const rallyData = directLoadMode && directVideoData?.rallyData ? directVideoData.rallyData : matchStats;
    const arr = rallyData?.rally_analysis || rallyData?.rallies || (Array.isArray(rallies) ? rallies : []);
    const r = arr.find(it => String(getIdBV(it)) === String(rid));
    if (!r) return null;

    const { p1, p2 } = getPlayerNames();
    const w = String(r?.winner || r?.winner_player || r?.rally_winner || '').toUpperCase();
    const winnerName = w === 'A' ? p1 : (w === 'B' ? p2 : '-');

    const dur = durOfBV(r);
    const durStr = Number.isFinite(dur) ? `${dur.toFixed(2)}s` : '-';

    let sA = scoreAOfBV(r);
    let sB = scoreBOfBV(r);
    if (sA === null || sB === null) {
      const cumulative = getCumulativeScoreUntilBV(rid);
      if (cumulative) {
        sA = cumulative.sA;
        sB = cumulative.sB;
      }
    }
    const scoreStr = (sA !== null && sB !== null) ? `${sA}-${sB}` : '-';
    return `Winner: ${winnerName} | Duration: ${durStr} | Score: ${scoreStr}`;
  };
  // --- END SHARED DATA HELPERS ---

  // Load player tracking data when video or relevant checkboxes change
  useEffect(() => {
    // Dependency guard: only run if relevant params changed
    const currentParams = {
      video: selectedVideo,
      tracking: fetchedCheckboxes.tracking,
      heatmap: fetchedCheckboxes.heatmap,
      occupancy: fetchedCheckboxes.occupancy
    };
    if (
      prevTrackingParamsRef.current.video === currentParams.video &&
      prevTrackingParamsRef.current.tracking === currentParams.tracking &&
      prevTrackingParamsRef.current.heatmap === currentParams.heatmap &&
      prevTrackingParamsRef.current.occupancy === currentParams.occupancy
    ) {
      return;
    }
    prevTrackingParamsRef.current = currentParams;

    // CRITICAL: Clear state IMMEDIATELY and SYNCHRONOUSLY to prevent stale data
    setTrackingData(null);
    setHitsData(null);
    setTrackingLoading(false);
    setTrackingError(null);

    let isMounted = true;

    const loadTrackingData = async () => {
      if (!selectedVideo || selectedVideo === 'none') {
        return;
      }

      // For direct_load_video, build paths directly from rally data
      let videoOption;
      if (selectedVideo === 'direct_load_video' && directLoadMode && directVideoData?.rallyData) {
        videoOption = buildPathsFromKey('direct_load_video');
      } else {
        videoOption = allVideoOptions.find(opt => opt.value === selectedVideo);
      }

      if (!videoOption?.coordinatesPath) {
        return;
      }

      setTrackingLoading(true);
      setTrackingError(null);

      try {
        // Prefer canonical exported CSVs: <video>_rally.csv and <video>_hits.csv; fallback for rally only
        const canonicalCoords = videoOption.coordinatesPath; // /playground/<video>/coordinates/<video>_rally.csv
        const legacyCoords = canonicalCoords?.replace(/[^/]+$/, 'rally_coords.csv');

        const canonicalHits = videoOption.hitsPath; // /playground/<video>/coordinates/<video>_hits.csv

        let coords = null;
        try {
          coords = await loadCSV(canonicalCoords);
        } catch (e1) {
          console.warn('Canonical CSV missing, trying legacy path...', canonicalCoords, e1?.message);
          coords = await loadCSV(legacyCoords);
        }

        let hits = null;
        if (canonicalHits) {
          try {
            hits = await loadCSV(canonicalHits);
          } catch (e2) {
            console.warn('Hits CSV missing:', canonicalHits, e2?.message);
            hits = [];
          }
        }

        if (isMounted) {
          setTrackingData(coords);
          setHitsData(hits);
        }
      } catch (err) {
        console.error('Error loading tracking data:', err);
        if (isMounted) {
          setTrackingError('Failed to load player tracking data');
        }
      } finally {
        if (isMounted) {
          setTrackingLoading(false);
        }
      }
    };

    loadTrackingData();

    return () => {
      isMounted = false;
    };
  }, [selectedVideo, directLoadMode, directVideoData, allVideoOptions]);

  // Load video data when selectedVideo changes
  useEffect(() => {
    let isMounted = true;

    const fetchVideoData = async () => {
      if (!selectedVideo || selectedVideo === 'none') {
        if (isMounted) {
          setRallies([]);
          setMatchStats(null);
          setFirstRally('none');
          setSecondRally('none');
          setCurrentRallyId(null);
        }
        return;
      }

      console.log('=== Loading data for video:', selectedVideo, '===');

      // Get video option - either from allVideoOptions or build from directVideoData
      let videoOption;
      if (directLoadMode && selectedVideo === 'direct_load_video' && directVideoData) {
        // Build video option from direct video data
        videoOption = buildPathsFromKey('direct_load_video');
        console.log('[Playground] Built video option from direct data:', videoOption);
      } else {
        videoOption = allVideoOptions.find(opt => opt.value === selectedVideo);
      }

      if (!videoOption) {
        console.error('Selected video not found in options');
        return;
      }

      // Pre-clear any previous video's summary-dependent state to avoid showing stale counts
      if (isMounted) {
        setRallies([]);
        setMatchStats(null);
        setFirstRally('none');
        setSecondRally('none');
        setCurrentRallyId(null);
        setCompareClicked(false);
      }

      try {
        let summaryData;

        // 1. Resolve summary source: Use embedded rallyData if available, otherwise fetch summary JSON
        // 1. Hybrid Data Loading: Start with embedded rallyData if available
        let baseSummary = videoOption.rallyData || (directLoadMode && selectedVideo === 'direct_load_video' ? directVideoData?.rallyData : null);

        if (baseSummary) {
          console.log('[Playground] Using embedded rallyData for summary base:', baseSummary);
          const ralliesArray = baseSummary.rally_analysis || baseSummary.rallies || baseSummary.data?.rally_analysis || [];
          summaryData = {
            ...baseSummary,
            rallies: ralliesArray,
            match_stats: baseSummary.match_stats || baseSummary.data || baseSummary
          };
        }

        // 2. Conditional Fetch: If we have no rallies yet, attempt to fetch from summaryPath
        if ((!summaryData?.rallies?.length) && videoOption.summaryPath) {
          const summaryUrl = videoOption.summaryPath;
          console.log('[Playground] Rallies missing or empty, fetching from summaryPath:', summaryUrl);

          if (summaryUrl.toLowerCase().endsWith('.csv')) {
            // Support CSV summaries
            const csvRallies = await loadCSV(summaryUrl);
            summaryData = {
              ...(summaryData || {}),
              rallies: csvRallies,
              match_stats: summaryData?.match_stats || {}
            };
          } else {
            // Regular mode: fetch summary from JSON file
            const summaryResponse = await fetch(summaryUrl, { cache: 'no-store' });
            if (!summaryResponse.ok) {
              // Only throw if we had NO summary data at all. If we had embedded stats, just log warning.
              if (!summaryData) throw new Error(`Failed to load summary: ${summaryResponse.status}`);
              console.warn(`[Playground] Optional summary fetch failed: ${summaryResponse.status}`);
            } else {
              const responseText = await summaryResponse.text();
              try {
                const fetchedJson = JSON.parse(responseText);
                summaryData = {
                  ...fetchedJson,
                  // Merge with existing embedded match_stats if any
                  ...summaryData,
                  rallies: fetchedJson.rallies || fetchedJson.rally_analysis || summaryData?.rallies || [],
                  match_stats: fetchedJson.match_stats || fetchedJson.data || fetchedJson || summaryData?.match_stats || {}
                };
              } catch (parseError) {
                console.error('Error parsing JSON:', parseError);
                if (!summaryData) throw new Error('Invalid JSON in summary file');
              }
            }
          }
        }

        if (!summaryData) {
          console.warn('[Playground] No summary data source found for video');
          summaryData = { rallies: [], match_stats: {} };
        }

        // Common processing for both rally data and summary JSON
        const cleanData = (obj) => {
          if (obj !== null && typeof obj === 'object') {
            Object.keys(obj).forEach(key => {
              if (obj[key] === 'NaN') {
                obj[key] = null;
              } else if (typeof obj[key] === 'object') {
                cleanData(obj[key]);
              } else if (typeof obj[key] === 'number' && isNaN(obj[key])) {
                obj[key] = null;
              }
            });
          }
          return obj;
        };

        summaryData = cleanData(summaryData);

        // Update cache
        const cacheEntry = {
          rallies: (summaryData?.rallies?.length ? ([...summaryData.rallies]
            .map((r, idx) => {
              const ridRaw = (r?.rally_id ?? r?.RallyID ?? r?.id ?? r?.number ?? r?.rallyNo ?? (idx + 1));
              const rally_id = Number.isFinite(Number(ridRaw)) ? Number(ridRaw) : (idx + 1);
              return { ...r, rally_id };
            })
            .sort((a, b) => {
              const as = Number(a?.stitched_time?.stitched_start_s);
              const bs = Number(b?.stitched_time?.stitched_start_s);
              const aF = Number.isFinite(as);
              const bF = Number.isFinite(bs);
              if (aF && bF) return as - bs;
              if (aF && !bF) return -1;
              if (!aF && bF) return 1;
              const aid = Number(a?.rally_id);
              const bid = Number(b?.rally_id);
              if (Number.isFinite(aid) && Number.isFinite(bid)) return aid - bid;
              return 0;
            })) : []),
          matchStats: (summaryData.match_stats || summaryData) || null,
        };
        summaryCacheRef.current[selectedVideo] = cacheEntry;

        if (isMounted) {
          // 2. Process rallies from summary data
          // Safe optional chaining to prevent TypeError: Cannot read properties of undefined (reading 'rallies')
          if (summaryData?.rallies?.length > 0) {
            // Normalize rally IDs and sort by stitched start time when available
            const normalized = [...summaryData.rallies]
              .map((r, idx) => {
                const ridRaw = (r?.rally_id ?? r?.RallyID ?? r?.id ?? r?.number ?? r?.rallyNo ?? (idx + 1));
                const rally_id = Number.isFinite(Number(ridRaw)) ? Number(ridRaw) : (idx + 1);
                return { ...r, rally_id };
              })
              .sort((a, b) => {
                const as = Number(a?.stitched_time?.stitched_start_s);
                const bs = Number(b?.stitched_time?.stitched_start_s);
                const aF = Number.isFinite(as);
                const bF = Number.isFinite(bs);
                if (aF && bF) return as - bs;
                if (aF && !bF) return -1;
                if (!aF && bF) return 1;
                // Secondary stable ordering by rally_id when stitched times are non-finite
                const aid = Number(a?.rally_id);
                const bid = Number(b?.rally_id);
                if (Number.isFinite(aid) && Number.isFinite(bid)) return aid - bid;
                return 0;
              });

            // Debug info to verify last rally presence
            try {
              const last = normalized[normalized.length - 1];
              const lastId = last?.rally_id ?? last?.RallyID ?? last?.id ?? last?.number ?? last?.rallyNo;
              console.debug('[Playground] Rallies loaded:', normalized.length, 'Last rally ID:', lastId);
            } catch { }

            setRallies(normalized);
            if (normalized.length > 0) {
              const firstId = normalized[0].rally_id;
              setFirstRally(firstId);
              setSecondRally('none');
            } else {
              setFirstRally('none');
              setSecondRally('none');
            }
          } else {
            setRallies([]);
            setFirstRally('none');
            setSecondRally('none');
          }

          // 3. Set match statistics
          setMatchStats(summaryData.match_stats || summaryData);
        }

      } catch (err) {
        console.error('Error loading video data:', err);
        if (isMounted) {
          setRallies([]);
          setMatchStats(null);
          setFirstRally('none');
          setSecondRally('none');
          setCurrentRallyId(null);
        }
      }
    };

    fetchVideoData();

    return () => {
      isMounted = false;
    };
  }, [selectedVideo, directLoadMode, directVideoData, allVideoOptions]); // Only depend on selectedVideo and direct load states


  // Reset analytics view when video changes
  useEffect(() => {
    setAnalyticsView(null);
  }, [selectedVideo]);

  // Load persisted coach feedbacks for the selected video (CreateFeedback parity)
  useEffect(() => {
    let isMounted = true;
    const run = async () => {
      try {
        try { console.debug('[PG] FeedbackLoad effect start. selectedVideo:', selectedVideo, 'optionsLen:', allVideoOptions.length); } catch { }
        if (!selectedVideo || selectedVideo === 'none') {
          if (isMounted) dispatch(setStandaloneFeedbacks([]));
          return;
        }
        const jwtToken = (() => { try { return localStorage.getItem('BQS_TOKEN'); } catch { return null; } })();
        if (!jwtToken) { try { console.warn('[PG] No JWT token; skipping getCoachFeedback'); } catch { } if (isMounted) dispatch(setStandaloneFeedbacks([])); return; }
        const postId = getPostIdForSelected();
        try { console.debug('[PG] FeedbackLoad -> resolved postId:', postId); } catch { }
        if (!postId) { if (isMounted) dispatch(setStandaloneFeedbacks([])); return; }
        const resp = await getCoachFeedback(jwtToken, postId);
        try { console.debug('[PG] GET getCoachFeedback OK. postId:', postId, 'resp keys:', Object.keys(resp || {})); } catch { }
        const apiFeedbacks = resp?.data?.feedbacks || resp?.data || [];
        const arr = Array.isArray(apiFeedbacks) ? apiFeedbacks : [];
        const mapped = arr.map((a, idx) => ({
          _id: a?._id || `${Date.now()}-${idx}`,
          timeRange: a?.timeRange || { startTime: Number(a?.timeSec) || 0, endTime: Number(a?.timeSec) || 0 },
          markerDetails: { coordinates: a?.markerDetails?.coordinates || { x1: 0, y1: 0, x2: 0, y2: 0 } },
          comments: Array.isArray(a?.comments) ? a.comments.map(c => ({ body: c?.body, metaData: c?.metaData })) : [],
        }));
        if (isMounted) dispatch(setStandaloneFeedbacks(mapped));
      } catch (e) {
        const status = e?.response?.status || e?.status;
        try { console.warn('[PG] GET getCoachFeedback failed:', status, e?.message || e); } catch { }
        if (status === 404) { if (isMounted) dispatch(setStandaloneFeedbacks([])); return; }
        if (isMounted) dispatch(setStandaloneFeedbacks([]));
      }
    };
    run();
    return () => { isMounted = false; };
  }, [dispatch, selectedVideo, allVideoOptions.length]);

  // Real-time synchronization of currentRallyId with video playback time
  // This ensures that side panel visuals and bottom rally highlights follow the video precisely.
  useEffect(() => {
    const el = videoRef?.current;
    if (!el || !Array.isArray(rallies) || rallies.length === 0) return undefined;

    const onSyncTime = () => {
      try {
        const t = el.currentTime;
        // Find the rally that contains the current timestamp
        const matchingRally = rallies.find(r => {
          const s = Number(r?.stitched_time?.stitched_start_s ?? r?.start_time_s ?? r?.start_s ?? r?.start) || 0;
          const e = Number(r?.stitched_time?.stitched_end_s ?? r?.end_time_s ?? r?.end_s ?? r?.end) || 0;
          return t >= s && t <= e;
        });

        if (matchingRally) {
          const rid = (matchingRally?.rally_id ?? matchingRally?.RallyID ?? matchingRally?.id ?? matchingRally?.number ?? matchingRally?.rallyNo);
          if (rid != null && String(rid) !== String(currentRallyId)) {
            setCurrentRallyId(rid);
          }
        } else {
          // If NOT in a rally, set to null so sideRallyIds falls back to firstRally
          if (currentRallyId !== null) setCurrentRallyId(null);
        }
      } catch { }
    };

    el.addEventListener('timeupdate', onSyncTime);
    return () => {
      try { el.removeEventListener('timeupdate', onSyncTime); } catch { }
    };
  }, [videoRef, rallies, currentRallyId]);

  // When rallies are loaded for a selected video, optionally auto-show visuals (do NOT auto-enable occupancy)
  useEffect(() => {
    try {
      if (!selectedVideo || selectedVideo === 'none') return;
      if (autoInitRightPanel) return;
      if (!Array.isArray(rallies) || rallies.length === 0) return;

      const getId = (r) => r?.rally_id ?? r?.RallyID ?? r?.id ?? r?.number ?? r?.rallyNo;
      const ids = rallies.map(r => Number(getId(r))).filter(Number.isFinite);
      if (!ids.length) return;
      let first = Math.min(...ids);
      let last = Math.max(...ids);
      // If trackingData is already available, merge its RallyIDs as well
      try {
        if (Array.isArray(trackingData) && trackingData.length) {
          const csvIds = trackingData.map(d => Number(d?.RallyID)).filter(Number.isFinite);
          if (csvIds.length) {
            first = Math.min(first, ...csvIds);
            last = Math.max(last, ...csvIds);
          }
        }
      } catch { }
      // Debug: confirm auto-init selection
      try { console.log('[AutoInit] Selected video:', selectedVideo, 'first/last rally IDs:', first, last); } catch { }

      setFetchedRallyIds([first, last]);
      // Update BOTH checkbox states for Range to work
      setCheckboxes(prev => ({
        ...prev,
        heatmap: false, // Don't auto-check individual checkboxes
        occupancy: false,
        tracking: false,
        cumulative: true, // Check the Range checkbox
      }));
      setFetchedCheckboxes(prev => ({
        ...prev,
        heatmap: false,
        occupancy: false,
        tracking: false,
        cumulative: true,
      }));
      setCompareClicked(true);
      setAutoInitRightPanel(true);
    } catch { }
  }, [selectedVideo, rallies, autoInitRightPanel, trackingData]);

  // If trackingData arrives later, adjust first/last rally IDs to include CSV range as well (only if user hasn't changed selection)
  useEffect(() => {
    try {
      if (!selectedVideo || selectedVideo === 'none') return;
      if (!Array.isArray(fetchedRallyIds) || fetchedRallyIds.length < 2) return;
      if (!Array.isArray(trackingData) || trackingData.length === 0) return;
      if (hasUserAction) return; // don't override user's manual selection

      const [curFirst, curLast] = fetchedRallyIds;
      const csvIds = trackingData.map(d => Number(d?.RallyID)).filter(Number.isFinite);
      if (!csvIds.length) return;
      const newFirst = Math.min(curFirst, ...csvIds);
      const newLast = Math.max(curLast, ...csvIds);
      if (newFirst !== curFirst || newLast !== curLast) {
        try { console.log('[AutoInit CSV merge] Adjusted first/last rally IDs:', newFirst, newLast); } catch { }
        setFetchedRallyIds([newFirst, newLast]);
      }
    } catch { }
  }, [selectedVideo, trackingData]);

  // Ensure default playback speed is 0.5x when the video is (re)loaded
  useEffect(() => {
    const el = videoRef?.current;
    const player = playerRef?.current;
    const setNativeRate = () => { try { if (el) el.playbackRate = 0.5; } catch { } };
    // Native video element
    if (el) {
      setNativeRate();
      const onMeta = () => setNativeRate();
      el.addEventListener('loadedmetadata', onMeta);
      // Cleanup
      return () => {
        try { el.removeEventListener('loadedmetadata', onMeta); } catch { }
      };
    }
    // video.js player fallback
    if (player && typeof player.playbackRate === 'function') {
      try { player.playbackRate(0.5); } catch { }
    }
    return undefined;
  }, [selectedVideo]);

  // Hard reset dependent UI/state when a new video is selected
  useEffect(() => {
    // Reset toggles and UI bits
    setCheckboxes({ occupancy: true, tracking: false, cumulative: true, heatmap: true, coach: false });
    setFetchedCheckboxes({ occupancy: true, tracking: false, cumulative: true, heatmap: true, coach: false });
    setFetchedRallyIds([]);
    setFirstRally('none');
    setSecondRally('none');
    setCurrentRallyId(null);
    setHasUserAction(false);
    setLegendVisible(false);
    setStrokeWidth(4);
    setChat([]);
    setInput('');
    setShowAnalytics(false);
    setRallyApiData(null);
    setRallyApiError(null);
    setRallyApiLoading(false);
    setRallyBVPage(0);
    setTrackingData(null);
    setHitsData(null);
    setTrackingLoading(false);
    setTrackingError(null);
    setVideoSeekTime(0);
    setAutoInitRightPanel(false);

    // Reset video position if possible
    try {
      const el = videoRef?.current;
      if (el && typeof el.currentTime === 'number') {
        try { el.pause(); } catch { }
        el.currentTime = 0;
        try { el.playbackRate = 0.5; } catch { }
      }
      const player = playerRef?.current;
      if (player && typeof player.currentTime === 'function') {
        try { player.pause(); } catch { }
        player.currentTime(0);
        try { if (typeof player.playbackRate === 'function') player.playbackRate(0.5); } catch { }
      }
    } catch { }

    // Scroll right response panel to top
    try {
      if (rightScrollRef?.current) {
        rightScrollRef.current.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      }
    } catch { }
  }, [selectedVideo]);

  // Auto-fetch cumulative stats for the full match when a video loads initially
  useEffect(() => {
    if (selectedVideo && selectedVideo !== 'none' && !compareClicked && trackingData != null) {
      const csvIds = Array.isArray(trackingData) ? trackingData.map(d => Number(d?.RallyID)).filter(Number.isFinite) : [];
      const jsonIds = Array.isArray(rallies) ? rallies.map(r => Number(r?.rally_id ?? r?.RallyID ?? r?.id ?? r?.number ?? r?.rallyNo)).filter(Number.isFinite) : [];
      const all = Array.from(new Set([...jsonIds, ...csvIds])).sort((a, b) => a - b);

      if (all.length > 0) {
        const first = all[0];
        const last = all[all.length - 1]; // Select full range (1 to end)
        setFirstRally(first);
        setSecondRally('none');
        setFetchedRallyIds([first, last]);
        setCompareClicked(true);
      }
    }
  }, [selectedVideo, trackingData, rallies, compareClicked]);

  // Fetch rally details from API when a rally is selected or detected
  useEffect(() => {
    let isMounted = true;
    const fetchRallyDetails = async () => {
      setRallyApiError(null);
      setRallyApiData(null);
      if (!selectedVideo || selectedVideo === 'none' || !currentRallyId) return;
      try {
        setRallyApiLoading(true);
        const userId = (() => {
          try {
            const authed = localStorage.getItem('BQS__ID') || localStorage.getItem('userId');
            if (authed) return String(authed);
            const key = sessionStorage.getItem('PG_TRIAL_PHONE_KEY') || '';
            const cc = localStorage.getItem('PRESIGN_CC') || '';
            const pn = localStorage.getItem('PRESIGN_PHONE') || '';
            const composed = (cc && pn) ? `${cc}${pn}` : '';
            return String(key || composed || 'explore-user');
          } catch { return 'explore-user'; }
        })();
        // Resolve actual video name for API
        const videoNameForApi = (directLoadMode && (
          (directVideoData?.videoTitle && directVideoData.videoTitle !== 'Unknown Title' ? directVideoData.videoTitle : null) ||
          directVideoData?.matchMeta?.matchMeta?.video_name ||
          directVideoData?.matchMeta?.video_name ||
          directVideoData?.rallyData?.video_name
        )) || selectedVideo;

        const requestData = {
          video_name: videoNameForApi,
          rally_ids: [Number(currentRallyId)].filter(Number.isFinite),
          query: `details for rally ${currentRallyId}`,
          include_raw_data: true,
          user_id: userId,
        };
        const resp = await analyzeRally(requestData);
        if (!isMounted) return;
        // Try common shapes to extract a single rally object
        const apiRally = (
          resp?.search_results?.rallies?.[0] ||
          resp?.search_results?.rally_summaries?.[0] ||
          resp?.rallies?.[0] ||
          resp?.rally ||
          null
        );
        setRallyApiData(apiRally || null);
      } catch (err) {
        if (isMounted) setRallyApiError(err?.message || 'Failed to fetch rally data');
      } finally {
        if (isMounted) setRallyApiLoading(false);
      }
    };

    // Debounce the API call by 300ms to prevent spamming during scrubbing or rapid playback changes
    const timeoutId = setTimeout(() => {
      fetchRallyDetails();
    }, 300);

    return () => {
      isMounted = false;
      clearTimeout(timeoutId);
    };
  }, [selectedVideo, currentRallyId]);

  // Seek the video to the start of a given rally (uses stitched_time.stitched_start_s when available)
  const seekToRally = (rallyId) => {
    try {
      if (!rallyId) return;
      try { ReactGA.event({ category: 'Playground', action: 'seek_to_rally', label: String(rallyId) }); } catch { }
      const selectedRally = rallies?.find(rally => {
        const rid = rally?.rally_id ?? rally?.RallyID ?? rally?.id ?? rally?.number ?? rally?.rallyNo;
        return String(rid) === String(rallyId);
      });
      if (!selectedRally) return;
      const rawStartTime = selectedRally?.stitched_time?.stitched_start_s ?? 0;
      const startTime = Math.max(0, (Number(rawStartTime) || 0) - 1);

      // Try native video element first
      const el = videoRef?.current;
      const player = playerRef?.current;
      if (player && typeof player.currentTime === 'function') {
        const doSeek = () => {
          try {
            try { player.pause(); } catch { }
            player.currentTime(startTime);
            try { player.play(); } catch { }
          } catch (e) {
            console.error('video.js seek failed:', e);
          }
        };

        if (typeof player.readyState === 'function' ? player.readyState() >= 1 : true) doSeek();
        else if (typeof player.one === 'function') player.one('loadedmetadata', doSeek);
        else setTimeout(doSeek, 100);
      }
    } catch (error) {
      console.error('seekToRally error:', error);
    }
  };

  // Seek to rally start, briefly play, then pause (for dual selection UX)
  const seekAndPauseAtRallyStart = (rallyId) => {
    try {
      if (!rallyId) return;
      const selectedRally = Array.isArray(rallies)
        ? rallies.find(rally => {
          const rid = rally?.rally_id ?? rally?.RallyID ?? rally?.id ?? rally?.number ?? rally?.rallyNo;
          return String(rid) === String(rallyId);
        })
        : null;
      if (!selectedRally) return;

      const rawStartTime = (
        selectedRally?.stitched_time?.stitched_start_s ??
        selectedRally?.start_time_s ??
        selectedRally?.start ??
        selectedRally?.start_s
      );
      const startTime = Number(rawStartTime) || 0;

      // video.js player path
      const player = playerRef?.current;
      if (player && typeof player.currentTime === 'function') {
        const doSeek = () => {
          try {
            try { player.pause(); } catch { }
            player.currentTime(startTime);
            try { player.play(); } catch { }
            setTimeout(() => { try { player.pause(); } catch { } }, 80);
          } catch (e) {
            console.error('video.js seek/play/pause failed:', e);
          }
        };
        if (typeof player.readyState === 'function' ? player.readyState() >= 1 : true) doSeek();
        else if (typeof player.one === 'function') player.one('loadedmetadata', doSeek);
        else setTimeout(doSeek, 100);
      }
    } catch (error) {
      console.error('seekAndPauseAtRallyStart error:', error);
    }
  };

  // Compute start/end times for a rally ID
  const getRallyTimes = (id) => {
    // Prefer canonical JSON (`matchStats.rallies`) for accurate start/end, then fallback to `rallies`
    const jsonArr = Array.isArray(matchStats?.rallies) ? matchStats.rallies : [];
    const stateArr = Array.isArray(rallies) ? rallies : [];
    const arr = jsonArr.length ? jsonArr : stateArr;
    const idOf = (x) => x?.rally_id ?? x?.RallyID ?? x?.id ?? x?.number ?? x?.rallyNo;
    let r = Array.isArray(arr)
      ? arr.find(x => String(idOf(x)) === String(id))
      : null;
    if (!r && Array.isArray(arr)) {
      // Fallback: if id looks like a number and within range, pick nth rally (1-indexed common in UIs)
      const n = Number(id);
      if (Number.isInteger(n) && n >= 1 && n <= arr.length) {
        r = arr[n - 1];
        try { console.debug('[Rally] getRallyTimes: using index fallback', { id, chosenIndex: n - 1, chosenId: idOf(r) }); } catch { }
      }
    }
    if (!r) {
      try { console.debug('[Rally] getRallyTimes: rally not found in JSON/state', { id }); } catch { }
      return null;
    }
    const start = (
      r?.stitched_time?.stitched_start_s ?? r?.stitched_start_s ??
      r?.start_time_s ?? r?.start ?? r?.start_s ?? r?.rally_start_s ?? 0
    );
    let end = (
      r?.stitched_time?.stitched_end_s ?? r?.stitched_end_s ??
      r?.end_time_s ?? r?.end ?? r?.end_s ?? r?.rally_end_s
    );
    if (end == null) {
      const dur = r?.duration ?? r?.duration_s ?? r?.rally_duration_s ?? r?.duration_secs ?? r?.duration_sec;
      if (dur != null) end = Number(start) + Number(dur);
    }
    // Final fallback: if still missing or equal, assume a short 3s clip
    if (end == null || Number(end) === Number(start)) {
      try { console.debug('[Rally] getRallyTimes: missing or zero duration, applying fallback', { id, start, end }); } catch { }
      end = Number(start) + 3;
    }
    const s = Math.max(0, Number(start) || 0);
    const e = Math.max(s, Number(end) || s);
    return { start: s, end: e };
  };

  // Clear any previous end guard listeners
  const clearEndGuard = () => {
    try { if (typeof endGuardCleanupRef.current === 'function') endGuardCleanupRef.current(); } catch { }
    endGuardCleanupRef.current = null;
  };

  // Play a single rally segment and pause exactly at its end
  // opts.forceChain: when true, onEnded callback will be invoked regardless of AUTO_CHAIN_RALLIES
  const playRallySegment = (rallyId, onEnded, opts = { forceChain: false }) => {
    const times = getRallyTimes(rallyId);
    if (!times) return;
    const { start, end } = times;
    const epsilon = 0.02; // precise end guard
    let endedFired = false;

    clearEndGuard();

    // Choose active player path: prefer video.js if present; else native <video>
    const player = playerRef?.current;
    const useVideoJs = player && typeof player.currentTime === 'function';
    if (useVideoJs) {
      const doStart = () => {
        try {
          try { player.pause(); } catch { }
          player.currentTime(start);
          const startPlay = () => { try { player.play(); } catch { } };
          try {
            if (typeof player.one === 'function') player.one('seeked', startPlay);
            else setTimeout(startPlay, 50);
          } catch { setTimeout(startPlay, 50); }
          try { console.debug('[Rally] play start (video.js):', { rallyId, start, end }); } catch { }
          try { setCurrentRallyId(rallyId); } catch { }
        } catch { }
      };
      if (typeof player.readyState === 'function' ? player.readyState() >= 1 : true) doStart();
      else if (typeof player.one === 'function') player.one('loadedmetadata', doStart);
      else setTimeout(doStart, 100);

      const onTime = () => {
        try {
          const t = Number(typeof player.currentTime === 'function' ? player.currentTime() : NaN);
          if (Number.isFinite(t) && t >= end - epsilon) {
            try { player.pause(); } catch { }
            try { if (typeof player.currentTime === 'function') player.currentTime(end); } catch { }
            clearEndGuard();
            try { console.debug('[Rally] end detected by timeupdate (video.js):', { rallyId, t, end }); } catch { }
            if (!endedFired) {
              endedFired = true;
              if (((opts && opts.forceChain) || AUTO_CHAIN_RALLIES) && typeof onEnded === 'function') onEnded();
            }
          }
        } catch { }
      };
      const onEndedEvt = () => {
        try { player.pause(); } catch { }
        try { if (typeof player.currentTime === 'function') player.currentTime(end); } catch { }
        clearEndGuard();
        try { console.debug('[Rally] end detected by ended event (video.js):', { rallyId, end }); } catch { }
        if (!endedFired) {
          endedFired = true;
          if (((opts && opts.forceChain) || AUTO_CHAIN_RALLIES) && typeof onEnded === 'function') onEnded();
        }
      };
      // rAF fallback for precise boundary
      let rafId = null;
      const tick = () => {
        try {
          const t = Number(typeof player.currentTime === 'function' ? player.currentTime() : NaN);
          if (Number.isFinite(t) && t >= end - epsilon) {
            try { player.pause(); } catch { }
            try { if (typeof player.currentTime === 'function') player.currentTime(end); } catch { }
            clearEndGuard();
            if (!endedFired) {
              endedFired = true;
              if (((opts && opts.forceChain) || AUTO_CHAIN_RALLIES) && typeof onEnded === 'function') onEnded();
            }
            return;
          }
        } catch { }
        rafId = window.requestAnimationFrame(tick);
      };
      try { player.on('timeupdate', onTime); } catch { }
      try { player.on('ended', onEndedEvt); } catch { }
      rafId = window.requestAnimationFrame(tick);
      endGuardCleanupRef.current = () => {
        try { player.off('timeupdate', onTime); } catch { }
        try { player.off('ended', onEndedEvt); } catch { }
        try { if (rafId) window.cancelAnimationFrame(rafId); } catch { }
      };
      return;
    }

    const el = videoRef?.current;
    if (el && typeof el.currentTime === 'number') {
      const doStart = () => {
        try {
          try { el.pause(); } catch { }
          el.currentTime = start;
          const startPlay = () => { try { el.play().catch(() => { }); } catch { } };
          try { el.addEventListener('seeked', startPlay, { once: true }); } catch { setTimeout(startPlay, 50); }
          try { console.debug('[Rally] play start (native):', { rallyId, start, end }); } catch { }
          try { setCurrentRallyId(rallyId); } catch { }
        } catch { }
      };
      if (el.readyState >= 1) doStart(); else el.addEventListener('loadedmetadata', doStart, { once: true });

      const onTime = () => {
        try {
          if (el.currentTime >= end - epsilon) {
            try { el.pause(); } catch { }
            try { el.currentTime = end; } catch { }
            clearEndGuard();
            try { console.debug('[Rally] end detected by timeupdate (native):', { rallyId, t: el.currentTime, end }); } catch { }
            if (!endedFired) {
              endedFired = true;
              if (((opts && opts.forceChain) || AUTO_CHAIN_RALLIES) && typeof onEnded === 'function') onEnded();
            }
          }
        } catch { }
      };
      const onEndedEvt = () => {
        try { el.pause(); } catch { }
        try { el.currentTime = end; } catch { }
        clearEndGuard();
        try { console.debug('[Rally] end detected by ended event (native):', { rallyId, end }); } catch { }
        if (!endedFired) {
          endedFired = true;
          if (((opts && opts.forceChain) || AUTO_CHAIN_RALLIES) && typeof onEnded === 'function') onEnded();
        }
      };
      // rAF fallback for precise boundary
      let rafId = null;
      const tick = () => {
        try {
          if (el.currentTime >= end - epsilon) {
            try { el.pause(); } catch { }
            try { el.currentTime = end; } catch { }
            clearEndGuard();
            if (!endedFired) {
              endedFired = true;
              if (((opts && opts.forceChain) || AUTO_CHAIN_RALLIES) && typeof onEnded === 'function') onEnded();
            }
            return;
          }
        } catch { }
        rafId = window.requestAnimationFrame(tick);
      };
      el.addEventListener('timeupdate', onTime);
      el.addEventListener('ended', onEndedEvt);
      rafId = window.requestAnimationFrame(tick);
      endGuardCleanupRef.current = () => {
        try { el.removeEventListener('timeupdate', onTime); } catch { }
        try { el.removeEventListener('ended', onEndedEvt); } catch { }
        try { if (rafId) window.cancelAnimationFrame(rafId); } catch { }
      };
    }
  };

  // Play both selected rallies back-to-back and pause after the second ends
  const playSelectedRalliesSequentially = () => {
    const isValidId = (id) => !(id === null || id === undefined || id === 'none' || id === 'undefined' || String(id).trim() === '');
    if (!isValidId(firstRally) || !isValidId(secondRally)) return;
    const t1 = getRallyTimes(firstRally);
    const t2 = getRallyTimes(secondRally);
    if (!t1 || !t2) {
      try { console.debug('[Rally] Cannot start sequence; missing times', { firstRally, t1, secondRally, t2, jsonCount: Array.isArray(matchStats?.rallies) ? matchStats.rallies.length : 0 }); } catch { }
      return;
    }
    try { console.debug('[Rally] Starting sequence with times', { firstRally, t1, secondRally, t2 }); } catch { }
    chainingRef.current = true;
    playRallySegment(firstRally, () => {
      // slight delay to allow player state to settle
      setTimeout(() => {
        // If both selections are the same, just stop after the first
        if (String(firstRally) === String(secondRally)) {
          chainingRef.current = false;
          return;
        }
        playRallySegment(secondRally, () => {
          chainingRef.current = false;
        }, { forceChain: true });
      }, 120);
    }, { forceChain: true });
  };

  // Trigger when both rally selections are valid
  useEffect(() => {
    const isValidId = (id) => !(id === null || id === undefined || id === 'none' || id === 'undefined' || String(id).trim() === '');
    const jsonReady = Array.isArray(matchStats?.rallies) && matchStats.rallies.length > 0;
    if (AUTO_CHAIN_RALLIES && isValidId(firstRally) && isValidId(secondRally) && jsonReady) {
      if (chainingRef.current) return; // avoid re-entry while a sequence is running
      playSelectedRalliesSequentially();
    } else {
      clearEndGuard();
    }
    return () => {
      clearEndGuard();
      chainingRef.current = false;
    };
  }, [firstRally, secondRally, matchStats?.rallies]);

  // Play an array of rally IDs sequentially, then stop.
  // Options:
  //   - sortAscending: when true (default), sort numerically ascending; when false, preserve provided order
  // After finishing all, the video remains paused at the last rally end; user must click Play to continue.
  const playRalliesInOrder = (ids, opts = { sortAscending: false }) => {
    try {
      const toNum = (v) => {
        const m = String(v).match(/\d+/);
        return m ? Number(m[0]) : Number(v);
      };
      const raw = Array.isArray(ids) ? ids : [];
      const valid = raw
        .map(x => (x == null ? null : String(x).trim()))
        .filter(s => s && s !== 'none');
      // Do not expand to ranges; only dedupe while preserving order if sortAscending is false
      const dedupPreserve = (arr) => {
        const seen = new Set();
        const out = [];
        for (const v of arr) { if (!seen.has(v)) { seen.add(v); out.push(v); } }
        return out;
      };
      const cleaned = opts?.sortAscending === false ? dedupPreserve(valid) : Array.from(new Set(valid)).sort((a, b) => toNum(a) - toNum(b));
      if (!cleaned.length) return;
      chainingRef.current = true;
      let i = 0;
      const next = () => {
        try {
          if (i >= cleaned.length) {
            try { console.debug('[Rally] Sequence complete.'); } catch { }
            chainingRef.current = false;
            return;
          }
          const id = cleaned[i++];
          const times = getRallyTimes(id);
          if (!times || !Number.isFinite(times.start) || !Number.isFinite(times.end)) {
            try { console.warn('[Rally] Skipping ID with missing times:', id); } catch { }
            // Skip invalid and continue
            setTimeout(next, 0);
            return;
          }
          const durMs = (() => {
            const d = Math.max(0, Number(times.end) - Number(times.start));
            // Lower bound 3.5s, upper bound 30s, add a small buffer
            return Math.min(30000, Math.max(3500, (d + 1.0) * 1000)) + 400;
          })();
          let endedLocal = false;
          let watchdogId = null;
          const clearWatchdog = () => { if (watchdogId) { try { clearTimeout(watchdogId); } catch { } watchdogId = null; } };
          const onEnd = () => {
            if (endedLocal) return;
            endedLocal = true;
            clearWatchdog();
            // Chain to next
            next();
          };
          // Watchdog in case ended/timeupdate/rAF does not fire (browser quirk)
          watchdogId = setTimeout(() => {
            if (endedLocal) return;
            try { console.warn('[Rally] Watchdog advancing sequence due to timeout for ID:', id, times); } catch { }
            try { clearEndGuard(); } catch { }
            onEnd();
          }, durMs);
          try { console.debug('[Rally] Playing ID in sequence:', id, times); } catch { }
          playRallySegment(id, onEnd, { forceChain: true });
        } catch (e) {
          try { console.warn('[Rally] Error during sequence step; advancing.', e); } catch { }
          setTimeout(next, 0);
        }
      };
      next();
    } catch { }
  };

  // Convenience: play exactly the provided rally IDs in the given order (no sorting)
  const playExactRallies = (ids) => playRalliesInOrder(ids, { sortAscending: false });

  // Expose sequence player for integration (e.g., from chat or dev console)
  useEffect(() => {
    try {
      window.Playground_playRalliesInOrder = playRalliesInOrder;
      window.Playground_playExactRallies = playExactRallies;
    } catch { }
    return () => {
      try {
        delete window.Playground_playRalliesInOrder;
        delete window.Playground_playExactRallies;
      } catch { }
    };
  }, []);

  // Find a rally object by an arbitrary ID across known key variants
  const findRallyDetails = (id) => {
    if (id === null || id === undefined || id === 'none' || id === 'undefined' || String(id).trim() === '') return null;
    // Prefer normalized rallies from state; fallback to matchStats.rallies
    const arr = (Array.isArray(rallies) && rallies.length)
      ? rallies
      : (Array.isArray(matchStats?.rallies) ? matchStats.rallies : []);
    if (!Array.isArray(arr)) return null;
    const target = String(id);
    return arr.find(r => {
      const rid = r?.rally_id ?? r?.RallyID ?? r?.id ?? r?.number ?? r?.rallyNo;
      return String(rid) === target;
    }) || null;
  };


  // Extract rally IDs from a freeform text (markdown or plain), e.g.,
  // "Rally 12", "shortest rally: 5", "best rally is 8"
  const extractRallyIdsFromText = (text) => {
    if (!text) return [];
    try {
      const s = String(text);
      const found = new Set();
      // Pattern 1: explicit "Rally <n>"
      for (const m of s.matchAll(/rally\s*(\d+)/ig)) {
        const n = Number(m[1]);
        if (Number.isFinite(n)) found.add(n);
      }
      // Pattern 2: keyword-based near-number (shortest/longest/best/worst) within small window
      for (const m of s.matchAll(/\b(shortest|longest|best|worst)[^\d\n]{0,20}(\d{1,4})\b/ig)) {
        const n = Number(m[2]);
        if (Number.isFinite(n)) found.add(n);
      }
      // Pattern 3: explicit link forms like (rally://12) already render clickable, but capture number as well
      for (const m of s.matchAll(/rally:\/\/(\d{1,4})/ig)) {
        const n = Number(m[1]);
        if (Number.isFinite(n)) found.add(n);
      }
      // Validate against available rally IDs from summary to avoid picking non-rally numbers (like percentages)
      const validIds = new Set(
        (Array.isArray(rallies) ? rallies : []).map(r => Number(r?.rally_id ?? r?.RallyID ?? r?.id ?? r?.number ?? r?.rallyNo)).filter(Number.isFinite)
      );
      return Array.from(found).filter(n => validIds.has(n));
    } catch {
      return [];
    }
  };

  // Handle chat question
  const handleQuestion = async (q) => {
    // Enforce trial question limit
    if (trialMode && trialChatLocked) {
      setTrialExpired(true);
      return;
    }
    if (trialMode) {
      const next = (trialAskCount || 0) + 1;
      setTrialAskCount(next);
      try { sessionStorage.setItem('PG_TRIAL_ASK_COUNT', String(next)); } catch { }
      if (next > TRIAL_ASK_LIMIT) {
        setTrialChatLocked(true);
        setTrialExpired(true);
        return;
      }
      if (next === TRIAL_ASK_LIMIT) {
        // Allow this question, but lock further ones after response
        setTimeout(() => setTrialChatLocked(true), 0);
      }
    }
    // Check if video is selected
    if (selectedVideo === 'none') {
      setChat((prev) => [...prev, { sender: 'user', text: q }]);
      setChat((prev) => [...prev, { sender: 'bot', text: 'Please select a video first before asking questions.' }]);
      setInput('');
      return;
    }

    setHasUserAction(true);
    setChat((prev) => [...prev, { sender: 'user', text: q }]);
    // Detect intents from quick tags or free-form queries
    const isTrackingIntent = /\b(player\s*tracking|tracking data|movement\s*(?:trail|path)|trajectory)\b/i.test(q);
    const isHeatmapIntent = /\b(heat\s*map|heatmap|player\s*moving\s*heat\s*map|occupancy\s*heat\s*map)\b/i.test(q);
    // Intent: user asks why a player lost the match -> show only overall win percentage
    const isWhyLoseIntent = /\bwhy\b[^?]*\bplayer\b[^?]*\b(lose|lost)\b[^?]*\bmatch\b/i.test(q);
    // Treat both 'cumulative' and common misspelling 'cummulative', and generic 'range' mentions as Range intent
    const isCumulativeStatsIntent = /\b(cu+mulative(?:\s*stats?)?|show\s*me\s*cu+mulative\s*stats?)\b/i.test(q);
    const isRangeIntent = /\b(range|rally\s*range|range\s*analysis)\b/i.test(q);
    const isRangeOrCumulative = isCumulativeStatsIntent || isRangeIntent;
    const isCompareIntent = /\b(compare|vs|versus)\b/i.test(q);
    // If the question itself includes phase percentages, parse and display immediately
    let hadFromUser = false;
    try {
      const fromUser = parsePhaseWinPercentages(null, q);
      if (fromUser && fromUser.length) {
        hadFromUser = true;
        setAnalyticsView('win_percentage');
        setCurrentRallyId(null);
        setWinPhaseStats(null);
        setLlmPhaseStats(fromUser);
        // If user provided phase-wise percentages, prefer showing phase chart unless pinned overall-only
        if (!isWhyLoseIntent) setForceOverallWinOnly(false);
      }
      // Also parse overall A/B win % directly from the question, if present
      const overallFromUser = parseOverallWinPercentages(null, q);
      if (overallFromUser && (Number.isFinite(overallFromUser.pctA) || Number.isFinite(overallFromUser.pctB))) {
        setLlmOverallWin(overallFromUser);
      }
    } catch { }
    setInput('');
    setChatLoading(true);

    // Continue to request/handle response
    try {
      // Prepare request data
      const userId = (() => {
        try {
          const authed = localStorage.getItem('BQS__ID') || localStorage.getItem('userId');
          if (authed) return String(authed);
          const key = sessionStorage.getItem('PG_TRIAL_PHONE_KEY') || '';
          const cc = localStorage.getItem('PRESIGN_CC') || '';
          const pn = localStorage.getItem('PRESIGN_PHONE') || '';
          const composed = (cc && pn) ? `${cc}${pn}` : '';
          return String(key || composed || 'explore-user');
        } catch { return 'explore-user'; }
      })();
      // Resolve actual video name for API
      const videoNameForApi = (directLoadMode && (
        (directVideoData?.videoTitle && directVideoData.videoTitle !== 'Unknown Title' ? directVideoData.videoTitle : null) ||
        directVideoData?.matchMeta?.matchMeta?.video_name ||
        directVideoData?.matchMeta?.video_name ||
        directVideoData?.rallyData?.video_name
      )) || selectedVideo;

      const requestData = {
        video_name: videoNameForApi,
        query: q,
        rally_ids: [],
        llm_model: 'gemini',
        include_raw_data: true,
        user_id: userId,
      };

      // Call BFF API
      const response = await analyzeRally(requestData);
      // Prefer markdown from backend when available (check common nesting shapes)
      // We treat `_summary` or `summary_markdown/summary_md/markdown` as markdown for CHAT display.
      // The plain `summary` (without underscore) remains available for right-panel logic elsewhere.
      const getSummaryMarkdown = (res) => {
        try {
          if (!res) return null;
          // Direct keys
          const direct = res._summary || res.summary_markdown || res.summary_md || res.markdown || res.summary;
          if (typeof direct === 'string' && direct.trim()) return direct;
          // Common nests
          const nests = [res.data, res.result, res.search_results, res.answer, res.response, res.payload];
          for (const n of nests) {
            if (!n) continue;
            if (typeof n === 'string' && n.trim()) return n; // sometimes API returns a raw string
            if (typeof n === 'object') {
              const s = n._summary || n.summary_markdown || n.summary_md || n.markdown || n.summary;
              if (typeof s === 'string' && s.trim()) return s;
            }
            if (Array.isArray(n)) {
              for (const item of n) {
                if (!item) continue;
                const s2 = item._summary || item.summary_markdown || item.summary_md || item.markdown || item.summary || item.text_md;
                if (typeof s2 === 'string' && s2.trim()) return s2;
              }
            }
          }
          // If root is array
          if (Array.isArray(res)) {
            for (const it of res) {
              const s3 = it?._summary || it?.summary_markdown || it?.summary_md || it?.markdown || it?.summary;
              if (typeof s3 === 'string' && s3.trim()) return s3;
            }
          }
          return null;
        } catch { return null; }
      };
      const summaryMarkdown = getSummaryMarkdown(response);
      try { console.debug('[Chat] summary_markdown picked:', !!summaryMarkdown); } catch { }
      let responseText = summaryMarkdown ?? (
        response?.Response_text ||
        response?.response_text ||
        response?.search_results?.Response_text ||
        response?.search_results?.response_text ||
        'No specific information found for your query.'
      );
      const hasMeaningfulText = typeof responseText === 'string' && responseText.trim().length > 0 && responseText !== 'No specific information found for your query.';
      if (response && hasMeaningfulText) {
        // Detect if user asked for win percentage
        const lowerQ = `${q} ${responseText}`.toLowerCase();
        const askedWinPct = /win\s*percentage|win\s*%|winning\s*percentage|win\s*rate/.test(lowerQ) || isWhyLoseIntent;
        if (askedWinPct) {
          setAnalyticsView('win_percentage');
          setCurrentRallyId(null); // suppress rally card if showing win %
          const stats = computeWinPercentages();
          setWinStats(stats);
          // For "why did player lose" intent, force overall-only view
          if (isWhyLoseIntent) setForceOverallWinOnly(true);
          // Try to use API-provided phased win percentages
          const apiPhases = parsePhaseWinPercentages(response, responseText);
          if (apiPhases && Array.isArray(apiPhases) && apiPhases.length) {
            setWinPhaseStats(apiPhases);
            setLlmPhaseStats(apiPhases);
            // Phase data present: allow phase view only if not pinned overall-only
            if (!isWhyLoseIntent) setForceOverallWinOnly(false);
          } else {
            setWinPhaseStats(null);
            // Try parsing directly from the response text
            const fromText = parsePhaseWinPercentages(null, responseText);
            if (fromText && fromText.length) {
              setLlmPhaseStats(fromText);
              if (!isWhyLoseIntent) setForceOverallWinOnly(false);
            } else if (!hadFromUser) {
              // Only clear if we didn't already parse from user's message
              setLlmPhaseStats(null);
            }
            // Also parse any overall two-number win % if present
            const apiOverall = parseOverallWinPercentages(response, responseText) || parseOverallWinPercentages(null, responseText);
            if (apiOverall && (Number.isFinite(apiOverall.pctA) || Number.isFinite(apiOverall.pctB))) {
              setLlmOverallWin(apiOverall);
            }
          }
          // Normalize percent formatting in LLM answer for exact match with UI
          responseText = normalizePercentsInText(responseText);
        } else {
          // Even if the user didn't explicitly ask, if the LLM text contains phase percentages, show the card with those exact values
          const fromText = parsePhaseWinPercentages(null, responseText);
          if (fromText && fromText.length) {
            setAnalyticsView('win_percentage');
            setCurrentRallyId(null);
            setWinPhaseStats(null);
            setLlmPhaseStats(fromText);
            if (!isWhyLoseIntent) setForceOverallWinOnly(false);
            // Also parse any overall two-number win % if present
            const apiOverall = parseOverallWinPercentages(response, responseText) || parseOverallWinPercentages(null, responseText);
            if (apiOverall && (Number.isFinite(apiOverall.pctA) || Number.isFinite(apiOverall.pctB))) {
              setLlmOverallWin(apiOverall);
            }
            responseText = normalizePercentsInText(responseText);
          } else if (!hadFromUser) {
            // Only reset if we didn't already capture from user's question
            setAnalyticsView(null);
            setWinPhaseStats(null);
            setLlmPhaseStats(null);
            setLlmOverallWin(null);
          }
        }
        // Transform any 'Rally N' or 'Rally A-B' mentions into clickable links in markdown
        const toLinkedMarkdown = (md) => {
          if (!md || typeof md !== 'string') return md;
          let placeholders = [];
          // Range forms: "Rally 5-9", "Rally 5 – 9", "Rally 5 to 9"
          const rangeRegexes = [
            /Rally\s+(\d+)\s*[–—-]\s*(\d+)/gi,
            /Rally\s+(\d+)\s+to\s+(\d+)/gi
          ];
          let tmp = md;
          rangeRegexes.forEach((re) => {
            tmp = tmp.replace(re, (_, a, b) => {
              const token = `__RANGE_TOKEN_${placeholders.length}__`;
              placeholders.push(`[Rally ${a}-${b}](range://${a}-${b})`);
              return token;
            });
          });
          // Single rally: "Rally 12"
          tmp = tmp.replace(/Rally\s+(\d+)/gi, (_, n) => `[Rally ${n}](rally://${n})`);
          // Restore range tokens
          placeholders.forEach((link, idx) => {
            tmp = tmp.replace(`__RANGE_TOKEN_${idx}__`, link);
          });
          return tmp;
        };

        let linkedMarkdown = toLinkedMarkdown(responseText);

        // Capture vizData if applicable
        let foundIds = [];
        const apiIds = Array.isArray(response.search_results?.rally_ids)
          ? response.search_results.rally_ids.map(Number).filter(Number.isFinite)
          : [];
        const textIds = extractRallyIdsFromText(responseText);
        foundIds = Array.from(new Set([...apiIds, ...textIds]));

        // If we found rally IDs, append a structured breakdown list ONLY IF the LLM didn't already provide it.
        // This ensures consistent fallback formatting (Winner, Duration, Score) for all rally mentions.
        if (foundIds.length > 0) {
          const breakdownLines = [];
          for (const rid of foundIds) {
            const desc = getRallyBreakdownDescription(rid);
            if (desc) {
              const line = `- [Rally ${rid}](rally://${rid}) — ${desc}`;
              // Avoid duplicates if the LLM already included something similar
              const simpleLine = `Rally ${rid}`;
              if (!linkedMarkdown.includes(simpleLine) || !linkedMarkdown.includes('Score:')) {
                breakdownLines.push(line);
              }
            }
          }
          if (breakdownLines.length > 0) {
            linkedMarkdown += `\n\n### Rally Breakdowns\n${breakdownLines.join('\n')}`;
          }
        }

        // Prepare vizData for this specific message
        const vizData = {
          rallyIds: [],
          checkboxes: { ...checkboxes },
          trackingPoints: null,
          hits: null,
          videoKey: selectedVideo,
          videoOption: allVideoOptions.find(o => o.value === selectedVideo),
          occupancyUrl: null,
        };

        if (vizData.videoOption) {
          vizData.occupancyUrl = vizData.videoOption.occupancyPath || `${vizData.videoOption.basePath}occupancy/${vizData.videoOption.value}_occupancy.csv`;
        }

        if (foundIds.length) {
          if (!askedWinPct) {
            const normalized = foundIds.map(Number).filter(Number.isFinite);
            const unique = Array.from(new Set(normalized));

            if (isRangeOrCumulative) {
              const getId = (r) => r?.rally_id ?? r?.RallyID ?? r?.id ?? r?.number ?? r?.rallyNo;
              const jsonIds = Array.isArray(rallies) ? rallies.map(r => Number(getId(r))).filter(Number.isFinite) : [];
              const csvIds = Array.isArray(trackingData) ? trackingData.map(d => Number(d?.RallyID)).filter(Number.isFinite) : [];
              const all = Array.from(new Set([...jsonIds, ...csvIds])).sort((a, b) => a - b);
              if (all.length >= 2) {
                const first = all[0];
                const last = all[all.length - 1];
                vizData.rallyIds = [first, last];
                vizData.checkboxes = { tracking: true, heatmap: true, occupancy: true, cumulative: true };
              } else if (unique.length >= 2) {
                vizData.rallyIds = [unique[0], unique[unique.length - 1]];
                vizData.checkboxes = { tracking: true, heatmap: true, occupancy: true, cumulative: true };
              } else {
                vizData.rallyIds = unique.slice(0, 1);
                vizData.checkboxes = { tracking: true, heatmap: true, occupancy: true, cumulative: false };
              }
            } else if (isHeatmapIntent) {
              if (unique.length >= 1) {
                vizData.rallyIds = unique.length > 1 ? [unique[0], unique[unique.length - 1]] : [unique[0]];
                vizData.checkboxes = { heatmap: true, tracking: false, occupancy: false, cumulative: isRangeOrCumulative && unique.length > 1 };
              }
            } else if (isTrackingIntent) {
              if (unique.length >= 1) {
                vizData.rallyIds = unique.length > 1 ? [unique[0], unique[unique.length - 1]] : [unique[0]];
                vizData.checkboxes = { tracking: true, heatmap: false, occupancy: false, cumulative: isRangeOrCumulative && unique.length > 1 };
              }
            } else {
              vizData.rallyIds = unique.slice(0, 4);
              vizData.checkboxes = { ...checkboxes };
            }
          }
        } else if (isTrackingIntent || isHeatmapIntent || isRangeOrCumulative) {
          const getId = (r) => r?.rally_id ?? r?.RallyID ?? r?.id ?? r?.number ?? r?.rallyNo;
          const jsonIds = Array.isArray(rallies) ? rallies.map(r => Number(getId(r))).filter(Number.isFinite) : [];
          const csvIds = Array.isArray(trackingData) ? trackingData.map(d => Number(d?.RallyID)).filter(Number.isFinite) : [];
          const all = Array.from(new Set([...jsonIds, ...csvIds])).sort((a, b) => a - b);
          if (all.length >= 1) {
            const first = all[0];
            const last = all[all.length - 1];
            vizData.rallyIds = all.length > 1 ? [first, last] : [first];
            vizData.checkboxes = {
              tracking: isTrackingIntent || isRangeOrCumulative,
              heatmap: isHeatmapIntent || isRangeOrCumulative,
              occupancy: isRangeOrCumulative,
              cumulative: isRangeOrCumulative && all.length > 1
            };
          }
        }

        setChat(prev => [...prev, {
          sender: 'bot',
          text: linkedMarkdown,
          vizData: vizData.rallyIds.length > 0 ? vizData : null
        }]);
      } else {
        setChat(prev => [...prev, { sender: 'bot', text: 'Sorry, I could not process your request. Please try again.' }]);
      }
    } catch (error) {
      console.error('BFF API Error:', error);
      setChat((prev) => [...prev, { sender: 'bot', text: 'Sorry, there was an error processing your request. Please try again.' }]);
    } finally {
      setChatLoading(false);
    }
  };

  // Chat input helpers
  const handleInput = (e) => setInput(e.target.value);
  // Handle clicks on special markdown links like rally://12 or range://5-18
  const handleMdLinkClick = (href) => {
    try {
      try { ReactGA.event({ category: 'Playground', action: 'md_link_click', label: href }); } catch { }
      if (!href || typeof href !== 'string') return;
      if (href.startsWith('rally://')) {
        const rid = Number(href.replace('rally://', ''));
        if (rid) {
          handleQuestion(`Show tracking for Rally ${rid}`);
        }
        return;
      }
      if (href.startsWith('range://')) {
        const range = href.replace('range://', '');
        handleQuestion(`Show cumulative analysis for Rally ${range}`);
        return;
      }
    } catch { }
  };
  const handleSubmit = (e) => {
    e.preventDefault();
    // Gate: trial users from Explore who are not signed in
    try {
      const authed = isAuthed;
      if (!authed && trialMode) {
        try { ReactGA.event({ category: 'Playground', action: 'signup_gate_open' }); } catch { }
        setShowSignupGate(true);
        return;
      }
    } catch { }
    if (!input.trim()) return;
    try { ReactGA.event({ category: 'Playground', action: 'ask_question', label: input.trim() }); } catch { }
    handleQuestion(input.trim());
  };

  const handleCompare = () => {
    try { ReactGA.event({ category: 'Playground', action: 'compare_click' }); } catch { }
    // Do nothing if no rally is selected in either dropdown and no currentRallyId is detected
    const isValidId = (id) => !(id === null || id === undefined || id === 'none' || id === 'undefined' || String(id).trim() === '');
    const dropdownCount = [firstRally, secondRally].filter(isValidId).length;
    const hasAny = dropdownCount > 0 || isValidId(currentRallyId);
    if (!hasAny) return;

    // If cumulative is enabled but only one rally is selected, show dialog and abort
    if (checkboxes.cumulative && dropdownCount === 1) {
      setShowCumDialog(true);
      return;
    }

    setCompareClicked(true);
    setHasUserAction(true);
    // Capture current selections as the fetched set so further dropdown changes won't auto-update UI
    const ids = [firstRally, secondRally].filter(isValidId);
    if (ids.length === 0 && isValidId(currentRallyId)) ids.push(currentRallyId);
    setFetchedRallyIds(ids.slice(0, 2));
    // Capture current checkbox choices for visuals
    setFetchedCheckboxes(prev => ({ ...prev, ...checkboxes }));
    // Build query per selection rules
    let qText = '';
    if (ids.length === 1) {
      qText = `Describe the details of Rally ${ids[0]}`;
    } else if (ids.length === 2) {
      const [a, b] = ids;
      if (checkboxes.cumulative) {
        qText = `Describe the Details from Rally ${a} to Rally ${b}`;
      } else {
        qText = `Compare Rally ${a} and Rally ${b}`;
      }
    }
    if (qText) handleQuestion(qText);
    // Decide which rally to use for seeking: prefer firstRally, else secondRally
    const chosenRallyId = (isValidId(firstRally))
      ? firstRally
      : (isValidId(secondRally) ? secondRally : null);

    if (!isValidId(chosenRallyId)) return; // Nothing to seek to

    // Find the chosen rally and get its start time
    const selectedRally = rallies.find(rally => {
      const rallyId = rally?.rally_id ?? rally?.RallyID ?? rally?.id ?? rally?.number ?? rally?.rallyNo;
      return String(rallyId) === String(chosenRallyId);
    });

    if (!selectedRally) return;

    try {
      const rawStartTime = selectedRally?.stitched_time?.stitched_start_s ?? 0;
      const startTime = Math.max(0, (Number(rawStartTime) || 0) - 1);

      // Try native video element first
      const el = videoRef?.current;
      if (el && typeof el.currentTime === 'number') {
        const doSeekEl = () => {
          try {
            try { el.pause(); } catch { }
            el.currentTime = startTime;
            try { el.play().catch(() => { }); } catch { }
          } catch (e) {
            console.error('Native seek failed:', e);
          }
        };
        if (el.readyState >= 1) doSeekEl(); else el.addEventListener('loadedmetadata', doSeekEl, { once: true });
        return;
      }

      // Fallback to video.js player
      const player = playerRef?.current;
      if (player && typeof player.currentTime === 'function') {
        const doSeek = () => {
          try {
            try { player.pause(); } catch { }
            player.currentTime(startTime);
            try { player.play(); } catch { }
          } catch (e) {
            console.error('video.js seek failed:', e);
          }
        };
        if (typeof player.readyState === 'function' ? player.readyState() >= 1 : true) doSeek();
        else if (typeof player.one === 'function') player.one('loadedmetadata', doSeek);
        else setTimeout(doSeek, 100);
      }
    } catch (error) {
      console.error('seekToRally error:', error);
    }
  };


  // Scroll to top on component mount and prevent browser scroll restoration
  useEffect(() => {
    // Prevent browser's automatic scroll restoration
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }

    // Multiple approaches to ensure scroll to top
    const scrollToTop = () => {
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    };

    // Immediate scroll
    scrollToTop();

    // Also try after DOM is ready
    const timer1 = setTimeout(scrollToTop, 0);
    const timer2 = setTimeout(scrollToTop, 100);
    const timer3 = setTimeout(scrollToTop, 300);

    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
    };
  }, []);

  // Scroll chat to bottom on new message
  // Ref for chat box
  const chatBoxRef = React.useRef(null);

  // Scroll chatBox to bottom on new message
  React.useEffect(() => {
    if (chatBoxRef.current) {
      chatBoxRef.current.scrollTo({
        top: chatBoxRef.current.scrollHeight,
        behavior: 'smooth'
      });
    }
  }, [chat]);

  // Compute cumulative stats for a rally range [r1..r2] using JSON rally summary data
  const computeCumulativeStats = (r1, r2) => {
    if (!Array.isArray(rallies) || rallies.length === 0) return null;
    const lo = Math.min(Number(r1), Number(r2));
    const hi = Math.max(Number(r1), Number(r2));
    if (!Number.isFinite(lo) || !Number.isFinite(hi)) return null;

    const inRange = rallies.filter(r => {
      const rid = Number(r?.rally_id ?? r?.RallyID ?? r?.id ?? r?.number ?? r?.rallyNo);
      return Number.isFinite(rid) && rid >= lo && rid <= hi;
    });
    if (inRange.length === 0) return { empty: true };

    const sum = {
      start: lo,
      end: hi,
      totalRallies: inRange.length,
      wins: { A: 0, B: 0 },
      duration: 0,
      score: { A: 0, B: 0 },
      distance: { A: 0, B: 0 },
      hits: { A: 0, B: 0 },
      missingCount: (hi - lo + 1) - inRange.length,
    };

    inRange.forEach(r => {
      const winner = r?.winner;
      if (winner === 'A') { sum.wins.A += 1; sum.score.A += 1; }
      else if (winner === 'B') { sum.wins.B += 1; sum.score.B += 1; }

      // Support both direct duration_s and nested orig_video.duration_s
      const dur = Number(r?.duration_s ?? r?.orig_video?.duration_s);
      if (Number.isFinite(dur)) sum.duration += dur;

      // Support both direct fields and nested player_distances, both uppercase and lowercase
      const dA = Number(r?.dist_A_m ?? r?.dist_a_m ?? r?.player_distances?.dist_a_m ?? r?.player_distances?.dist_A_m);
      const dB = Number(r?.dist_B_m ?? r?.dist_b_m ?? r?.player_distances?.dist_b_m ?? r?.player_distances?.dist_B_m);
      if (Number.isFinite(dA)) sum.distance.A += dA;
      if (Number.isFinite(dB)) sum.distance.B += dB;

      // Support both uppercase and lowercase hits_count
      const hA = Number(r?.hits_count?.A ?? r?.hits_count?.a);
      const hB = Number(r?.hits_count?.B ?? r?.hits_count?.b);
      if (Number.isFinite(hA)) sum.hits.A += hA;
      if (Number.isFinite(hB)) sum.hits.B += hB;
    });

    return sum;
  };

  // ===== DATA PARSING MEMOIZATION =====
  // These arrays used to be mapped and filtered inline in the JSX multiple times per render.
  // We memoize them so 10,000+ item trackingData arrays are only iterated when they actually change.
  const memoJsonIds = useMemo(() => {
    if (!Array.isArray(rallies)) return [];
    return rallies.map(r => Number(r?.rally_id ?? r?.RallyID ?? r?.id ?? r?.number ?? r?.rallyNo)).filter(Number.isFinite);
  }, [rallies]);

  const memoCsvIds = useMemo(() => {
    if (!Array.isArray(trackingData) || trackingData.length === 0) return [];
    return trackingData.map(d => Number(d?.RallyID)).filter(Number.isFinite);
  }, [trackingData]);

  const memoTotalRalliesCount = useMemo(() => {
    try {
      const u = new Set([...memoJsonIds, ...memoCsvIds]);
      return u.size || (Array.isArray(rallies) ? rallies.length : 0);
    } catch {
      return Array.isArray(rallies) ? rallies.length : 0;
    }
  }, [memoJsonIds, memoCsvIds, rallies]);

  // ===== MEMOIZED STATS (expensive aggregations lifted out of render) =====
  const memoTotalDistA = useMemo(() => {
    const fromStats = numBV(matchStats?.match_player_distances?.total_dist_m_p1 ?? matchStats?.match_player_distances?.dist_a_m ?? matchStats?.dist_a_m);
    if (fromStats) return fromStats;
    return Array.isArray(rallies) ? rallies.reduce((a, r) => a + numBV(distAOfBV(r)), 0) : 0;
  }, [matchStats, rallies]);

  const memoTotalDistB = useMemo(() => {
    const fromStats = numBV(matchStats?.match_player_distances?.total_dist_m_p2 ?? matchStats?.match_player_distances?.dist_b_m ?? matchStats?.dist_b_m);
    if (fromStats) return fromStats;
    return Array.isArray(rallies) ? rallies.reduce((a, r) => a + numBV(distBOfBV(r)), 0) : 0;
  }, [matchStats, rallies]);

  const memoTotalShotsA = useMemo(() => {
    const fromStats = numBV(matchStats?.match_hits_count?.total_shots_p1 ?? matchStats?.hits_count?.A ?? matchStats?.hits_count?.a);
    if (fromStats) return fromStats;
    return Array.isArray(rallies) ? rallies.reduce((a, r) => a + numBV(hitsAOfBV(r)), 0) : 0;
  }, [matchStats, rallies]);

  const memoTotalShotsB = useMemo(() => {
    const fromStats = numBV(matchStats?.match_hits_count?.total_shots_p2 ?? matchStats?.hits_count?.B ?? matchStats?.hits_count?.b);
    if (fromStats) return fromStats;
    return Array.isArray(rallies) ? rallies.reduce((a, r) => a + numBV(hitsBOfBV(r)), 0) : 0;
  }, [matchStats, rallies]);

  const memoAvgDuration = useMemo(() => {
    const e = numBV(matchStats?.avg_rally_duration_s);
    if (e > 0) return e;
    const n = Array.isArray(rallies) ? rallies.length : 0;
    const s = Array.isArray(rallies) ? rallies.reduce((a, r) => a + numBV(durOfBV(r)), 0) : 0;
    return n ? (s / n) : 0;
  }, [matchStats, rallies]);

  const memoRangeStats = useMemo(() => {
    const isValidId = (id) => !(id === null || id === undefined || id === 'none' || id === 'undefined' || String(id).trim() === '');
    const showRangeTable = fetchedCheckboxes.cumulative && compareClicked;
    if (!showRangeTable || !Array.isArray(fetchedRallyIds) || fetchedRallyIds.length < 2) return null;
    try {
      const r1 = fetchedRallyIds[0];
      const r2 = fetchedRallyIds[1];
      if (isValidId(r1) && isValidId(r2)) return computeCumulativeStats(r1, r2);
    } catch { }
    return null;
  }, [fetchedCheckboxes.cumulative, compareClicked, fetchedRallyIds, rallies]);

  const memoMatchCards = useMemo(() => {
    const rallyData = directLoadMode && directVideoData?.rallyData ? directVideoData.rallyData : matchStats;
    const ralliesArr = rallyData?.rally_analysis || rallyData?.rallies || [];
    const meta = directLoadMode && directVideoData?.matchMeta?.matchMeta ? directVideoData.matchMeta.matchMeta : null;

    // Winner
    let winnerName = 'N/A';
    let wonPlayer = null;
    if (rallyData?.final_score) {
      const s1 = Number(rallyData.final_score.player_1 || rallyData.final_score.A || 0);
      const s2 = Number(rallyData.final_score.player_2 || rallyData.final_score.B || 0);
      if (s1 > s2) wonPlayer = 'A'; else if (s2 > s1) wonPlayer = 'B';
      else if (s1 === s2 && s1 > 0) winnerName = 'Tie';
    } else {
      let wA = 0, wB = 0;
      ralliesArr.forEach(r => { if (r.winner === 'A' || r.winner === 'a') wA++; else if (r.winner === 'B' || r.winner === 'b') wB++; });
      if (wA > wB) wonPlayer = 'A'; else if (wB > wA) wonPlayer = 'B';
      else if (wA === wB && wA > 0) winnerName = 'Tie';
    }
    if (wonPlayer) {
      const pos = (meta?.playerPosition || '').toLowerCase();
      const isFar = pos.includes('far');
      const isNear = pos.includes('near');
      const userDisplayName = meta?.playerName || 'You';

      if (wonPlayer === 'A') {
        winnerName = isFar ? userDisplayName : 'Opponent';
      } else if (wonPlayer === 'B') {
        winnerName = isNear ? userDisplayName : 'Opponent';
      }
    }

    // Determine player side from position
    const pos = (meta?.playerPosition || '').toLowerCase();
    const playerSide = pos.includes('near') ? 'B' : 'A';

    // Score
    let finalScoreStr = 'N/A';
    let sA = 0, sB = 0;
    if (rallyData?.final_score) {
      sA = Number(rallyData.final_score.player_1 || rallyData.final_score.A || 0);
      sB = Number(rallyData.final_score.player_2 || rallyData.final_score.B || 0);
    } else {
      ralliesArr.forEach(r => {
        if (r.winner === 'A' || r.winner === 'a') sA++;
        else if (r.winner === 'B' || r.winner === 'b') sB++;
      });
    }

    if (sA > 0 || sB > 0) {
      if (playerSide === 'B') {
        finalScoreStr = `${sB} - ${sA}`;
      } else {
        finalScoreStr = `${sA} - ${sB}`;
      }
    }

    return {
      winnerName,
      finalScoreStr,
      pName: meta?.playerName || 'Player',
      pPos: meta?.playerPosition ? `(${meta.playerPosition})` : '',
    };
  }, [directLoadMode, directVideoData, matchStats]);

  // Gating: short-circuit render safely after all hooks
  if (isAuthed && userRole === 'coach' && profileLoading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>
    );
  }

  if (isAuthed && userRole === 'coach' && !hasGenerateAIFeedback) {
    return (
      <AccessRestricted
        onBack={() => { try { window.history.length > 1 ? window.history.back() : (window.location.href = '/'); } catch { } }}
        onRequestAccess={null}
      />
    );
  }

  return (
    <div className={styles.playgroundContainer}>
      {/* Trial randomizing overlay */}
      {trialRandomizing && createPortal(
        <div style={{ position: 'fixed', inset: 0, backdropFilter: 'blur(10px)', background: 'radial-gradient(1200px 500px at 10% -10%, rgba(0,177,64,0.08), transparent 60%), radial-gradient(1200px 500px at 110% 110%, rgba(0,177,64,0.08), transparent 60%), rgba(0,0,0,0.35)', zIndex: 2000 }}>
          <style>{`
            @keyframes breathe { 0% { transform: scale(0.98); } 50% { transform: scale(1.01); } 100% { transform: scale(0.98); } }
            @keyframes floatUp { 0% { transform: translateY(8px) scale(0.9); opacity: 0; } 20% { opacity: 1; } 100% { transform: translateY(-36px) scale(1); opacity: 0; } }
            @keyframes shine { 0% { opacity: 0.1; transform: translateX(-120%);} 100% { opacity: 0.28; transform: translateX(120%);} }
            @keyframes glow { 0% { box-shadow: 0 0 0 0 rgba(0,177,64,0.25);} 70% { box-shadow: 0 0 0 18px rgba(0,177,64,0);} 100% { box-shadow: 0 0 0 0 rgba(0,177,64,0);} }
            .twinkle { position:absolute; width:8px; height:8px; border-radius:50%; background: #00b140; filter: blur(0.5px); animation: floatUp 1300ms ease-out forwards; }
          `}</style>

          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 20px' }}>
            <div style={{ position: 'relative', width: '100%', maxWidth: '480px', padding: '24px 20px', borderRadius: 24, background: 'linear-gradient(180deg,#ffffff,#f4fff8)', border: '1px solid rgba(0,177,64,0.22)', boxShadow: '0 28px 72px rgba(0,177,64,0.28)', textAlign: 'center', animation: 'breathe 2200ms ease-in-out infinite' }}>
              <div style={{ position: 'absolute', inset: -8, borderRadius: 28, pointerEvents: 'none', animation: 'glow 1500ms ease-out infinite' }} />
              <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 4, background: 'linear-gradient(90deg, transparent, rgba(0,177,64,0.25), transparent)', opacity: 0.4 }} />
              <div style={{ fontWeight: 900, color: '#0a3d2a', fontSize: 'clamp(18px, 4vw, 24px)', marginBottom: 14 }}>Curating a match for you…</div>

              {/* Hero thumbnail with soft crossfade */}
              {(() => {
                const current = allVideoOptions.find(o => o.value === selectedVideo) || allVideoOptions[0];
                const src = current ? getThumbnailUrlFor(current) : null;
                return (
                  <div style={{ position: 'relative', width: '100%', maxWidth: '420px', aspectRatio: '16/9', margin: '0 auto 12px', borderRadius: 16, overflow: 'hidden', border: '1px solid rgba(0,0,0,0.08)' }}>
                    <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(135deg, rgba(0,177,64,0.08), transparent 60%)', zIndex: 2 }} />
                    <img src={src} alt="curating" style={{ width: '100%', height: '100%', objectFit: 'cover', transition: 'opacity 360ms ease', opacity: 1 }} />
                    <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(45deg, transparent, rgba(255,255,255,0.35), transparent)', transform: 'skewX(-8deg)', animation: 'shine 1200ms ease-in-out infinite', zIndex: 3 }} />
                  </div>
                );
              })()}

              {/* Mini filmstrip - hide on very small screens to save vertical space */}
              <div className={`${styles.spinRow} hidden sm:flex`} style={{ display: 'flex', gap: 10, alignItems: 'center', justifyContent: 'center', opacity: 0.95 }}>
                {allVideoOptions.slice(0, 6).map((o, i) => (
                  <div key={i} style={{ width: 60, height: 40, borderRadius: 8, overflow: 'hidden', border: '1px solid rgba(0,0,0,0.06)', transform: `rotate(${(i % 2 ? -2.5 : 2.5)}deg)` }}>
                    <img src={getThumbnailUrlFor(o)} alt={o.label} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  </div>
                ))}
              </div>
              <div style={{ marginTop: 14, color: '#2a473c', fontSize: '14px' }}>Sit back and relax while we pick a great one…</div>

              {/* Floating twinkles */}
              {[...Array(8)].map((_, i) => (
                <span key={i} className="twinkle" style={{ left: `${20 + Math.random() * 60}%`, bottom: `${-8 + Math.random() * 8}px`, animationDelay: `${i * 100}ms`, background: i % 3 === 0 ? '#00b140' : i % 3 === 1 ? '#00d34f' : '#1e7e34' }} />
              ))}
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Direct load animation */}
      <DirectLoadAnimation
        videoTitle={directVideoData?.videoTitle}
        isVisible={loadingVideo}
        onComplete={() => setLoadingVideo(false)}
      />

      {/* Trial expiry dialog - Tailwind version */}
      {trialExpired && createPortal((
        <div
          className="fixed inset-0 bg-black/45 backdrop-blur-lg flex items-center justify-center z-[2147483647] animate-fadeIn"
          onClick={() => { }}
        >
          <div
            className="bg-white rounded-3xl overflow-hidden max-w-lg w-full mx-4 shadow-[0_28px_72px_rgba(0,0,0,0.25)] border border-[rgba(0,177,64,0.14)] animate-dialogPop"
            style={{
              background: 'radial-gradient(1200px 500px at 10% -10%, rgba(0,177,64,0.10) 0%, rgba(0,177,64,0.00) 60%), radial-gradient(1200px 500px at 110% 110%, rgba(0,177,64,0.10) 0%, rgba(0,177,64,0.00) 60%), linear-gradient(180deg, #ffffff 0%, #f7fff9 100%)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative p-4 sm:p-6">
              <div className="flex items-center gap-2.5 px-3 py-1.5 bg-[rgba(0,177,64,0.08)] border border-[rgba(0,177,64,0.25)] text-[#026B33] font-extrabold w-fit rounded-full mx-auto mt-1 mb-2" title="SmartInsights Playground">
                <img src="/playground/shuttle.png" alt="Shuttlecock" className="w-4 h-4 sm:w-5 sm:h-5" />
                <span className="text-[11px] sm:text-[13px]">SmartInsights Trial</span>
              </div>
              <div className="text-center mx-1 sm:mx-3 mt-1 mb-2">
                <div className="text-xl sm:text-2xl font-black text-[#0a3d2a] leading-tight">Your Free Question Limit Is Reached</div>
                <div className="text-[#2a473c] mt-2 text-[14px] sm:text-[15px] leading-relaxed">
                  Kindly Sign Up to ask more questions and continue exploring SmartInsights.
                </div>
              </div>

              {/* Benefit bullets */}
              <div className="flex gap-2 justify-center flex-wrap mx-1 sm:mx-3 mt-3">
                <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#eef7f1] text-[#0a3d2a] font-bold text-[11px] sm:text-[13px] border border-[rgba(0,177,64,0.18)]">✔ Full video choice</div>
                <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#eef7f1] text-[#0a3d2a] font-bold text-[11px] sm:text-[13px] border border-[rgba(0,177,64,0.18)]">✔ Deep analytics</div>
                <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#eef7f1] text-[#0a3d2a] font-bold text-[11px] sm:text-[13px] border border-[rgba(0,177,64,0.18)]">✔ Coach insights</div>
              </div>

              {/* CTA Row */}
              <div className="flex gap-3 justify-center mt-5 pb-1">
                <button
                  onClick={() => { setTrialExpired(false); navigate('/'); }}
                  className="px-4 py-2 font-extrabold text-[#026B33] bg-transparent border-none cursor-pointer hover:bg-black/5 rounded-lg transition-colors"
                >
                  Back to Home
                </button>
                <button
                  onClick={() => {
                    try {
                      sessionStorage.setItem('openPreSignOnReturn', '1');
                      sessionStorage.setItem('openPreSignStep', '3');
                    } catch { }
                    navigate('/signup');
                  }}
                  className="px-4 py-2 font-black rounded-lg text-white shadow-[0_10px_24px_rgba(0,177,64,0.28)] hover:shadow-[0_12px_28px_rgba(0,177,64,0.35)] transition-all cursor-pointer border-none"
                  style={{
                    backgroundImage: 'linear-gradient(135deg,#026B33,#00b140)'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundImage = 'linear-gradient(135deg,#026B33,#00923a)'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundImage = 'linear-gradient(135deg,#026B33,#00b140)'}
                >
                  Sign Up
                </button>
              </div>
            </div>
          </div>
        </div>
      ), document.body)}
      {/* Background Court */}
      <div className={styles.courtBackground}>
        <div className={styles.court}>
          <div className={styles.net}></div>
          <div className={styles.centerLine}></div>
          <div className={styles.serviceArea}></div>
        </div>
        <ShuttlecockAnimation styles={styles} />
      </div>

      {/* Main Content */}
      <div className={styles.playgroundGrid}>
        {/* Left Section */}
        <div className={styles.leftSection}>
          {/* Updated Header Container */}
          <div className={styles.playgroundHeaderRow}>
            <div className={styles.playgroundDesktopTitleWrap}>
              <h1 className={styles.playgroundHeading}>
                <span className={styles.headingText}>
                  SMARTINSIGHTS <span className={styles.headingSeparator}>-</span> <span className={styles.headingDetail}>{currentPlaygroundTitle}</span>
                </span>
              </h1>
            </div>

            <div className={styles.playgroundResponsiveTitleBlock}>
              <h1 className={styles.playgroundHeading}>
                <span className={styles.headingBrand}>SMARTINSIGHTS</span>
              </h1>
              <div className={styles.playgroundTitleMetaRow}>
                <div className={styles.playgroundSubheading} title={currentPlaygroundTitle}>
                  {currentPlaygroundTitle}
                </div>

                <div className={styles.headerControls}>
                  {renderHeaderActions()}
                </div>
              </div>
            </div>

            <div className={styles.playgroundDesktopControls}>
              <div className={styles.headerControls}>
                {renderHeaderActions()}
              </div>
            </div>
          </div>




          {/* Video Player */}
          <div className={styles.videoStatsRow}>
            <div
              className={styles.videoWrapper}
              onDragOver={(e) => { e.preventDefault(); }}
              onDrop={(e) => { e.preventDefault(); if (!trialMode) requestOpenSelectDialog(); }}
            >
              {/* Always define refs at the top level to avoid conditional hook calls */}
              {selectedVideo !== 'none' && (() => {
                // Direct load mode: use video data from CreateFeedback
                if (directLoadMode && selectedVideo === 'direct_load_video') {
                  // PRIORITIZE BLOB URL if available (secure fetch)
                  const fallbackSrc = directVideoData ? getVideoUrlFor({ value: 'direct_load_video' }) : null;
                  const src = stitchedVideoBlobUrl || fallbackSrc;
                  return (
                    <Html5VideoPlayer
                      key={selectedVideo}
                      videoId={selectedVideo}
                      env={"standalone"}
                      videoLoaded={true}
                      videoContent={{ videoContentUrl: src }}
                      standAloneFeedbacks={standaloneAnnotations}
                      annotations={Array.isArray(standaloneAnnotations) ? standaloneAnnotations : []}
                      setAnnotations={() => { }}
                      setLoading={() => { }}
                      addStandaloneFeedback={(payload) => { dispatch(addStandaloneFeedbackAction(payload)); }}
                      pendingAnnotation={pendingAnnotation}
                      onPendingAnnotationChange={setPendingAnnotation}
                      playerRef={playerRef}
                      videoRef={videoRef}
                      isPlayerSide={userRole === 'player'}
                      showAnnotations={true}
                      videoKey={src}
                      currentTime={videoSeekTime}
                      setCurrentTime={() => { }}
                    />
                  );
                }

                // Regular mode: look up video option from allVideoOptions
                const videoOption = allVideoOptions.find(opt => opt.value === selectedVideo);
                const src = videoOption ? getVideoUrlFor(videoOption) : null;



                return (videoOption && src) ? (
                  <Html5VideoPlayer
                    key={selectedVideo}
                    videoId={selectedVideo}
                    env={"standalone"}
                    videoLoaded={true}
                    videoContent={{ videoContentUrl: src }}
                    standAloneFeedbacks={trialMode ? [] : standaloneAnnotations}
                    // Show markers based solely on standalone annotations so that
                    // using Clear All Feedbacks also clears timeline markers.
                    annotations={trialMode ? [] : (Array.isArray(standaloneAnnotations) ? standaloneAnnotations : [])}
                    setAnnotations={() => { /* disabled in trial */ }}
                    setLoading={() => { }}
                    addStandaloneFeedback={(payload) => { if (!trialMode) dispatch(addStandaloneFeedbackAction(payload)); }}
                    pendingAnnotation={trialMode ? null : pendingAnnotation}
                    onPendingAnnotationChange={trialMode ? () => { } : setPendingAnnotation}
                    playerRef={playerRef}
                    videoRef={videoRef}
                    isPlayerSide={userRole === 'player'}
                    showAnnotations={!trialMode}
                    videoKey={src}
                    currentTime={videoSeekTime}
                    setCurrentTime={() => { }} // Required prop but we don't need to track time changes
                  />
                ) : (
                  <div style={{ padding: 12, color: '#6b7280' }}>
                    Remote video URL not configured for "{selectedVideo}". Please select another video.
                  </div>
                );
              })()}

              {/* Placeholder when no video is selected (hide in direct load mode) */}
              {(!selectedVideo || selectedVideo === 'none') && !directLoadMode && (
                <div
                  className={styles.videoPlaceholder}
                  onDragOver={(e) => { e.preventDefault(); }}
                  onDrop={(e) => { e.preventDefault(); if (!trialMode) requestOpenSelectDialog(); }}
                >
                  <img
                    src="/playground/shuttle.png"
                    alt="Shuttlecock"
                    className={styles.shuttleFloat}
                  />
                  <h1 className={styles.headerTitle}>SmartInsights Playground</h1>
                  <div className={styles.placeholderSubtitle}>
                    Pick a sample match video
                  </div>
                  <button
                    type="button"
                    className={styles.selectVideoCta}
                    onClick={trialMode ? undefined : requestOpenSelectDialog}
                    disabled={!!trialMode}
                    style={trialMode ? { pointerEvents: 'none', opacity: 0.7, cursor: 'not-allowed' } : undefined}
                  >
                    Select a Video
                  </button>
                </div>
              )}
            </div>
            {feedbackModeActive
              ? renderSideUtilitiesGroup(styles.desktopFeedbackSideUtilities, false)
              : renderSideUtilitiesGroup('', true)}
          </div>


          {/* Controls bar — relocated below Video Row and Match Info */}
          {selectedVideo && selectedVideo !== 'none' && (
            feedbackModeActive
              ? (
                <>
                  {renderControlsBar(styles.desktopFeedbackControlsOnly)}
                  {renderControlsBar(styles.tabletFeedbackControlsOnly)}
                </>
              )
              : (
                <>
                  {renderControlsBar(styles.desktopControlsRow)}
                  {renderControlsBar(styles.tabletControlsRow)}
                </>
              )
          )}

          {/* ===== BELOW VIDEO HORIZONTAL ANALYSIS STRIP ===== */}
          {selectedVideo && selectedVideo !== 'none' && (Array.isArray(rallies) && rallies.length > 0 || matchStats) && (() => {
            if (trialMode && trialChatLocked) return null;

            return (
              <>
                <div className={`${styles.belowVideoStrip} ${(!trialMode && checkboxes.coach) ? `${styles.feedbackStripMode} ${styles.mobileFeedbackBelowVideo}` : styles.primaryMetricsStrip}`}>
                  {!trialMode && checkboxes.coach ? (
                    <>
                      <div className={styles.coachFeedbackLeft}>
                        <h4 className={styles.cfHeading}>
                          <FaPlus className="text-emerald-500 mr-2" />
                          Add Feedback
                        </h4>
                        <div className={styles.cfInputContainer}>
                          <textarea
                            className={styles.cfTextarea}
                            value={coachFeedbackHook.coachFeedbackText}
                            onChange={(e) => coachFeedbackHook.setCoachFeedbackText(e.target.value)}
                            placeholder="Type your expert insights here..."
                          />
                          <button
                            className={`${styles.cfMicBtn} ${isCoachListening ? styles.recording : ''}`}
                            onClick={() => handleToggleMicFor(coachFeedbackHook.setCoachFeedbackText, 'coach')}
                            title={isCoachListening ? 'Stop Voice Recording' : 'Start Voice Typing'}
                          >
                            <FaMicrophone />
                          </button>
                        </div>
                        <button
                          className={styles.cfSubmitBtn}
                          onClick={handleSaveCoachFeedback}
                          disabled={!coachFeedbackHook.coachFeedbackText?.trim()}
                        >
                          <FaMagic className="mr-2" />
                          Save Feedback
                        </button>
                      </div>

                      <div className={styles.cfVerticalDivider} />

                      <div className={styles.coachFeedbackRight}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <h4 className={styles.cfHeading}>
                            <FaHistory className="text-emerald-500 mr-2" />
                            Feedback History
                          </h4>
                          <div className="transform scale-90 origin-right">
                            <ClearButton clearAllFeedbacks={clearAllFeedbacks} />
                          </div>
                        </div>
                        <div className={styles.feedbackListDarkWrapper}>
                          <FeedbackList
                            hideHeader={true}
                            annotations={Array.isArray(standaloneAnnotations) ? standaloneAnnotations : []}
                            clearAllFeedbacks={clearAllFeedbacks}
                            loading={feedbackLoading}
                            editingCommentId={editingCommentId}
                            editedCommentText={editedCommentText}
                            setEditingCommentId={setEditingCommentId}
                            setEditedCommentText={setEditedCommentText}
                            handleUpdateComment={handleUpdateComment}
                            handleDeleteClick={handleDeleteClick}
                            handleAudioPlay={handleAudioPlay}
                            currentTime={(() => { try { return Number(videoRef?.current?.currentTime) || 0; } catch { return 0; } })()}
                            showClearButton={true}
                          />
                        </div>
                      </div>
                    </>
                  ) : (
                    renderMetricOverview(styles.primaryMetricsPanel)
                  )}
                </div>
                {feedbackModeActive && renderSideUtilitiesGroup(styles.mobileFeedbackUtilities, true)}
                <div className={`${styles.bvDivider} ${!feedbackModeActive ? styles.primaryMetricsDivider : ''}`} />
              </>
            );
          })()}


        </div>


        {/* Right Section: Ask VISIST */}
        <div className={`${styles.rightSection} ${selectedVideo && selectedVideo !== 'none' ? styles.activated : ''}`} style={{ display: 'flex', flexDirection: 'column', gap: 0, overflowY: 'hidden' }}>


          {/* === ASK VISIST AI PANEL === */}
          <div className={`${styles.askVisistWrapper} flex-1 flex flex-col overflow-hidden`}>
            {/* Accordion Header */}
            <button
              type="button"
              onClick={() => setAskPanelOpen(o => !o)}
              className="w-full flex items-center justify-between px-4 py-3 cursor-pointer group focus:outline-none flex-shrink-0"
              style={{ background: 'transparent', border: 'none' }}
            >
              <div className="flex items-center gap-2.5">
                <img src="/favicon.ico" alt="VISIST AI" className="w-7 h-7 rounded-full object-contain" />
                <span className={`${styles.askVisistTitle} text-white text-sm sm:text-base font-bold tracking-wide`}>ASK VISIST.AI</span>
              </div>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" className="transition-transform duration-300" style={{ transform: askPanelOpen ? 'rotate(180deg)' : 'rotate(0deg)', color: '#00b140' }}><path d="M6 9l6 6 6-6" stroke="#00b140" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
            </button>
            {/* Accordion Body */}
            {askPanelOpen && (
              <div style={{ borderTop: '1px solid rgba(0,177,64,0.12)', flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, padding: '12px 16px' }}>
                <div className={styles.chatContent}>
                  {/* Predefined question bubbles */}
                  {chat.length === 0 ? (
                    <div className={`${styles.predefinedQuestions} flex flex-wrap gap-2 pb-4`}>
                      {PREDEFINED_QUESTIONS.map((q, i) => (
                        <button
                          key={i}
                          type="button"
                          onClick={() => {
                            if (trialMode && trialChatLocked) { setTrialExpired(true); return; }
                            handleQuestion(q);
                          }}
                          disabled={chatLoading}
                          className={`${styles.predefinedQuestionBtn} group flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-medium transition-all duration-250 hover:-translate-y-0.5 hover:shadow-[0_0_12px_rgba(0,177,64,0.28)] disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-[#00b140]/40`}
                        >
                          <svg width="8" height="8" viewBox="0 0 24 24" fill="none" className="opacity-60 flex-shrink-0">
                            <circle cx="12" cy="12" r="4" fill="#00b140" />
                          </svg>
                          {q}
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className={styles.predefinedWrapper}>
                      <div
                        ref={questionsScrollRef}
                        className={`${styles.predefinedQuestions} ${styles.scrollableRow} flex gap-2`}
                      >
                        {PREDEFINED_QUESTIONS.map((q, i) => (
                          <button
                            key={i}
                            type="button"
                            onClick={() => {
                              if (trialMode && trialChatLocked) { setTrialExpired(true); return; }
                              handleQuestion(q);
                            }}
                            disabled={chatLoading}
                            className={`${styles.predefinedQuestionBtn} group flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-medium transition-all duration-250 hover:-translate-y-0.5 hover:shadow-[0_0_12px_rgba(0,177,64,0.28)] disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-[#00b140]/40`}
                          >
                            <svg width="8" height="8" viewBox="0 0 24 24" fill="none" className="opacity-60 flex-shrink-0">
                              <circle cx="12" cy="12" r="4" fill="#00b140" />
                            </svg>
                            {q}
                          </button>
                        ))}
                      </div>
                      {showQuestionsArrow && (
                        <div
                          className={`${styles.scrollArrowIndicator} ${styles.clickable}`}
                          onClick={() => {
                            if (questionsScrollRef.current) {
                              questionsScrollRef.current.scrollBy({ left: 150, behavior: 'smooth' });
                            }
                          }}
                        >
                          <FaChevronRight size={14} />
                        </div>
                      )}
                    </div>
                  )}

                  {/* Chat messages */}
                  <div className={styles.responseChatBox} ref={chatBoxRef}>
                    {/* Typing indicator */}
                    {chatLoading && (
                      <div className="flex justify-start mb-3">
                        <div className={`${styles.typingIndicator} flex items-center gap-1.5 px-4 py-3 rounded-2xl rounded-bl-sm`}>
                          {[0, 0.2, 0.4].map((delay, di) => (
                            <span
                              key={di}
                              className="w-2 h-2 rounded-full bg-[#00b140] opacity-60"
                              style={{ animation: `bounce 1s ease-in-out ${delay}s infinite` }}
                            />
                          ))}
                        </div>
                      </div>
                    )}

                    {[...chat].reverse().map((msg, i) =>
                      msg.sender === 'user' ? (
                        /* User bubble */
                        <div key={i} className="flex justify-end mb-3">
                          <div
                            className="max-w-[85%] text-[13px] text-white leading-relaxed px-4 py-2.5 rounded-2xl rounded-br-sm shadow-lg"
                            style={{
                              background: 'linear-gradient(135deg, #026B33 0%, #00b140 100%)',
                              boxShadow: '0 4px 16px rgba(0,177,64,0.28)',
                            }}
                          >
                            {msg.text}
                          </div>
                        </div>
                      ) : (
                        /* Bot bubble */
                        <div key={i} className="flex justify-start mb-3">
                          <div
                            className="max-w-[92%] text-[13px] leading-relaxed"
                            style={{}}
                          >
                            <div className="flex items-center gap-2 mb-1">
                              <img src="/favicon.ico" alt="VISIST.AI" className="w-[22px] h-[22px] rounded-full object-contain" />
                              <span className={`${styles.aiChipText} text-white font-bold tracking-wide text-[11px] sm:text-[12px]`}>VISIST.AI</span>
                            </div>
                            <div className={`px-4 py-3 rounded-2xl rounded-bl-sm text-gray-200 ${styles.botMsgContent} ${styles.mdBody}`}>
                              <ReactMarkdown
                                remarkPlugins={[remarkGfm]}
                                components={{
                                  a: ({ node, href, children, ...props }) => {
                                    if (typeof href === 'string' && (href.startsWith('rally://') || href.startsWith('range://'))) {
                                      return (
                                        <a
                                          href={href}
                                          onClick={(e) => { e.preventDefault(); handleMdLinkClick(href); }}
                                          className="text-[#1db954] underline underline-offset-2 hover:text-[#a3f0c0] font-medium transition-colors"
                                          {...props}
                                        >
                                          {children}
                                        </a>
                                      );
                                    }
                                    return <a href={href} {...props} target="_blank" rel="noopener noreferrer" className="text-[#1db954] underline underline-offset-2 hover:text-[#a3f0c0] transition-colors">{children}</a>;
                                  },
                                  code: ({ inline, className, children, ...props }) => (
                                    <code
                                      className={`${className || ''} bg-[#0b1b11] text-[#a3f0c0] border border-[rgba(0,177,64,0.2)] px-1.5 py-0.5 rounded text-[12px] font-mono`}
                                      {...props}
                                    >
                                      {children}
                                    </code>
                                  )
                                }}
                              >
                                {normalizeLLMText(String(msg.text || ''))}
                              </ReactMarkdown>

                              {/* Unified Visualizations inside the bubble */}
                              {(() => {
                                const isTrackingActive = msg.vizData?.checkboxes?.tracking === true;
                                const isHeatmapActive = msg.vizData?.checkboxes?.heatmap === true;
                                const isCumulativeActive = msg.vizData?.checkboxes?.cumulative === true;
                                const isOccupancyActive = msg.vizData?.checkboxes?.occupancy === true;

                                const hasValidData = trackingData && (Array.isArray(trackingData) ? trackingData.length > 0 : Object.keys(trackingData).length > 0);

                                if (!hasValidData) return null;
                                if (!isTrackingActive && !isHeatmapActive && !isOccupancyActive) return null;

                                return (
                                  <div className={styles.vizInlineSection}>
                                    <div className="flex items-center justify-between gap-1 mb-2 px-1">
                                      <span className={styles.vizInlineLabel}>Visual Analysis</span>
                                      <div className={`${styles.chatLegendToggle} flex items-center gap-1.5 px-2 py-1 rounded-md`}>
                                        <input
                                          type="checkbox"
                                          id={`legend-toggle-${i}`}
                                          checked={legendVisible}
                                          onChange={(e) => setLegendVisible(e.target.checked)}
                                          className="w-3 h-3 accent-[#00b140] cursor-pointer"
                                        />
                                        <label htmlFor={`legend-toggle-${i}`} className={`${styles.chatLegendLabel} text-[9px] font-extrabold cursor-pointer select-none uppercase tracking-wider`}>Legend</label>
                                      </div>
                                    </div>
                                    <div className={styles.vizInlineRow}>
                                      {/* Player Tracking */}
                                      {isTrackingActive && (
                                        <div
                                          className={styles.vizInlineCard}
                                          onClick={() => setZoomedViz({ type: 'tracking', msgIndex: i })}
                                          style={{ cursor: 'zoom-in' }}
                                        >
                                          <span className={styles.vizInlineTitle}>
                                            Tracking {msg.vizData.rallyIds?.length > 1 ? `${msg.vizData.rallyIds[0]}-${msg.vizData.rallyIds[1]}` : msg.vizData.rallyIds?.[0]}
                                          </span>
                                          <div className={styles.vizInlineCanvas}>
                                            {(() => {
                                              const rids = msg.vizData.rallyIds || [];
                                              const r1 = rids[0];
                                              const r2 = rids[1];
                                              const isValid = (id) => id !== undefined && id !== null && id !== 'none' && String(id).trim() !== '';
                                              const strokeWidth = 1.6;

                                              if (isCumulativeActive && isValid(r1) && isValid(r2)) {
                                                const compactSize = getChatVizCanvasSize('compact');
                                                const localIndexOf = (rid) => {
                                                  if (!Array.isArray(rallies)) return -1;
                                                  return rallies.findIndex(r => String(r?.rally_id ?? r?.RallyID ?? r?.id ?? r?.number ?? r?.rallyNo) === String(rid));
                                                };
                                                const localRallyIdAt = (idx) => {
                                                  const r = rallies?.[idx];
                                                  return r?.rally_id ?? r?.RallyID ?? r?.id ?? r?.number ?? r?.rallyNo;
                                                };
                                                const idx1 = localIndexOf(r1);
                                                const idx2 = localIndexOf(r2);
                                                const startIdx = Math.min(idx1, idx2);
                                                const endIdx = Math.max(idx1, idx2);
                                                const aggRallyIds = [];
                                                for (let j = startIdx; j <= endIdx; j++) {
                                                  const rid = localRallyIdAt(j);
                                                  if (rid != null) aggRallyIds.push(String(rid));
                                                }
                                                const dataArr = getCleanedTrackingPoints(aggRallyIds);
                                                const hitsArr = getCleanedTrackingPoints(aggRallyIds);
                                                return <PlayerTrackingCanvas data={dataArr} hits={hitsArr} width={compactSize.width} height={compactSize.height} showLegend={legendVisible} pathColors={{ A: '#ef4444', B: '#60A5FA' }} pathStrokeWidth={strokeWidth} style={{ width: '100%' }} />;
                                              }
                                              if (isValid(r1) && isValid(r2) && !isCumulativeActive) {
                                                const singleSize = getChatVizCanvasSize('single');
                                                return (
                                                  <div className="flex flex-col gap-2 w-full">
                                                    {rids.map(rid => (
                                                      <div key={rid} className="w-full">
                                                        <div className="text-[7px] font-bold text-[#026B33] mb-0.5 uppercase opacity-60 text-center">R{rid}</div>
                                                        <PlayerTrackingCanvas data={getCleanedTrackingPoints(rid)} hits={getCleanedTrackingPoints(rid)} width={singleSize.width} height={singleSize.height} showLegend={legendVisible} pathColors={{ A: '#ef4444', B: '#60A5FA' }} pathStrokeWidth={strokeWidth} style={{ width: '100%' }} />
                                                      </div>
                                                    ))}
                                                  </div>
                                                );
                                              }
                                              if (isValid(r1) && !isValid(r2)) {
                                                const singleSize = getChatVizCanvasSize('single');
                                                const data = getCleanedTrackingPoints(r1);
                                                const hits = getCleanedTrackingPoints(r1);
                                                return <PlayerTrackingCanvas data={data} hits={hits} width={singleSize.width} height={singleSize.height} showLegend={legendVisible} pathColors={{ A: '#ef4444', B: '#60A5FA' }} pathStrokeWidth={strokeWidth} style={{ width: '100%' }} />;
                                              }
                                              return null;
                                            })()}
                                          </div>
                                        </div>
                                      )}

                                      {/* Heatmap */}
                                      {isHeatmapActive && (
                                        <div
                                          className={styles.vizInlineCard}
                                          onClick={() => setZoomedViz({ type: 'heatmap', msgIndex: i })}
                                          style={{ cursor: 'zoom-in' }}
                                        >
                                          <span className={styles.vizInlineTitle}>
                                            Heatmap {msg.vizData.rallyIds.length > 1 ? `${msg.vizData.rallyIds[0]}-${msg.vizData.rallyIds[1]}` : msg.vizData.rallyIds[0]}
                                          </span>
                                          <div className={styles.vizInlineCanvas}>
                                            {(() => {
                                              const rids = msg.vizData.rallyIds || [];
                                              const r1 = rids[0];
                                              const r2 = rids[1];
                                              const isValid = (id) => id !== undefined && id !== null && id !== 'none' && String(id).trim() !== '';

                                              if (msg.vizData.checkboxes.cumulative && isValid(r1) && isValid(r2)) {
                                                const compactSize = getChatVizCanvasSize('compact');
                                                const localIndexOf = (rid) => {
                                                  if (!Array.isArray(rallies)) return -1;
                                                  return rallies.findIndex(r => String(r?.rally_id ?? r?.RallyID ?? r?.id ?? r?.number ?? r?.rallyNo) === String(rid));
                                                };
                                                const localRallyIdAt = (idx) => {
                                                  const r = rallies?.[idx];
                                                  return r?.rally_id ?? r?.RallyID ?? r?.id ?? r?.number ?? r?.rallyNo;
                                                };
                                                const idx1 = localIndexOf(r1);
                                                const idx2 = localIndexOf(r2);
                                                const startIdx = Math.min(idx1, idx2);
                                                const endIdx = Math.max(idx1, idx2);
                                                const aggRallyIds = [];
                                                for (let j = startIdx; j <= endIdx; j++) {
                                                  const rid = localRallyIdAt(j);
                                                  if (rid != null) aggRallyIds.push(String(rid));
                                                }
                                                const dataArr = getCleanedTrackingPoints(aggRallyIds);
                                                return <OccupancyHeatmapCanvas data={dataArr} width={compactSize.width} height={compactSize.height} showLegend={legendVisible} style={{ width: '100%' }} />;
                                              }
                                              if (isValid(r1) && isValid(r2) && !msg.vizData.checkboxes.cumulative) {
                                                const singleSize = getChatVizCanvasSize('single');
                                                return (
                                                  <div className="flex flex-col gap-2 w-full">
                                                    {rids.map(rid => (
                                                      <div key={rid} className="w-full">
                                                        <div className="text-[7px] font-bold text-[#026B33] mb-0.5 uppercase opacity-60 text-center">R{rid}</div>
                                                        <OccupancyHeatmapCanvas data={getCleanedTrackingPoints(rid)} width={singleSize.width} height={singleSize.height} showLegend={legendVisible} style={{ width: '100%' }} />
                                                      </div>
                                                    ))}
                                                  </div>
                                                );
                                              }
                                              if (isValid(r1) && !isValid(r2)) {
                                                const singleSize = getChatVizCanvasSize('single');
                                                const data = getCleanedTrackingPoints(r1);
                                                return <OccupancyHeatmapCanvas data={data} width={singleSize.width} height={singleSize.height} showLegend={legendVisible} style={{ width: '100%' }} />;
                                              }
                                              return null;
                                            })()}
                                          </div>
                                        </div>
                                      )}

                                      {/* Occupancy */}
                                      {isOccupancyActive && trackingData && (
                                        <div
                                          className={styles.vizInlineCard}
                                          onClick={() => setZoomedViz({ type: 'occupancy', msgIndex: i })}
                                          style={{ cursor: 'zoom-in' }}
                                        >
                                          <span className={styles.vizInlineTitle}>
                                            Occupancy {msg.vizData.rallyIds.length > 1 ? `${msg.vizData.rallyIds[0]}-${msg.vizData.rallyIds[1]}` : msg.vizData.rallyIds[0]}
                                          </span>
                                          <div className={styles.vizInlineCanvas}>
                                            {(() => {
                                              const rids = msg.vizData.rallyIds || [];
                                              const r1 = rids[0];
                                              const r2 = rids[1];
                                              const isValid = (id) => id !== undefined && id !== null && id !== 'none' && String(id).trim() !== '';

                                              const vOpt = msg.vizData.videoOption || allVideoOptions.find(opt => opt.value === msg.vizData.videoKey) || selectedVideo;
                                              const csvUrl = msg.vizData.videoKey === 'direct_load_video' ? undefined : (msg.vizData.occupancyUrl || vOpt?.occupancyPath || (vOpt ? `${vOpt.basePath}occupancy/${vOpt.value}_occupancy.csv` : null));

                                              if (csvUrl !== null) {
                                                if (msg.vizData.checkboxes.cumulative && isValid(r1) && isValid(r2)) {
                                                  const compactSize = getChatVizCanvasSize('compact');
                                                  const localIndexOf = (rid) => {
                                                    if (!Array.isArray(rallies)) return -1;
                                                    return rallies.findIndex(r => String(r?.rally_id ?? r?.RallyID ?? r?.id ?? r?.number ?? r?.rallyNo) === String(rid));
                                                  };
                                                  const localRallyIdAt = (idx) => {
                                                    const r = rallies?.[idx];
                                                    return r?.rally_id ?? r?.RallyID ?? r?.id ?? r?.number ?? r?.rallyNo;
                                                  };
                                                  const idx1 = localIndexOf(r1);
                                                  const idx2 = localIndexOf(r2);
                                                  const startIdx = Math.min(idx1, idx2);
                                                  const endIdx = Math.max(idx1, idx2);
                                                  const aggRallyIds = [];
                                                  for (let j = startIdx; j <= endIdx; j++) {
                                                    const rid = localRallyIdAt(j);
                                                    if (rid != null) aggRallyIds.push(String(rid));
                                                  }
                                                  const trackingPts = getCleanedTrackingPoints(aggRallyIds);
                                                  return <GridOccupancyCanvas csvUrl={csvUrl} width={compactSize.width} height={compactSize.height} gridCols={2} gridRows={6} rallyIds={aggRallyIds} showLegend={legendVisible} trackingPoints={trackingPts} style={{ width: '100%' }} />;
                                                }
                                                if (isValid(r1) && isValid(r2) && !msg.vizData.checkboxes.cumulative) {
                                                  const singleSize = getChatVizCanvasSize('single');
                                                  return (
                                                    <div className="flex flex-col gap-2 w-full">
                                                      {rids.map(rid => (
                                                        <div key={rid} className="w-full">
                                                          <div className="text-[7px] font-bold text-[#026B33] mb-0.5 uppercase opacity-60 text-center">R{rid}</div>
                                                          <GridOccupancyCanvas csvUrl={csvUrl} width={singleSize.width} height={singleSize.height} gridCols={2} gridRows={6} rallyId={rid} showLegend={legendVisible} trackingPoints={getCleanedTrackingPoints(rid)} style={{ width: '100%' }} />
                                                        </div>
                                                      ))}
                                                    </div>
                                                  );
                                                }
                                                if (isValid(r1) && !isValid(r2)) {
                                                  const singleSize = getChatVizCanvasSize('single');
                                                  const trackingPts = getCleanedTrackingPoints(r1);
                                                  return <GridOccupancyCanvas csvUrl={csvUrl} width={singleSize.width} height={singleSize.height} gridCols={2} gridRows={6} rallyId={r1} showLegend={legendVisible} trackingPoints={trackingPts} style={{ width: '100%' }} />;
                                                }
                                              }
                                              return null;
                                            })()}
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                );
                              })()}
                            </div>
                          </div>
                        </div>
                      )
                    )}
                    <div ref={chatEndRef} />


                  </div>
                </div>

                {/* Chat input row */}
                <form
                  className={styles.chatInputForm}
                  onSubmit={handleSubmit}
                >
                  {/* Mic button */}
                  <button
                    type="button"
                    title={isChatListening ? 'Listening… click to stop' : 'Speak to transcribe (English)'}
                    onClick={handleToggleMic}
                    disabled={chatLoading}
                    aria-pressed={isChatListening}
                    className="flex-shrink-0 flex items-center justify-center w-10 h-10 ml-1 rounded-full transition-all duration-200 hover:scale-110 active:scale-95 focus:outline-none disabled:opacity-50"
                    style={{
                      background: isChatListening ? 'rgba(225,29,72,0.10)' : 'transparent',
                      animation: isChatListening ? 'pulseRing 1.5s ease-out infinite' : 'none',
                    }}
                  >
                    <FaMicrophone
                      size={14}
                      color={isChatListening ? '#e11d48' : '#026B33'}
                    />
                  </button>

                  {/* Text input */}
                  <input
                    className={`${styles.chatInput} flex-1 bg-transparent text-sm placeholder-[#a8c4b1] py-3 px-2 outline-none min-w-0`}
                    type="text"
                    value={input}
                    onChange={handleInput}
                    onClick={() => { if (trialMode && trialChatLocked) setTrialExpired(true); }}
                    placeholder={
                      trialMode
                        ? (trialChatLocked
                          ? 'Trial limit reached — Sign Up to continue'
                          : 'Ask up to 5 questions in trial mode…')
                        : 'Ask VISIST AI anything about this match…'
                    }
                    disabled={chatLoading}
                  />

                  {/* Send button */}
                  <button
                    type="submit"
                    disabled={chatLoading}
                    className="group relative flex-shrink-0 flex items-center justify-center gap-2 px-5 py-3 font-bold text-sm text-white disabled:opacity-60 disabled:cursor-not-allowed transition-all duration-200 hover:brightness-110 active:scale-95 focus:outline-none overflow-hidden"
                    style={{
                      background: 'linear-gradient(135deg, #026B33 0%, #00b140 100%)',
                      width: '140px',
                    }}
                  >
                    <span
                      className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                      style={{
                        background: 'linear-gradient(105deg, transparent 35%, rgba(255,255,255,0.15) 50%, transparent 65%)',
                        animation: 'shimmer 1.6s linear infinite',
                      }}
                      aria-hidden="true"
                    />
                    {chatLoading ? (
                      <span className="relative z-10 flex items-center gap-2">
                        <AnalysisCanvasLoader size={16} dotColor="#ffffff" />
                        <span className="hidden sm:inline">Processing</span>
                      </span>
                    ) : (
                      <span className="relative z-10 flex items-center gap-1.5">
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                          <line x1="22" y1="2" x2="11" y2="13" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          <polygon points="22,2 15,22 11,13 2,9" stroke="white" strokeWidth="2" fill="white" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        Ask
                      </span>
                    )}
                  </button>
                </form>
              </div>
            )}
          </div>
        </div>

        {/* Zoom Lightbox Modal */}
        {zoomedViz && (
          <div className={styles.zoomModalOverlay} onClick={() => setZoomedViz(null)}>
            <div
              className={styles.zoomModalContent}
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-labelledby="playground-zoom-title"
            >
              <button className={styles.zoomCloseBtn} onClick={() => setZoomedViz(null)}>&times;</button>
              <div className={styles.zoomTitle} id="playground-zoom-title">
                {zoomedViz.type === 'tracking' && 'Player Tracking Detail'}
                {zoomedViz.type === 'heatmap' && 'Occupancy Heatmap Detail'}
                {zoomedViz.type === 'occupancy' && 'Occupancy Grid Detail'}
              </div>
              <div className={styles.zoomMain}>
                <div className={styles.zoomCanvasWrapper}>
                  {(() => {
                    const isFromSide = zoomedViz.isSidePanel;
                    let vizData = null;
                    let activeRallyIds = [];
                    let activeVideoKey = selectedVideo;
                    let activeOccupancyUrl = undefined;
                    let activeVideoOption = undefined;

                    if (isFromSide) {
                      vizData = {
                        checkboxes: fetchedCheckboxes,
                        rallyIds: Array.isArray(sideRallyIds) ? sideRallyIds : (sideRallyIds ? [sideRallyIds] : []),
                      };
                      activeRallyIds = vizData.rallyIds;
                      const vOpt = allVideoOptions.find(opt => opt.value === selectedVideo);
                      activeVideoOption = vOpt;
                      activeOccupancyUrl = vOpt?.occupancyPath || (vOpt ? `${vOpt.basePath}occupancy/${vOpt.value}_occupancy.csv` : null);
                    } else {
                      const msg = [...chat].reverse()[zoomedViz.msgIndex];
                      if (!msg?.vizData) return null;
                      vizData = msg.vizData;
                      activeRallyIds = vizData.rallyIds || [];
                      activeVideoKey = vizData.videoKey;
                      activeOccupancyUrl = vizData.occupancyUrl;
                      activeVideoOption = vizData.videoOption || allVideoOptions.find(opt => opt.value === activeVideoKey) || selectedVideo;
                    }

                    const r1 = activeRallyIds[0];
                    const r2 = activeRallyIds[1];
                    const isValid = (id) => id !== undefined && id !== null && id !== 'none' && String(id).trim() !== '';

                    // Dimensions favoring a vertical court aspect ratio (approx 1:2.2)
                    const zoomW = 330;
                    const zoomH = 720;
                    const strokeWidth = 2.4;

                    if (zoomedViz.type === 'tracking') {
                      if (vizData.checkboxes.cumulative && isValid(r1) && isValid(r2)) {
                        const localIndexOf = (rid) => {
                          if (!Array.isArray(rallies)) return -1;
                          return rallies.findIndex(r => String(r?.rally_id ?? r?.RallyID ?? r?.id ?? r?.number ?? r?.rallyNo) === String(rid));
                        };
                        const localRallyIdAt = (idx) => {
                          const r = rallies?.[idx];
                          return r?.rally_id ?? r?.RallyID ?? r?.id ?? r?.number ?? r?.rallyNo;
                        };
                        const idx1 = localIndexOf(r1);
                        const idx2 = localIndexOf(r2);
                        const startIdx = Math.min(idx1, idx2);
                        const endIdx = Math.max(idx1, idx2);
                        const aggRallyIds = [];
                        for (let j = startIdx; j <= endIdx; j++) {
                          const rid = localRallyIdAt(j);
                          if (rid != null) aggRallyIds.push(String(rid));
                        }
                        return <PlayerTrackingCanvas data={getCleanedTrackingPoints(aggRallyIds)} hits={getCleanedTrackingPoints(aggRallyIds)} width={zoomW} height={zoomH} showLegend={false} pathColors={{ A: '#ef4444', B: '#60A5FA' }} pathStrokeWidth={strokeWidth} style={{ height: '100%', maxHeight: '72vh' }} />;
                      }
                      const activeRid = r1;
                      return <PlayerTrackingCanvas data={getCleanedTrackingPoints(activeRid)} hits={getCleanedTrackingPoints(activeRid)} width={zoomW} height={zoomH} showLegend={false} pathColors={{ A: '#ef4444', B: '#60A5FA' }} pathStrokeWidth={strokeWidth} style={{ height: '100%', maxHeight: '72vh' }} />;
                    }

                    if (zoomedViz.type === 'heatmap') {
                      if (vizData.checkboxes.cumulative && isValid(r1) && isValid(r2)) {
                        const localIndexOf = (rid) => {
                          if (!Array.isArray(rallies)) return -1;
                          return rallies.findIndex(r => String(r?.rally_id ?? r?.RallyID ?? r?.id ?? r?.number ?? r?.rallyNo) === String(rid));
                        };
                        const localRallyIdAt = (idx) => {
                          const r = rallies?.[idx];
                          return r?.rally_id ?? r?.RallyID ?? r?.id ?? r?.number ?? r?.rallyNo;
                        };
                        const idx1 = localIndexOf(r1);
                        const idx2 = localIndexOf(r2);
                        const startIdx = Math.min(idx1, idx2);
                        const endIdx = Math.max(idx1, idx2);
                        const aggRallyIds = [];
                        for (let j = startIdx; j <= endIdx; j++) {
                          const rid = localRallyIdAt(j);
                          if (rid != null) aggRallyIds.push(String(rid));
                        }
                        return <OccupancyHeatmapCanvas data={getCleanedTrackingPoints(aggRallyIds)} width={zoomW} height={zoomH} showLegend={false} style={{ height: '100%', maxHeight: '72vh' }} />;
                      }
                      const activeRid = r1;
                      return <OccupancyHeatmapCanvas data={getCleanedTrackingPoints(activeRid)} width={zoomW} height={zoomH} showLegend={false} style={{ height: '100%', maxHeight: '72vh' }} />;
                    }

                    if (zoomedViz.type === 'occupancy') {
                      const csvUrl = activeVideoKey === 'direct_load_video' ? undefined : (activeOccupancyUrl || activeVideoOption?.occupancyPath || (activeVideoOption ? `${activeVideoOption.basePath}occupancy/${activeVideoOption.value}_occupancy.csv` : null));

                      if (vizData.checkboxes.cumulative && isValid(r1) && isValid(r2)) {
                        const localIndexOf = (rid) => {
                          if (!Array.isArray(rallies)) return -1;
                          return rallies.findIndex(r => String(r?.rally_id ?? r?.RallyID ?? r?.id ?? r?.number ?? r?.rallyNo) === String(rid));
                        };
                        const localRallyIdAt = (idx) => {
                          const r = rallies?.[idx];
                          return r?.rally_id ?? r?.RallyID ?? r?.id ?? r?.number ?? r?.rallyNo;
                        };
                        const idx1 = localIndexOf(r1);
                        const idx2 = localIndexOf(r2);
                        const startIdx = Math.min(idx1, idx2);
                        const endIdx = Math.max(idx1, idx2);
                        const aggRallyIds = [];
                        for (let j = startIdx; j <= endIdx; j++) {
                          const rid = localRallyIdAt(j);
                          if (rid != null) aggRallyIds.push(String(rid));
                        }
                        return <GridOccupancyCanvas csvUrl={csvUrl} width={zoomW} height={zoomH} gridCols={2} gridRows={6} rallyIds={aggRallyIds} showLegend={false} trackingPoints={getCleanedTrackingPoints(aggRallyIds)} style={{ height: '100%', maxHeight: '72vh' }} />;
                      }
                      const activeRid = r1;
                      return <GridOccupancyCanvas csvUrl={csvUrl} width={zoomW} height={zoomH} gridCols={2} gridRows={6} rallyId={activeRid} showLegend={false} trackingPoints={getCleanedTrackingPoints(activeRid)} style={{ height: '100%', maxHeight: '72vh' }} />;
                    }

                    return null;
                  })()}
                </div>

                <div className={styles.zoomDetailsPanel}>
                  {(() => {
                    const isFromSide = zoomedViz.isSidePanel;
                    let rids = [];
                    let isRange = false;

                    if (isFromSide) {
                      rids = Array.isArray(sideRallyIds) ? sideRallyIds : (sideRallyIds ? [sideRallyIds] : []);
                      isRange = fetchedCheckboxes.cumulative || rids.length > 1;
                    } else {
                      const msg = [...chat].reverse()[zoomedViz.msgIndex];
                      rids = msg?.vizData?.rallyIds || [];
                      isRange = msg?.vizData?.checkboxes?.cumulative || rids.length > 1;
                    }
                    const { p1, p2 } = getPlayerNames();

                    if (isRange && rids.length >= 2) {
                      const r1 = rids[0];
                      const r2 = rids[rids.length - 1];
                      const rangeStatsZoom = computeCumulativeStats(r1, r2);

                      return (
                        <div className={styles.rallyDataCard}>
                          <div className={styles.rallyDataHeaderContainer}>
                            <div className={styles.rallyDataTitle}>Range Rally Analysis</div>
                            <div className={styles.rallyDataNavGroup}>
                              <button
                                type="button"
                                className={styles.rallyDataNavBtn}
                                onClick={() => scrollZoomDataMetrics(-1)}
                                aria-label="Scroll range data left"
                              >
                                <FaChevronLeft />
                              </button>
                              <button
                                type="button"
                                className={styles.rallyDataNavBtn}
                                onClick={() => scrollZoomDataMetrics(1)}
                                aria-label="Scroll range data right"
                              >
                                <FaChevronRight />
                              </button>
                            </div>
                          </div>
                          {!rangeStatsZoom.empty ? (
                            <div className={styles.dataMetricsGrid} ref={zoomDataMetricsRef}>
                              <div className={styles.dataMetricCard}>
                                <div className={styles.dmHeader}><span className={styles.dmLabel}>Range</span><span className={styles.dmIconWrapper}><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><path d="M4 11a1 1 0 0 1 1-1h14a1 1 0 0 1 1 1v2a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1v-2zM12 4V2M12 22v-2" strokeLinecap="round" /></svg></span></div>
                                <span className={styles.dmValue}>{r1} – {r2}</span>
                              </div>
                              <div className={styles.dataMetricCard}>
                                <div className={styles.dmHeader}><span className={styles.dmLabel}>Rallies</span><span className={styles.dmIconWrapper}><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><path d="M3 6h18M3 12h18M3 18h18" strokeLinecap="round" /></svg></span></div>
                                <span className={styles.dmValue}>{rangeStatsZoom.totalRallies}</span>
                              </div>
                              <div className={styles.dataMetricCard}>
                                <div className={styles.dmHeader}><span className={styles.dmLabel}>Total Duration</span><span className={styles.dmIconWrapper}><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><circle cx="12" cy="12" r="10" /><path d="M12 6v6l4 2" strokeLinecap="round" /></svg></span></div>
                                <span className={styles.dmValue}>{Number(rangeStatsZoom.duration || 0).toFixed(1)}s</span>
                              </div>
                              <div className={styles.dataMetricCard}>
                                <div className={styles.dmHeader}><span className={styles.dmLabel}>Wins {p1}</span><span className={styles.dmIconWrapper}><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6M18 9h1.5a2.5 2.5 0 0 0 0-5H18M4 22h16M10 14.66V17M14 14.66V17M18 20a2 2 0 0 0-2-2H8a2 2 0 0 0-2 2M12 14v3M15 9a3 3 0 1 0-6 0c0 4 2 5 3 6 1-1 3-2 3-6z" strokeLinecap="round" /></svg></span></div>
                                <span className={styles.dmValue}>{rangeStatsZoom.wins?.A ?? 0}</span>
                              </div>
                              <div className={styles.dataMetricCard}>
                                <div className={styles.dmHeader}><span className={styles.dmLabel}>Wins {p2}</span><span className={styles.dmIconWrapper}><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6M18 9h1.5a2.5 2.5 0 0 0 0-5H18M4 22h16M10 14.66V17M14 14.66V17M18 20a2 2 0 0 0-2-2H8a2 2 0 0 0-2 2M12 14v3M15 9a3 3 0 1 0-6 0c0 4 2 5 3 6 1-1 3-2 3-6z" strokeLinecap="round" /></svg></span></div>
                                <span className={styles.dmValue}>{rangeStatsZoom.wins?.B ?? 0}</span>
                              </div>
                              <div className={styles.dataMetricCard}>
                                <div className={styles.dmHeader}><span className={styles.dmLabel}>Dist. {p1}</span><span className={styles.dmIconWrapper}><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><path d="M13 4v16M17 4v16M19 4H5a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2zM9 4v16" strokeLinecap="round" /></svg></span></div>
                                <span className={styles.dmValue}>{(rangeStatsZoom.distance?.A ?? 0).toFixed(1)}m</span>
                              </div>
                              <div className={styles.dataMetricCard}>
                                <div className={styles.dmHeader}><span className={styles.dmLabel}>Dist. {p2}</span><span className={styles.dmIconWrapper}><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><path d="M13 4v16M17 4v16M19 4H5a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2zM9 4v16" strokeLinecap="round" /></svg></span></div>
                                <span className={styles.dmValue}>{(rangeStatsZoom.distance?.B ?? 0).toFixed(1)}m</span>
                              </div>
                              <div className={styles.dataMetricCard}>
                                <div className={styles.dmHeader}><span className={styles.dmLabel}>Hits {p1}</span><span className={styles.dmIconWrapper}><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><path d="m13 2-2 10h9L7 22l2-10H1L13 2z" strokeLinecap="round" strokeLinejoin="round" /></svg></span></div>
                                <span className={styles.dmValue}>{rangeStatsZoom.hits?.A ?? 0}</span>
                              </div>
                              <div className={styles.dataMetricCard}>
                                <div className={styles.dmHeader}><span className={styles.dmLabel}>Hits {p2}</span><span className={styles.dmIconWrapper}><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><path d="m13 2-2 10h9L7 22l2-10H1L13 2z" strokeLinecap="round" strokeLinejoin="round" /></svg></span></div>
                                <span className={styles.dmValue}>{rangeStatsZoom.hits?.B ?? 0}</span>
                              </div>
                            </div>
                          ) : (
                            <div className={styles.bvEmptyNote}>No rallies in selected range.</div>
                          )}
                        </div>
                      );
                    }

                    // Determine which rally's data to show (for individual view)
                    const activeRid = rids[zoomRallyIndex] || rids[0];
                    const rallyMeta = Array.isArray(rallies) ? rallies.find(r => String(getIdBV(r)) === String(activeRid)) : null;

                    if (!rallyMeta) {
                      return (
                        <div className="flex flex-col items-center justify-center h-full text-gray-400 gap-2">
                          <div className="text-sm">Rally metadata unavailable</div>
                        </div>
                      );
                    }

                    let sA = scoreAOfBV(rallyMeta);
                    let sB = scoreBOfBV(rallyMeta);

                    // If explicit scores are missing, use the "range rally analysis" method: sum up wins until this rally
                    if (sA === null || sB === null) {
                      const cumulative = getCumulativeScoreUntilBV(activeRid);
                      if (cumulative) {
                        sA = cumulative.sA;
                        sB = cumulative.sB;
                      }
                    }

                    const dur = durOfBV(rallyMeta);

                    const displayStats = {
                      rally_id: activeRid,
                      hitsA: hitsAOfBV(rallyMeta),
                      hitsB: hitsBOfBV(rallyMeta),
                      distA: distAOfBV(rallyMeta).toFixed(1),
                      distB: distBOfBV(rallyMeta).toFixed(1),
                      duration: dur !== null ? `${dur.toFixed(1)}s` : 'N/A',
                      score: (sA !== null && sB !== null) ? `${sA}-${sB}` : (typeof rallyMeta.score === 'string' ? rallyMeta.score : 'N/A'),
                      winner: typeof rallyMeta.winner === 'string' ? rallyMeta.winner : (rallyMeta.winner?.a || rallyMeta.winner?.A || rallyMeta.winner?.b || rallyMeta.winner?.B || 'N/A')
                    };

                    return (
                      <div className={styles.rallyDataCard}>
                        <div className={styles.rallyDataHeaderContainer}>
                          <div className={styles.rallyDataTitle}>Rally Data</div>
                          <div className={styles.rallyDataNavGroup}>
                            <button
                              type="button"
                              className={styles.rallyDataNavBtn}
                              onClick={() => scrollZoomDataMetrics(-1)}
                              aria-label="Scroll rally data left"
                            >
                              <FaChevronLeft />
                            </button>
                            <button
                              type="button"
                              className={styles.rallyDataNavBtn}
                              onClick={() => scrollZoomDataMetrics(1)}
                              aria-label="Scroll rally data right"
                            >
                              <FaChevronRight />
                            </button>
                          </div>
                          {rids.length > 1 && !isRange && (
                            <div className={styles.rallySelector}>
                              <button
                                className={styles.rallySelectorBtn}
                                onClick={() => setZoomRallyIndex(prev => Math.max(0, prev - 1))}
                                disabled={zoomRallyIndex === 0}
                              >
                                &larr;
                              </button>
                              <span className={styles.rallySelectorText}>{zoomRallyIndex + 1} / {rids.length}</span>
                              <button
                                className={styles.rallySelectorBtn}
                                onClick={() => setZoomRallyIndex(prev => Math.min(rids.length - 1, prev + 1))}
                                disabled={zoomRallyIndex === rids.length - 1}
                              >
                                &rarr;
                              </button>
                            </div>
                          )}
                        </div>

                        <div className={styles.dataMetricsGrid} ref={zoomDataMetricsRef}>
                          <div className={styles.dataMetricCard}>
                            <div className={styles.dmHeader}><span className={styles.dmLabel}>Rally #</span><span className={styles.dmIconWrapper}><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><path d="M4 9h16M4 15h16M10 3L8 21M16 3l-2 18" strokeLinecap="round" /></svg></span></div>
                            <span className={styles.dmValue}>{displayStats.rally_id}</span>
                          </div>
                          <div className={styles.dataMetricCard}>
                            <div className={styles.dmHeader}><span className={styles.dmLabel}>Winner</span><span className={styles.dmIconWrapper}><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6M18 9h1.5a2.5 2.5 0 0 0 0-5H18M4 22h16M10 14.66V17M14 14.66V17M18 20a2 2 0 0 0-2-2H8a2 2 0 0 0-2 2M12 14v3M15 9a3 3 0 1 0-6 0c0 4 2 5 3 6 1-1 3-2 3-6z" strokeLinecap="round" /></svg></span></div>
                            <span className={styles.dmValue}>{displayStats.winner}</span>
                          </div>
                          <div className={styles.dataMetricCard}>
                            <div className={styles.dmHeader}><span className={styles.dmLabel}>Duration</span><span className={styles.dmIconWrapper}><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><circle cx="12" cy="12" r="10" /><path d="M12 6v6l4 2" strokeLinecap="round" /></svg></span></div>
                            <span className={styles.dmValue}>{displayStats.duration}</span>
                          </div>
                          <div className={styles.dataMetricCard}>
                            <div className={styles.dmHeader}><span className={styles.dmLabel}>Score</span><span className={styles.dmIconWrapper}><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" /><rect x="8" y="2" width="8" height="4" rx="1" ry="1" /><path d="M9 14h6M9 18h6" strokeLinecap="round" /></svg></span></div>
                            <span className={styles.dmValue}>{displayStats.score}</span>
                          </div>
                          <div className={styles.dataMetricCard}>
                            <div className={styles.dmHeader}><span className={styles.dmLabel}>Dist. {p1}</span><span className={styles.dmIconWrapper}><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><path d="M13 4v16M17 4v16M19 4H5a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2zM9 4v16" strokeLinecap="round" /></svg></span></div>
                            <span className={styles.dmValue}>{displayStats.distA}m</span>
                          </div>
                          <div className={styles.dataMetricCard}>
                            <div className={styles.dmHeader}><span className={styles.dmLabel}>Dist. {p2}</span><span className={styles.dmIconWrapper}><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><path d="M13 4v16M17 4v16M19 4H5a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2zM9 4v16" strokeLinecap="round" /></svg></span></div>
                            <span className={styles.dmValue}>{displayStats.distB}m</span>
                          </div>
                          <div className={styles.dataMetricCard}>
                            <div className={styles.dmHeader}><span className={styles.dmLabel}>Hits {p1}</span><span className={styles.dmIconWrapper}><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><path d="m13 2-2 10h9L7 22l2-10H1L13 2z" strokeLinecap="round" strokeLinejoin="round" /></svg></span></div>
                            <span className={styles.dmValue}>{displayStats.hitsA}</span>
                          </div>
                          <div className={styles.dataMetricCard}>
                            <div className={styles.dmHeader}><span className={styles.dmLabel}>Hits {p2}</span><span className={styles.dmIconWrapper}><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><path d="m13 2-2 10h9L7 22l2-10H1L13 2z" strokeLinecap="round" strokeLinejoin="round" /></svg></span></div>
                            <span className={styles.dmValue}>{displayStats.hitsB}</span>
                          </div>
                        </div>




                        <div className="mt-4 p-3 bg-white/5 rounded-xl border border-white/10">
                          <div className="text-[9px] font-bold text-white/40 uppercase mb-1">Rally Insight</div>
                          <p className="text-[11px] text-[#a8c4b1] leading-relaxed italic">
                            Player {displayStats.winner} dominated with precision.
                            {Number(displayStats.hitsA) + Number(displayStats.hitsB) > 12 ? ' A long defensive exchange.' : ' A swift offensive strike.'}
                          </p>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>
            </div>
          </div>
        )}
        {/* === Match Info Popup Modal === */}
        {showSharePopup && (
          <div className={styles.matchInfoOverlay} onClick={() => !shareInProgress && setShowSharePopup(false)}>
            <div
              className={`${styles.matchInfoPopup} ${styles.sharePopup}`}
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-labelledby="playground-share-title"
            >
              <button
                className={styles.popupClose}
                onClick={() => !shareInProgress && setShowSharePopup(false)}
                disabled={shareInProgress}
              >
                &times;
              </button>
              <h2 id="playground-share-title">Share Options</h2>
              <div className={styles.sharePopupContent}>
                <p className={styles.sharePopupIntro}>
                  Choose what you want to share from this SmartInsights page.
                </p>
                <div className={styles.shareOptionsList}>
                  {SHARE_OPTIONS.map((option) => (
                    <label key={option.id} className={`${styles.shareOptionCard} ${shareSelections[option.id] ? styles.shareOptionCardActive : ''}`}>
                      <input
                        type="checkbox"
                        name={`share-option-${option.id}`}
                        checked={!!shareSelections[option.id]}
                        onChange={() => setShareSelections((prev) => ({ ...prev, [option.id]: !prev[option.id] }))}
                        disabled={shareInProgress}
                      />
                      <span className={styles.shareOptionLabel}>{option.label}</span>
                    </label>
                  ))}
                </div>
                <div className={styles.sharePopupActions}>
                  <button
                    type="button"
                    className={styles.shareSecondaryBtn}
                    onClick={() => setShowSharePopup(false)}
                    disabled={shareInProgress}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className={styles.sharePrimaryBtn}
                    onClick={handleShareSelection}
                    disabled={shareInProgress || !selectedVideo || selectedVideo === 'none' || !Object.values(shareSelections).some(Boolean)}
                  >
                    {shareInProgress ? 'Preparing...' : 'Share Now'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {showMatchInfoPopup && (
          <div className={styles.matchInfoOverlay} onClick={() => setShowMatchInfoPopup(false)}>
            <div
              className={styles.matchInfoPopup}
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-labelledby="playground-match-info-title"
            >
              <button className={styles.popupClose} onClick={() => setShowMatchInfoPopup(false)}>&times;</button>
              <h2 id="playground-match-info-title">Set Information</h2>
              <div className={styles.popupScrollContent}>
                {!selectedVideo || selectedVideo === 'none' ? (
                  <div className={styles.bvEmptyNote}>No match selected.</div>
                ) : matchStats ? (() => {
                  const { p1, p2 } = getPlayerNames();
                  const meta = directLoadMode && directVideoData?.matchMeta?.matchMeta ? directVideoData.matchMeta.matchMeta : null;
                  const playerName = meta?.playerName;
                  const playerPosition = meta?.playerPosition;
                  const location = meta?.location || matchStats.location;
                  const event = meta?.event || matchStats.event;
                  const matchType = meta?.matchType || matchStats.match_type;
                  const matchDate = meta?.matchDate;
                  const vName = directVideoData?.videoTitle || meta?.video_name || rallyData?.video_name || (directLoadMode ? selectedVideo?.replace('direct_load_video', 'Set Video')?.replace(/_/g, ' ') : 'PLAYGROUND');

                  const rallyData = directLoadMode && directVideoData?.rallyData ? directVideoData.rallyData : matchStats;
                  const rallies = rallyData?.rally_analysis || rallyData?.rallies || [];

                  let winner = 'N/A';
                  let wonPlayer = null; // 'A' or 'B'

                  // 1. Determine who won from final_score or rallies
                  if (rallyData?.final_score) {
                    const score1 = Number(rallyData.final_score.player_1 || rallyData.final_score.A || 0);
                    const score2 = Number(rallyData.final_score.player_2 || rallyData.final_score.B || 0);
                    if (score1 > score2) wonPlayer = 'A';
                    else if (score2 > score1) wonPlayer = 'B';
                    else if (score1 === score2 && (score1 > 0)) winner = 'Tie';
                  } else {
                    // Fallback to rally counting
                    let winsA = 0, winsB = 0;
                    rallies.forEach(r => {
                      if (r.winner === 'A' || r.winner === 'a') winsA++;
                      else if (r.winner === 'B' || r.winner === 'b') winsB++;
                    });
                    if (winsA > winsB) wonPlayer = 'A';
                    else if (winsB > winsA) wonPlayer = 'B';
                    else if (winsA === winsB && (winsA > 0)) winner = 'Tie';
                  }

                  // 2. Map winning player to playerName or 'Opponent' based on playerPosition
                  if (wonPlayer) {
                    const pos = (meta?.playerPosition || '').toLowerCase();
                    const isFar = pos.includes('far');
                    const isNear = pos.includes('near');

                    const userDisplayName = meta?.playerName || 'You';

                    if (wonPlayer === 'A') {
                      winner = isFar ? userDisplayName : 'Opponent';
                    } else if (wonPlayer === 'B') {
                      winner = isNear ? userDisplayName : 'Opponent';
                    }
                  }

                  let longestRally = Number(matchStats?.longest_rally_s || 0);
                  if (longestRally <= 0) {
                    rallies.forEach(rally => {
                      const duration = Number(rally.orig_video?.duration_s || rally.duration_s || 0);
                      if (duration > longestRally) longestRally = duration;
                    });
                  }

                  const totalDistance = Number(matchStats?.match_player_distances?.total_dist_m || matchStats?.total_dist_m || 0);
                  let matchDuration = Number(matchStats?.match_duration_s || 0);
                  if (matchDuration <= 0) {
                    rallies.forEach(rally => {
                      matchDuration += Number(rally.orig_video?.duration_s || rally.duration_s || 0);
                    });
                  }
                  const durationMinutes = Math.floor(matchDuration / 60);
                  const durationSeconds = Math.floor(matchDuration % 60);
                  const formattedDuration = `${durationMinutes}:${durationSeconds.toString().padStart(2, '0')} min`;

                  let formattedDate = matchStats.date;
                  if (matchDate) {
                    try {
                      const date = new Date(matchDate);
                      formattedDate = date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
                    } catch (e) { formattedDate = matchDate; }
                  }

                  let finalScoreStr = 'N/A';
                  if (rallyData?.final_score) {
                    finalScoreStr = `${rallyData.final_score.player_1 || rallyData.final_score.A || 0} - ${rallyData.final_score.player_2 || rallyData.final_score.B || 0}`;
                  } else {
                    let winsA = 0, winsB = 0;
                    rallies.forEach(r => {
                      if (r.winner === 'A' || r.winner === 'a') winsA++;
                      else if (r.winner === 'B' || r.winner === 'b') winsB++;
                    });
                    if (winsA > 0 || winsB > 0) finalScoreStr = `${winsA} - ${winsB}`;
                  }

                  return (
                    <>
                      <div className={styles.infoSection}>
                        <div className={styles.statRow}><span className={styles.statLabel}>Video Name</span><span className={styles.statValue}>{vName}</span></div>
                        <div className={styles.statRow}><span className={styles.statLabel}>Player</span><span className={styles.statValue}>{playerName || p1}</span></div>
                        {playerPosition && <div className={styles.statRow}><span className={styles.statLabel}>Position</span><span className={styles.statValue}>{playerPosition.replace('_', ' ')}</span></div>}
                        <div className={styles.statRow}><span className={styles.statLabel}>Winner</span><span className={styles.statValue}>{winner}</span></div>
                        <div className={styles.statRow}><span className={styles.statLabel}>Final Score</span><span className={styles.statValue}>{finalScoreStr}</span></div>
                        {matchType && <div className={styles.statRow}><span className={styles.statLabel}>Set Type</span><span className={styles.statValue}>{matchType.charAt(0).toUpperCase() + matchType.slice(1)}</span></div>}
                        <div className={styles.statRow}><span className={styles.statLabel}>Set Duration</span><span className={styles.statValue}>{formattedDuration}</span></div>
                        {location && <div className={styles.statRow}><span className={styles.statLabel}>Location</span><span className={styles.statValue}>{location}</span></div>}
                        {formattedDate && <div className={styles.statRow}><span className={styles.statLabel}>Date</span><span className={styles.statValue}>{formattedDate}</span></div>}
                        {event && <div className={styles.statRow}><span className={styles.statLabel}>Event</span><span className={styles.statValue}>{event}</span></div>}
                      </div>

                      {!(directLoadMode && directVideoData?.matchMeta?.matchMeta) && (
                        <div className={`${styles.infoSection} ${styles.infoSectionSeparated}`}>
                          <h4 style={{ color: '#00b140', marginBottom: '12px' }}>Players</h4>
                          <div className={styles.statRow}><span className={styles.statLabel}>{p1}</span><span className={styles.statValue}>P1 (Away)</span></div>
                          <div className={styles.statRow}><span className={styles.statLabel}>{p2}</span><span className={styles.statValue}>P2 (Close)</span></div>
                        </div>
                      )}
                    </>
                  );
                })() : (
                  <div className={styles.bvEmptyNote}>Loading match info...</div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
